/**
 * Slide renderer — the single React implementation of the slide design
 * system (mirrors backend/src/engine/slideDesign.ts + pdfRenderer.ts).
 *
 * Used read-only by the presentation viewer and in EDIT mode by the Reports
 * editor: wrap the renderer in <SlideEditProvider> and every text becomes
 * editable in place, media gets Replace/Remove, lists get per-item delete
 * and an inline "+", and empty optional fields show a faint placeholder you
 * can click to add. Outside the provider nothing is interactive.
 */
import { createContext, Fragment, useContext, useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { BeatlyMark, BEATLY_MARK_PATHS, BEATLY_MARK_VIEWBOX } from './beatlyMark';
import { cellStatus, STATUS_COLORS } from './tableStatus';
import { TABLE_FIT, tableLayout, splitTableSlide } from './tableFit';
import { chartSvg } from './chartSvg';
import { FONT_CHOICES, fontHref, type FreeElement } from './elements';
import { useVideosMuted } from './videoPrefs';
import { richHtml, htmlToMarkup, tokenRe, parseStyle, hasMarkup, domToRuns, runsToMarkup, formatRuns, styleAt, selectionOffsets, setSelectionOffsets, runsLength, type FormatOp } from './richTextModel';

// ─── Theme ──────────────────────────────────────────────────────────────────

export interface Theme {
  primary: string;
  accent: string;
  primarySoft: string;
  primaryTint: string;
  text: string;
  textMuted: string;
  textSubtle: string;
  textInverse: string;
  textInverseMuted: string;
  bg: string;
  bgSoft: string;
  surface: string;
  bgDark: string;
  bgBrand: string;
  border: string;
  borderStrong: string;
  fontDisplay: string;
  /** Deck palette (chart series, shape fills) when the deck sets one. */
  palette?: string[];
  fontSerif: string;
  brandName: string;
  logoUrl?: string;
  logoLightUrl?: string;
  /** Beatly wordmark top-right (default true; per-deck setting can hide it). */
  beatlyMark: boolean;
}

const FONT_DEFAULT = '"Inter", "Söhne", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';
const FONT_SERIF = '"Tiempos Headline", "Charter", "Iowan Old Style", Georgia, serif';

function withHash(c?: string): string | undefined {
  if (!c) return undefined;
  return c.startsWith('#') ? c : `#${c}`;
}

function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const expanded = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const r = parseInt(expanded.slice(0, 2), 16);
  const g = parseInt(expanded.slice(2, 4), 16);
  const b = parseInt(expanded.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function pickLogo(brand: any): { dark?: string; light?: string } {
  const b = brand?.brand ?? {};
  const top = brand ?? {};
  const fromMap = (m: any) => m?.logoDark || m?.logoLight || m?.iconDark || m?.iconLight;
  const fromMapLight = (m: any) => m?.logoLight || m?.iconLight || m?.logoDark || m?.iconDark;
  const dark = fromMap(b.logos) || fromMap(top.logos) || b.logoDark || b.logoUrl || b.logo_url || top.logoDark || top.logoUrl || top.brand_logo_url;
  const light = fromMapLight(b.logos) || fromMapLight(top.logos) || b.logoLight || b.logoUrl || b.logo_url || top.logoLight || top.logoUrl || top.brand_logo_url;
  return { dark, light };
}

/** Relative luminance of a #hex colour (0 = black, 1 = white). */
function lum(hex: string): number {
  const h = hex.replace('#', ''); if (h.length < 6) return 0.5;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
/** Mix a #hex colour towards black (t = share of black). */
function mixWithBlack(hex: string, t: number): string {
  const h = hex.replace('#', ''); if (h.length < 6) return hex;
  const c = [0, 2, 4].map((i) => Math.round(parseInt(h.slice(i, i + 2), 16) * (1 - t)));
  return `#${c.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}
/**
 * Dark surface inside the brand identity: the darkest colour the brand declares (when it is genuinely
 * dark), otherwise the primary colour taken almost to black so section dividers, dark covers and
 * closings keep the brand's undertone instead of a generic navy.
 */
function brandDark(colors: string[], primary: string): string {
  const declared = [...colors].filter((c) => lum(c) < 0.18).sort((a, b) => lum(a) - lum(b))[0];
  return declared || mixWithBlack(primary, 0.82);
}

export function deriveTheme(brand: any): Theme {
  const colorsRaw: string[] = brand?.brand?.primaryColors || brand?.primaryColors || [];
  const colors = colorsRaw.map((c) => withHash(c)).filter((c): c is string => !!c);
  const brandFont = brand?.brand?.fonts?.[0] || brand?.fonts?.[0];
  const font = brandFont ? `"${brandFont}", ${FONT_DEFAULT}` : FONT_DEFAULT;
  const { dark, light } = pickLogo(brand);
  const primary = colors[0] || '#7C3AED';
  const accent = colors[1] || colors[0] || '#0EA5E9';
  // Deck-wide page colours (deck settings) — mirrors slideDesign.deriveTheme on the backend.
  const dcBg = withHash(brand?.deckColors?.background); const dcText = withHash(brand?.deckColors?.text);
  const bgIsDark = dcBg ? lum(dcBg) < 0.5 : false;
  return {
    primary,
    accent,
    primarySoft: hexToRgba(primary, 0.08),
    primaryTint: hexToRgba(primary, 0.16),
    text: dcText || (bgIsDark ? '#FFFFFF' : '#0B1220'),
    textMuted: dcText ? hexToRgba(dcText, 0.72) : (bgIsDark ? 'rgba(255, 255, 255, 0.72)' : '#475569'),
    textSubtle: dcText ? hexToRgba(dcText, 0.5) : (bgIsDark ? 'rgba(255, 255, 255, 0.5)' : '#94A3B8'),
    textInverse: dcText || '#FFFFFF',
    textInverseMuted: dcText ? hexToRgba(dcText, 0.72) : 'rgba(255, 255, 255, 0.72)',
    bg: dcBg || '#FFFFFF',
    bgSoft: dcBg || '#F8FAFC',
    surface: '#F1F5F9',
    bgDark: colors.length ? brandDark(colors, primary) : '#0B1220',
    bgBrand: primary,
    border: '#E2E8F0',
    borderStrong: '#CBD5E1',
    fontDisplay: font,
    palette: Array.isArray(brand?.palette) && brand.palette.length ? brand.palette : undefined,
    fontSerif: FONT_SERIF,
    brandName: brand?.brand?.name || brand?.brandName || '',
    logoUrl: dark || light,
    logoLightUrl: light || dark,
    beatlyMark: brand?.beatlyMark !== false,
  };
}

type ThemeKind = 'light' | 'soft' | 'dark' | 'brand';

interface ResolvedTheme {
  bg: string;
  text: string;
  textMuted: string;
  isInverse: boolean;
  logoUrl?: string;
}

function resolveTheme(t: Theme, kind: ThemeKind): ResolvedTheme {
  switch (kind) {
    case 'soft':  return { bg: t.bgSoft,  text: t.text,        textMuted: t.textMuted,        isInverse: false, logoUrl: t.logoUrl };
    case 'dark':  return { bg: t.bgDark,  text: t.textInverse, textMuted: t.textInverseMuted, isInverse: true,  logoUrl: t.logoLightUrl || t.logoUrl };
    case 'brand': return { bg: t.bgBrand, text: t.textInverse, textMuted: t.textInverseMuted, isInverse: true,  logoUrl: t.logoLightUrl || t.logoUrl };
    case 'light':
    default:      return { bg: t.bg,      text: t.text,        textMuted: t.textMuted,        isInverse: false, logoUrl: t.logoUrl };
  }
}

// ─── Type scale (cqw = % of slide width) ────────────────────────────────────

export const TYPE = {
  caption:    { fontSize: '0.95cqw', fontWeight: 600, letterSpacing: '0.14em', lineHeight: 1.3, textTransform: 'uppercase' as const },
  bodySmall:  { fontSize: '1.15cqw', fontWeight: 400, letterSpacing: '0', lineHeight: 1.5 },
  body:       { fontSize: '1.40cqw', fontWeight: 400, letterSpacing: '0', lineHeight: 1.55 },
  bodyLarge:  { fontSize: '1.70cqw', fontWeight: 400, letterSpacing: '0', lineHeight: 1.5 },
  lead:       { fontSize: '2.10cqw', fontWeight: 500, letterSpacing: '-0.005em', lineHeight: 1.4 },
  title:      { fontSize: '2.30cqw', fontWeight: 700, letterSpacing: '-0.012em', lineHeight: 1.2 },
  headline:   { fontSize: '3.30cqw', fontWeight: 700, letterSpacing: '-0.022em', lineHeight: 1.1 },
  displaySm:  { fontSize: '4.20cqw', fontWeight: 800, letterSpacing: '-0.028em', lineHeight: 1.04 },
  display:    { fontSize: '5.40cqw', fontWeight: 800, letterSpacing: '-0.030em', lineHeight: 1.02 },
  displayLg:  { fontSize: '7.00cqw', fontWeight: 800, letterSpacing: '-0.038em', lineHeight: 0.98 },
  statistic:  { fontSize: '9.00cqw', fontWeight: 800, letterSpacing: '-0.050em', lineHeight: 0.92, fontVariantNumeric: 'tabular-nums' as const },
} as const;

const GUTTER = '7.5cqw';
const TOP_PAD = '8cqh';

// ─── Edit context ───────────────────────────────────────────────────────────

export interface SlideMediaValue {
  url: string;
  alt?: string;
  kind?: 'image' | 'video';
  posterUrl?: string;
  sourceId?: string;
  href?: string;
  caption?: string;
}

export interface SlideEditApi {
  editable: boolean;
  /** Apply a change to the slide being rendered (mutator gets a shallow copy). */
  update: (mutate: (slide: any) => any) => void;
  /** Open the media picker; `apply` receives the chosen media. */
  pickMedia: (apply: (m: SlideMediaValue) => void) => void;
  /** Open the creator picker; `apply` receives the creator's facts (creator API). Optional for hosts without it. */
  pickCreator?: (apply: (facts: any) => void) => void;
  /** Open the team-member picker (Beatly people); `apply` receives { name, title, email, phone, pictureUrl } or nothing for a blank card. */
  pickTeamMember?: (apply: (m: any | null) => void) => void;
  /** Brand colours offered first in the text colour menu. */
  palette?: string[];
  /** Replace the page being rendered with these pages (a long table split into continuation pages). */
  replaceWith?: (slides: any[]) => void;
  /** Deck language when known ("sv", "Swedish"…): continuation pages read "(forts.)" in Swedish. */
  language?: string;
}

const NOOP_EDIT: SlideEditApi = { editable: false, update: () => {}, pickMedia: () => {} };

// ─── Creator cards: which facts from the creator API a page shows ───────────
/** Showcase variants whose cards fill from the creator API and honour the page's creator-field toggles. */
export const CREATOR_VARIANTS = ['profiles', 'creator-cards', 'creator-reels', 'creator-split', 'creator-feature'];
export interface CreatorField { id: string; label: string; on: boolean }
export const CREATOR_FIELDS: CreatorField[] = [
  { id: 'photo', label: 'Profile photo', on: true },
  { id: 'handle', label: 'Handle & platform', on: true },
  { id: 'followers', label: 'Followers', on: true },
  { id: 'engagement', label: 'Engagement rate', on: true },
  { id: 'age', label: 'Age group', on: true },
  { id: 'gender', label: 'Gender', on: false },
  { id: 'country', label: 'Country', on: true },
  { id: 'language', label: 'Language', on: false },
  { id: 'interests', label: 'Interests', on: false },
  { id: 'audience', label: 'Audience (top country · women · top age)', on: false },
  { id: 'real', label: 'Real followers share', on: false },
  { id: 'posts', label: 'Content count', on: false },
  { id: 'bio', label: 'Bio when no description is written', on: true },
];
/** Page-level toggles merged over the defaults. */
export function creatorFieldsOf(slide: any): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const f of CREATOR_FIELDS) out[f.id] = slide?.fields && typeof slide.fields[f.id] === 'boolean' ? slide.fields[f.id] : f.on;
  return out;
}
const compactNum = (n: number) => (n >= 1e6 ? `${(n / 1e6).toFixed(1).replace(/\.0$/, '')}M` : n >= 1e3 ? `${Math.round(n / 1e3)}k` : String(n));
const regionName = (code: string) => { try { return new Intl.DisplayNames(['en'], { type: 'region' }).of(code.toUpperCase()) || code; } catch { return code; } };
const languageName = (code: string) => { try { return new Intl.DisplayNames(['en'], { type: 'language' }).of(code) || code; } catch { return code; } };
export const platformLabel = (p?: string | null) => (String(p || '').toUpperCase() === 'TIKTOK' ? 'TikTok' : String(p || '').toUpperCase() === 'YOUTUBE' ? 'YouTube' : 'Instagram');
/** Fact lines for a card: the enabled creator-API facts first, then the hand-written stats (deduped by label). */
export function creatorLines(item: any, fields: Record<string, boolean>): Array<{ label: string; value: string; auto: boolean; index?: number }> {
  const c = item?.creator; const out: Array<{ label: string; value: string; auto: boolean; index?: number }> = [];
  if (c) {
    if (fields.followers && c.followers != null) out.push({ label: 'Followers', value: compactNum(Number(c.followers)), auto: true });
    if (fields.engagement && c.engagementRate != null) out.push({ label: 'Engagement rate', value: `${Number(c.engagementRate).toFixed(Number(c.engagementRate) >= 10 ? 0 : 1)}%`, auto: true });
    if (fields.age && c.ageGroup) out.push({ label: 'Age', value: String(c.ageGroup), auto: true });
    if (fields.gender && c.gender) out.push({ label: 'Gender', value: c.gender === 'F' ? 'Woman' : 'Man', auto: true });
    if (fields.country && (c.country || c.location)) out.push({ label: 'Country', value: c.location || regionName(String(c.country)), auto: true });
    if (fields.language && c.language) out.push({ label: 'Language', value: languageName(String(c.language)), auto: true });
    if (fields.interests && Array.isArray(c.interests) && c.interests.length) out.push({ label: 'Interests', value: c.interests.slice(0, 3).map((x: string) => x.split(',')[0].trim()).join(' · '), auto: true });
    if (fields.audience && c.audience) { const a = c.audience; const parts = [a.topCountries?.[0] ? `${regionName(a.topCountries[0].code)} ${Math.round(a.topCountries[0].pct)}%` : '', a.femalePct != null ? `${a.femalePct}% women` : '', a.topAgeRange ? `mostly ${a.topAgeRange}` : ''].filter(Boolean); if (parts.length) out.push({ label: 'Audience', value: parts.join(' · '), auto: true }); }
    if (fields.real && c.audience?.realFollowersPct != null) out.push({ label: 'Real followers', value: `${Math.round(c.audience.realFollowersPct)}%`, auto: true });
    if (fields.posts && c.contentCount != null) out.push({ label: 'Posts', value: compactNum(Number(c.contentCount)), auto: true });
  }
  const manual: any[] = Array.isArray(item?.stats) ? item.stats : [];
  manual.forEach((st, i) => { if (!out.some((o) => o.label.toLowerCase() === String(st?.label || '').toLowerCase().replace(/\s*rate$/, ' rate'))) out.push({ label: String(st?.label ?? ''), value: String(st?.value ?? ''), auto: false, index: i }); });
  return out;
}
/** A card item from creator-API facts (what "Add creator" inserts). */
export function itemFromCreator(f: any, keep: any = {}): any {
  return {
    ...keep,
    title: keep.title && keep.title !== 'Creator name' ? keep.title : f.name,
    subtitle: `@${f.handle} · ${platformLabel(f.platform)}`,
    body: keep.body || '',
    avatarUrl: f.avatarUrl || keep.avatarUrl, avatar: f.avatarUrl ? { url: f.avatarUrl, kind: 'image', aspect: '1:1' } : keep.avatar,
    stats: Array.isArray(keep.stats) ? keep.stats.filter((st: any) => st?.value) : [],
    creator: f,
  };
}
const SlideEditContext = createContext<SlideEditApi>(NOOP_EDIT);

const EDIT_CSS = `
[data-editable] [data-etext]{cursor:text;border-radius:0.3cqw;transition:box-shadow .12s;min-width:2cqw}
[data-editable] [data-etext]:hover{box-shadow:0 0 0 0.15cqw rgba(190,67,255,.55)}
[data-editable] [data-etext]:focus{outline:none;box-shadow:0 0 0 0.2cqw #be43ff;background:rgba(190,67,255,.06)}
[data-editable] [data-etext]:empty::before{content:attr(data-placeholder);opacity:.38;font-style:normal}
[data-editable] [data-ecard]{position:relative}
[data-editable] [data-ecard] [data-ecard-x]{opacity:0;transition:opacity .12s}
[data-editable] [data-ecard]:hover [data-ecard-x]{opacity:1}
[data-editable] [data-emedia] [data-emedia-tools]{opacity:0;transition:opacity .12s}
[data-editable] [data-emedia]:hover [data-emedia-tools]{opacity:1}
[data-editable] [data-eadd]{opacity:.55;transition:opacity .12s}
[data-editable] [data-eadd]:hover{opacity:1}
[data-editable] [data-ebox]{outline-offset:0.1cqw;touch-action:none;user-select:none;-webkit-user-select:none;-webkit-touch-callout:none}
[data-editable] [data-ebox]:hover{outline:0.1cqw solid #be43ff}
[data-editable] [data-ebox][data-ebox-editing]{user-select:text;-webkit-user-select:text;touch-action:auto}
[data-editable] [data-ebox] [data-etext]{cursor:inherit}
[data-editable] [data-ebox] [data-etext]:hover{box-shadow:none}
[data-editable] [data-ebox] [data-etext]:focus{box-shadow:none;background:transparent;cursor:text}
[data-editable] [data-ebox] [data-ebox-tools]{opacity:0;transition:opacity .12s}
[data-editable] [data-ebox]:hover [data-ebox-tools],[data-editable] [data-ebox]:focus-within [data-ebox-tools]{opacity:1}
`;

export function SlideEditProvider({ api, children }: { api: SlideEditApi; children: ReactNode }) {
  return (
    <SlideEditContext.Provider value={api}>
      {api.editable && <style>{EDIT_CSS}</style>}
      {children}
      {api.editable && <FormatBar />}
    </SlideEditContext.Provider>
  );
}

const WEIGHTS: Array<{ w: number; label: string }> = [
  { w: 400, label: 'Regular' }, { w: 500, label: 'Medium' }, { w: 600, label: 'Semibold' }, { w: 700, label: 'Bold' }, { w: 800, label: 'Extra bold' },
];
const BASE_COLOURS = ['#101828', '#475467', '#98A2B3', '#FFFFFF', '#D92D20', '#F79009', '#12B76A', '#2E90FA', '#7F56D9', '#EE46BC'];

/** Apply one formatting action to a field (the selection, or the whole text when nothing is selected); keeps the selection. */
export function formatField(el: HTMLElement, op: FormatOp, range?: { start: number; end: number } | null): { start: number; end: number } {
  const runs = domToRuns(el);
  const r = range && range.end > range.start ? range : { start: 0, end: runsLength(runs) };
  const next = formatRuns(runs, r.start, r.end, op);
  el.innerHTML = richHtml(runsToMarkup(next));
  el.focus();
  try { setSelectionOffsets(el, r.start, r.end); } catch { /* field re-rendered */ }
  return r;
}

/**
 * Floating formatting bar for the focused text: bold / italic / larger / smaller, colour, weight and font. Every
 * action applies to the selection, or to the whole text when nothing is selected, and is written as the inline
 * markup every renderer reads (editor, PDF, PPTX). Buttons keep the text focused (mousedown is prevented); the
 * custom colour picker takes focus, so the selection is remembered as character offsets and restored.
 */
function FormatBar() {
  const edit = useContext(SlideEditContext);
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null);
  const [menu, setMenu] = useState<null | 'colour' | 'weight' | 'font'>(null);
  const [cur, setCur] = useState<ReturnType<typeof styleAt> | null>(null);
  const field = useRef<HTMLElement | null>(null);
  const saved = useRef<{ start: number; end: number } | null>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const customRef = useRef<HTMLInputElement>(null);

  const refresh = () => {
    const el = field.current; if (!el) return;
    const runs = domToRuns(el);
    const r = saved.current && saved.current.end > saved.current.start ? saved.current : { start: 0, end: runsLength(runs) };
    setCur(styleAt(runs, r.start, r.end));
  };
  useEffect(() => {
    let hideTimer: number | undefined;
    const onFocusIn = (e: FocusEvent) => {
      const el = e.target as HTMLElement | null;
      if (!el || !el.matches?.('[data-etext]')) return;
      window.clearTimeout(hideTimer);
      if (field.current !== el) { field.current = el; saved.current = null; setMenu(null); }
      const r = el.getBoundingClientRect();
      setAnchor({ top: Math.max(8, r.top - 38), left: Math.max(8, Math.min(r.left, window.innerWidth - 470)) });
      refresh();
    };
    const onFocusOut = () => {
      window.clearTimeout(hideTimer);
      hideTimer = window.setTimeout(() => {
        const a = document.activeElement as HTMLElement | null;
        if (a && (barRef.current?.contains(a) || a.matches?.('[data-etext]'))) return;
        setAnchor(null); setMenu(null); field.current = null;
      }, 150);
    };
    const onSelection = () => {
      const el = field.current;
      if (!el || document.activeElement !== el) return;
      saved.current = selectionOffsets(el);
      refresh();
    };
    document.addEventListener('focusin', onFocusIn); document.addEventListener('focusout', onFocusOut);
    document.addEventListener('selectionchange', onSelection);
    return () => { document.removeEventListener('focusin', onFocusIn); document.removeEventListener('focusout', onFocusOut); document.removeEventListener('selectionchange', onSelection); window.clearTimeout(hideTimer); };
  }, []);

  const apply = (op: FormatOp) => {
    const el = field.current; if (!el || !el.isConnected) return;
    saved.current = formatField(el, op, saved.current);
    refresh();
  };
  // The native colour picker takes focus; apply once it is committed.
  useEffect(() => {
    const input = customRef.current; if (!input) return;
    const onChange = () => { apply({ kind: 'style', patch: { c: input.value } }); setMenu(null); };
    input.addEventListener('change', onChange);
    return () => input.removeEventListener('change', onChange);
  });

  if (!anchor) return null;
  const palette = [...new Set([...(edit.palette || []), ...BASE_COLOURS].map((c) => c.toUpperCase()).filter((c) => /^#[0-9A-F]{6}$/.test(c)))].slice(0, 16);
  const btn: CSSProperties = { border: 0, background: 'transparent', color: '#fff', borderRadius: 4, fontSize: 12, lineHeight: '22px', padding: '0 7px', cursor: 'pointer', whiteSpace: 'nowrap' };
  const on: CSSProperties = { background: 'rgba(255,255,255,0.16)' };
  const sep = <span style={{ width: 1, alignSelf: 'stretch', margin: '2px 3px', background: 'rgba(255,255,255,0.2)' }} />;
  const pop: CSSProperties = { position: 'absolute', top: 32, zIndex: 61, borderRadius: 8, background: '#fff', boxShadow: '0 10px 28px rgba(16,24,40,0.22)', border: '1px solid #EAECF0', padding: 6, color: '#101828', fontSize: 12 };
  const item: CSSProperties = { display: 'block', width: '100%', textAlign: 'left', border: 0, background: 'transparent', borderRadius: 6, padding: '5px 8px', cursor: 'pointer', color: '#101828', fontSize: 12 };
  const weightLabel = WEIGHTS.find((x) => x.w === cur?.w)?.label || (cur?.bold ? 'Bold' : 'Weight');
  return (
    <div ref={barRef} role="toolbar" aria-label="Text formatting" title="Applies to the selected words, or the whole text when nothing is selected" onMouseDown={(e) => { if ((e.target as HTMLElement).tagName !== 'INPUT') e.preventDefault(); }} style={{ position: 'fixed', top: anchor.top, left: anchor.left, zIndex: 60, display: 'flex', alignItems: 'center', gap: 2, padding: 3, borderRadius: 8, background: 'rgba(16,24,40,0.94)', boxShadow: '0 6px 20px rgba(16,24,40,0.3)' }}>
      <button type="button" title="Bold (⌘B)" onClick={() => apply({ kind: 'bold' })} style={{ ...btn, ...(cur?.bold ? on : {}), fontWeight: 700 }}>B</button>
      <button type="button" title="Italic (⌘I)" onClick={() => apply({ kind: 'italic' })} style={{ ...btn, ...(cur?.italic ? on : {}), fontStyle: 'italic' }}>I</button>
      <button type="button" title="Larger" onClick={() => apply({ kind: 'size', sz: 'up' })} style={btn}>A+</button>
      <button type="button" title="Smaller" onClick={() => apply({ kind: 'size', sz: 'down' })} style={btn}>A−</button>
      {sep}
      <div style={{ position: 'relative' }}>
        <button type="button" title="Text colour" onClick={() => setMenu(menu === 'colour' ? null : 'colour')} style={{ ...btn, display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 13, height: 13, borderRadius: 3, border: '1px solid rgba(255,255,255,0.5)', background: cur?.c || 'transparent', backgroundImage: cur?.c ? undefined : 'linear-gradient(135deg, transparent 45%, rgba(255,255,255,0.7) 45%, rgba(255,255,255,0.7) 55%, transparent 55%)' }} />Colour ▾
        </button>
        {menu === 'colour' && (
          <div style={{ ...pop, left: 0, width: 184 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 4 }}>
              {palette.map((c) => (
                <button key={c} type="button" title={c} onClick={() => { apply({ kind: 'style', patch: { c } }); setMenu(null); }} style={{ width: 18, height: 18, borderRadius: 4, cursor: 'pointer', background: c, border: cur?.c?.toUpperCase() === c ? '2px solid #7F56D9' : '1px solid #D0D5DD', padding: 0 }} />
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <label style={{ ...item, flex: 1, display: 'flex', alignItems: 'center', gap: 6, position: 'relative', border: '1px solid #EAECF0' }}>
                Custom…
                <input ref={customRef} type="color" defaultValue={cur?.c && /^#[0-9a-fA-F]{6}$/.test(cur.c) ? cur.c : '#101828'} style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }} />
              </label>
              <button type="button" onClick={() => { apply({ kind: 'style', patch: { c: null } }); setMenu(null); }} style={{ ...item, width: 'auto', border: '1px solid #EAECF0' }}>Default</button>
            </div>
          </div>
        )}
      </div>
      <div style={{ position: 'relative' }}>
        <button type="button" title="Font weight" onClick={() => setMenu(menu === 'weight' ? null : 'weight')} style={btn}>{weightLabel} ▾</button>
        {menu === 'weight' && (
          <div style={{ ...pop, left: 0, width: 140 }}>
            {WEIGHTS.map((x) => (
              <button key={x.w} type="button" onClick={() => { apply({ kind: 'style', patch: { w: x.w } }); setMenu(null); }} style={{ ...item, fontWeight: x.w, background: cur?.w === x.w ? '#F4EBFF' : 'transparent' }}>{x.label}</button>
            ))}
          </div>
        )}
      </div>
      <div style={{ position: 'relative' }}>
        <button type="button" title="Font" onClick={() => setMenu(menu === 'font' ? null : 'font')} style={{ ...btn, maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis' }}>{cur?.f || 'Font'} ▾</button>
        {menu === 'font' && (
          <div style={{ ...pop, left: 0, width: 190, maxHeight: 280, overflowY: 'auto' }}>
            <button type="button" onClick={() => { apply({ kind: 'style', patch: { f: null } }); setMenu(null); }} style={{ ...item, background: !cur?.f ? '#F4EBFF' : 'transparent' }}>Deck font</button>
            {FONT_CHOICES.map((f) => (
              <button key={f.family} type="button" onClick={() => { ensureFontLoaded(f.family); apply({ kind: 'style', patch: { f: f.family } }); setMenu(null); }} style={{ ...item, fontFamily: `'${f.family}', system-ui, sans-serif`, background: cur?.f === f.family ? '#F4EBFF' : 'transparent' }}>{f.label}</button>
            ))}
          </div>
        )}
      </div>
      {sep}
      <button type="button" title="Clear formatting" onClick={() => apply({ kind: 'clear' })} style={btn}>Clear</button>
    </div>
  );
}

/** Load a Google font for the editor preview (the deck's font loader picks it up once the text is saved). */
function ensureFontLoaded(family: string) {
  const id = `font-${family.replace(/\s+/g, '-')}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link'); link.id = id; link.rel = 'stylesheet'; link.href = fontHref([family]);
  document.head.appendChild(link);
}

/** Set a dotted path ('metrics.1.value') on a shallow-cloned object tree. */
export function setPath(obj: any, path: string, value: unknown): any {
  const keys = path.split('.');
  const root = Array.isArray(obj) ? [...obj] : { ...obj };
  let cur: any = root;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    const next = cur[k];
    cur[k] = Array.isArray(next) ? [...next] : { ...(next || {}) };
    cur = cur[k];
  }
  const last = keys[keys.length - 1];
  if (value === undefined) delete cur[last]; else cur[last] = value;
  return root;
}

// ─── Editable atoms ─────────────────────────────────────────────────────────

/**
 * Editable text. Read-only: renders the value (nothing when empty).
 * Editable: contentEditable in place; empty shows `placeholder`; clearing an
 * optional field removes it from the slide.
 */
function EText({ path, value, style, tag = 'div', placeholder, multiline, fallback, className }: {
  path: string; value: unknown; style?: CSSProperties; tag?: string; placeholder?: string; multiline?: boolean;
  /** Read-only text shown when the value is empty (e.g. deck title on the cover). */
  fallback?: string; className?: string;
}) {
  const edit = useContext(SlideEditContext);
  const ref = useRef<HTMLElement>(null);
  const text = value == null ? '' : String(value);
  const Tag = tag as any;

  // Keep the DOM in sync when the value changes from outside (Kai, undo)
  // without fighting the caret while the user types. Edit mode shows the
  // markup as real bold / italic (like Slides or Canva) and serialises back to it.
  useEffect(() => {
    const el = ref.current;
    if (!el || document.activeElement === el) return;
    const html = richHtml(text);
    if (el.innerHTML !== html) el.innerHTML = html;
  }, [text]);

  if (!edit.editable) {
    const shown = text || fallback || '';
    if (!shown) return null;
    const rich = hasMarkup(shown);
    return <Tag className={className} style={{ ...style, whiteSpace: multiline && !rich ? 'pre-line' : undefined }}>{rich ? richNodes(shown) : shown}</Tag>;
  }

  const commit = () => {
    const el = ref.current;
    if (!el) return;
    const next = htmlToMarkup(el).replace(/\u00a0/g, ' ').replace(/\n+$/, '').trim();
    if (next === text) return;
    edit.update((s) => setPath(s, path, next === '' ? undefined : next));
  };

  return (
    <Tag
      ref={ref}
      className={className}
      data-etext=""
      data-placeholder={placeholder || 'Add text'}
      contentEditable
      suppressContentEditableWarning
      spellCheck={false}
      style={{ ...style, whiteSpace: 'pre-wrap', outline: 'none' }}
      onBlur={commit}
      onKeyDown={(e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key === 'Enter' && !multiline && !e.shiftKey) { e.preventDefault(); (e.currentTarget as HTMLElement).blur(); }
        if (e.key === 'Escape') { if (ref.current) ref.current.innerHTML = richHtml(text); (e.currentTarget as HTMLElement).blur(); }
        // ⌘B / ⌘I toggle bold / italic on the selection, as in Slides and Canva.
        if ((e.metaKey || e.ctrlKey) && (e.key === 'b' || e.key === 'B')) { e.preventDefault(); document.execCommand('bold'); }
        if ((e.metaKey || e.ctrlKey) && (e.key === 'i' || e.key === 'I')) { e.preventDefault(); document.execCommand('italic'); }
      }}
      onPaste={(e: React.ClipboardEvent<HTMLElement>) => {
        e.preventDefault();
        document.execCommand('insertText', false, e.clipboardData.getData('text/plain'));
      }}
      dangerouslySetInnerHTML={{ __html: richHtml(text) }}
    />
  );
}

export { richHtml, htmlToMarkup };

/** Minimal inline markup for display: **bold**, *italic*, ++larger++, ~~smaller~~, {style|spans}, line breaks. */
export function richNodes(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = tokenRe();
  let last = 0; let m: RegExpExecArray | null; let k = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok === '\n') out.push(<br key={k++} />);
    else if (m[1] !== undefined) { const st = parseStyle(m[1]); out.push(<span key={k++} style={{ color: st.c, fontWeight: st.w, fontStyle: st.i ? 'italic' : undefined, fontFamily: st.f ? `'${st.f}', system-ui, sans-serif` : undefined }}>{richNodes(m[2])}</span>); }
    else if (tok.startsWith('**')) out.push(<strong key={k++} style={{ fontWeight: 700 }}>{richNodes(tok.slice(2, -2))}</strong>);
    else if (tok.startsWith('++')) out.push(<span key={k++} style={{ fontSize: '1.28em' }}>{richNodes(tok.slice(2, -2))}</span>);
    else if (tok.startsWith('~~')) out.push(<span key={k++} style={{ fontSize: '0.8em' }}>{richNodes(tok.slice(2, -2))}</span>);
    else out.push(<em key={k++}>{richNodes(tok.slice(1, -1))}</em>);
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/** "**Lead** — rest" / "**Lead.** rest" → [lead, rest]; null when the bullet has no bold lead. */
export function splitLead(text: string): [string, string] | null {
  const m = /^\*\*(.+?)\*\*\s*(?:[—–:-]\s*)?([\s\S]*)$/.exec(text.trim());
  return m ? [m[1], m[2]] : null;
}

function ToolButton({ label, onClick, danger }: { label: string; onClick: () => void; danger?: boolean }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      style={{
        pointerEvents: 'auto', cursor: 'pointer', border: 'none', borderRadius: 999,
        padding: '0.5cqh 1cqw', fontSize: '0.95cqw', fontWeight: 600, lineHeight: 1.4,
        background: danger ? 'rgba(180,35,24,0.9)' : 'rgba(17,24,39,0.85)', color: '#fff',
      }}
    >
      {label}
    </button>
  );
}

/** Editable media frame. */
function EMedia({ path, media, style, placeholder = 'Add image', frameRadius = '1cqw' }: {
  path: string; media: any; style?: CSSProperties; placeholder?: string; frameRadius?: string;
}) {
  const edit = useContext(SlideEditContext);
  const has = !!(media?.url || media?.posterUrl);
  const frameRef = useRef<HTMLDivElement>(null);
  // Live focal point while positioning (null = not positioning); committed to media.focus on Done.
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  useEffect(() => { setPos(null); }, [media?.url]);
  const dragFocus = (e: ReactPointerEvent) => {
    e.stopPropagation(); e.preventDefault();
    const el = frameRef.current?.querySelector('img,video') as HTMLImageElement | HTMLVideoElement | null;
    if (!el || !pos) return;
    const box = el.getBoundingClientRect();
    const nw = (el as HTMLImageElement).naturalWidth || (el as HTMLVideoElement).videoWidth || 0;
    const nh = (el as HTMLImageElement).naturalHeight || (el as HTMLVideoElement).videoHeight || 0;
    if (!nw || !nh || !box.width || !box.height) return;
    // object-fit: cover → the overflow on each axis is what the drag can move through.
    const scale = Math.max(box.width / nw, box.height / nh);
    const ox = nw * scale - box.width; const oy = nh * scale - box.height;
    const start = { x: e.clientX, y: e.clientY }; const from = pos;
    const clamp = (v: number) => Math.max(0, Math.min(100, Math.round(v * 10) / 10));
    const move = (ev: PointerEvent) => {
      const dx = ev.clientX - start.x; const dy = ev.clientY - start.y;
      setPos({ x: ox > 1 ? clamp(from.x - (dx / ox) * 100) : from.x, y: oy > 1 ? clamp(from.y - (dy / oy) * 100) : from.y });
    };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };

  if (!edit.editable) {
    if (!has) return null;
    return <MediaFrame media={media} style={style} radius={frameRadius} />;
  }

  // Keep a preset fit (e.g. accent-fit layouts) when the image is picked.
  const pick = () => edit.pickMedia((m) => edit.update((s) => setPath(s, path, media?.fit ? { ...m, fit: media.fit } : m)));
  if (!has) {
    return (
      <div
        data-eadd=""
        onClick={pick}
        style={{
          ...style, cursor: 'pointer', borderRadius: frameRadius,
          border: '0.15cqw dashed rgba(124,58,237,0.6)', background: 'rgba(124,58,237,0.04)',
          display: 'grid', placeItems: 'center', color: '#7C3AED', ...TYPE.bodySmall, fontWeight: 600,
        }}
      >
        + {placeholder}
      </div>
    );
  }
  return (
    <div data-emedia="" ref={frameRef} style={{ position: 'relative', ...style }}>
      <MediaFrame media={pos ? { ...media, focus: pos } : media} style={{ width: '100%', height: '100%' }} radius={frameRadius} />
      {pos ? (
        <>
          {/* Position mode: drag the picture inside its frame; the focal point is stored as media.focus (percent). */}
          <div data-emedia-pos="" onPointerDown={dragFocus} style={{ position: 'absolute', inset: 0, cursor: 'move', zIndex: 3, touchAction: 'none' }} />
          <div style={{ position: 'absolute', left: '50%', bottom: '1.5cqh', transform: 'translateX(-50%)', zIndex: 4, display: 'flex', alignItems: 'center', gap: '0.6cqw', pointerEvents: 'none' }}>
            <span style={{ borderRadius: 999, padding: '0.5cqh 1cqw', fontSize: '0.95cqw', fontWeight: 600, background: 'rgba(17,24,39,0.85)', color: '#fff' }}>Drag to position</span>
            <ToolButton label="Done" onClick={() => { edit.update((s) => setPath(s, `${path}.focus`, pos)); setPos(null); }} />
          </div>
        </>
      ) : (
        <div data-emedia-tools="" style={{ position: 'absolute', top: '1.2cqh', right: '0.8cqw', display: 'flex', gap: '0.5cqw', pointerEvents: 'none' }}>
          {media?.fit !== 'contain' && <ToolButton label="Position" onClick={() => setPos(media?.focus && typeof media.focus.x === 'number' ? { x: media.focus.x, y: media.focus.y } : { x: 50, y: 50 })} />}
          <ToolButton label="Replace" onClick={pick} />
          <ToolButton label="Remove" danger onClick={() => edit.update((s) => setPath(s, path, undefined))} />
        </div>
      )}
    </div>
  );
}

/** Wrap a list card: shows a remove "×" on hover in edit mode. */
function ECard({ listPath, index, children, style }: { listPath: string; index: number; children: ReactNode; style?: CSSProperties }) {
  const edit = useContext(SlideEditContext);
  if (!edit.editable) return <div style={style}>{children}</div>;
  // Reorder: swap with the neighbour in the list (the list's own order is the visual order on every layout).
  const move = (dir: -1 | 1) => edit.update((s) => { const list = [...(s[listPath] || [])]; const j = index + dir; if (j < 0 || j >= list.length) return s; [list[index], list[j]] = [list[j], list[index]]; return { ...s, [listPath]: list }; });
  const btn: CSSProperties = { width: '2.2cqw', height: '2.2cqw', borderRadius: '50%', border: 'none', background: 'rgba(17,24,39,0.85)', color: '#fff', fontSize: '1.1cqw', lineHeight: 1, cursor: 'pointer', display: 'grid', placeItems: 'center' };
  return (
    <div data-ecard="" style={style}>
      {children}
      <div data-ecard-x="" style={{ position: 'absolute', top: '-1.2cqh', left: '-0.6cqw', display: 'flex', gap: '0.3cqw', zIndex: 2 }}>
        <button type="button" title="Move earlier" onMouseDown={(e) => e.preventDefault()} onClick={(e) => { e.stopPropagation(); move(-1); }} style={btn}>‹</button>
        <button type="button" title="Move later" onMouseDown={(e) => e.preventDefault()} onClick={(e) => { e.stopPropagation(); move(1); }} style={btn}>›</button>
      </div>
      <button
        type="button"
        data-ecard-x=""
        title="Remove"
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => { e.stopPropagation(); edit.update((s) => { const list = [...(s[listPath] || [])]; list.splice(index, 1); return { ...s, [listPath]: list }; }); }}
        style={{ position: 'absolute', top: '-1.2cqh', right: '-0.6cqw', width: '2.2cqw', height: '2.2cqw', borderRadius: '50%', border: 'none', background: 'rgba(180,35,24,0.9)', color: '#fff', fontSize: '1.2cqw', lineHeight: 1, cursor: 'pointer', zIndex: 2 }}
      >
        ×
      </button>
    </div>
  );
}

/** "+ Add" tile at the end of a list (edit mode only). */
/** "+ Add card" for a list: a floating pill that takes NO layout space, so the editor's grid is exactly the presentation's. */
function EAddCard({ listPath, blank, max, count, label = 'Add', onAdd, style }: { listPath: string; blank: () => any; max: number; count: number; label?: string; style?: CSSProperties; onAdd?: () => void }) {
  const edit = useContext(SlideEditContext);
  if (!edit.editable || count >= max) return null;
  return (
    <div
      data-eadd=""
      onClick={() => (onAdd ? onAdd() : edit.update((s) => ({ ...s, [listPath]: [...(s[listPath] || []), blank()] })))}
      title={`${label} (up to ${max})`}
      style={{
        position: 'absolute', right: '1cqw', bottom: '1cqh', zIndex: 4, cursor: 'pointer', borderRadius: 999,
        background: 'rgba(124,58,237,0.92)', color: '#fff', padding: '0.5cqh 1cqw', ...TYPE.caption, fontWeight: 700, boxShadow: '0 0.4cqw 1.2cqw rgba(16,24,40,0.2)', whiteSpace: 'nowrap',
        // Only the offsets from a caller's style apply: the pill stays a pill, but can move off an aside panel.
        ...(style?.right !== undefined ? { right: style.right } : {}), ...(style?.bottom !== undefined ? { bottom: style.bottom } : {}),
      }}
    >
      + {label}
    </div>
  );
}

function useEdit() { return useContext(SlideEditContext); }

// ─── Shared atoms ───────────────────────────────────────────────────────────

/** Where a cover-fitted picture is anchored inside its frame: media.focus {x, y} in percent, centre by default. */
export function focusPos(media: any): string {
  const f = media?.focus;
  const n = (v: unknown, d: number) => (typeof v === 'number' && Number.isFinite(v) ? Math.max(0, Math.min(100, v)) : d);
  return `${n(f?.x, 50)}% ${n(f?.y, 50)}%`;
}

export function MediaFrame({ media, style, radius = '1cqw' }: { media: any; style?: CSSProperties; radius?: string }) {
  // Click the play badge → the poster is replaced by a real <video> with native
  // controls (sound on, volume/mute available). Works in the editor and in
  // Present mode; the state resets when the media changes (next page).
  const [playing, setPlaying] = useState(false);
  const [videosMuted] = useVideosMuted();
  useEffect(() => { setPlaying(false); }, [media?.url]);
  if (!media?.url && !media?.posterUrl) return null;
  const embed = embedUrlOf(media.href || media.embedUrl);
  // A direct video file plays natively; a TikTok / Instagram permalink plays through the platform's embed player.
  const isVideo = (media.kind === 'video' && !!media.url) || !!embed;
  const src = media.kind === 'video' ? (media.posterUrl || media.url) : media.url;
  const player = (extra: CSSProperties = {}) => (embed && !(media.kind === 'video' && /\.(mp4|mov|webm|m4v)(\?|$)/i.test(media.url || ''))
    ? <iframe src={embed} title={media.alt || 'Post'} allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen style={{ width: '100%', height: '100%', border: 0, display: 'block', background: '#000', ...extra }} />
    : <video src={media.url} poster={media.posterUrl || undefined} controls autoPlay muted={videosMuted} playsInline onEnded={() => setPlaying(false)} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: focusPos(media), display: 'block', background: '#000', ...extra }} />);
  const fit = media.fit === 'contain' ? 'contain' : 'cover';
  const stop = (e: { stopPropagation: () => void }) => e.stopPropagation();
  // 99% of creator content is vertical: portrait media gets a portrait frame
  // fitted by height and centred in the slot, never stretched into a landscape box.
  // An explicit 16:9 / 1:1 gets a landscape frame: full width of the slot, height from the ratio.
  const aspect = mediaAspect(media) || ((media.aspect === '16:9' || media.aspect === '1:1') ? media.aspect : null);
  if (aspect) {
    const phone = media.frame === 'phone';
    const landscape = aspect === '16:9' || aspect === '1:1';
    const inner: CSSProperties = {
      position: 'relative', overflow: 'hidden', aspectRatio: aspect.replace(':', ' / '),
      ...(landscape ? { width: '100%', height: 'auto', maxHeight: '100%' } : { height: '100%', maxWidth: '100%' }),
      borderRadius: phone ? '2.6cqw' : radius, background: playing ? '#000' : 'rgba(0,0,0,0.06)',
      ...(phone ? { border: '0.45cqw solid #111', boxShadow: '0 1.5cqh 4cqh rgba(0,0,0,0.25)', background: '#111' } : {}),
    };
    return (
      <div onClick={isVideo && playing ? stop : undefined} onKeyDown={isVideo && playing ? stop : undefined} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', ...style, background: 'transparent', overflow: 'visible' }}>
        <div style={inner}>
          {isVideo && playing ? player() : (
            <>
              {src ? <img src={src} alt={media.alt || ''} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: focusPos(media), display: 'block' }} />
                   : <video src={`${media.url}#t=0.5`} muted playsInline preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: focusPos(media), display: 'block' }} />}
              {(media.kind === 'video' || embed) && <PlayBadge onClick={isVideo ? (e) => { e.stopPropagation(); setPlaying(true); } : undefined} />}
            </>
          )}
        </div>
      </div>
    );
  }
  if (isVideo && playing) {
    return (
      <div onClick={stop} onKeyDown={stop} style={{ position: 'relative', overflow: 'hidden', borderRadius: radius, background: '#000', ...style }}>
        {player({ objectFit: fit === 'contain' ? 'contain' : 'cover', objectPosition: fit === 'cover' ? focusPos(media) : undefined })}
      </div>
    );
  }
  return (
    <div style={{ position: 'relative', overflow: 'hidden', borderRadius: radius, background: 'rgba(0,0,0,0.06)', ...style }}>
      {src ? (
        <img src={src} alt={media.alt || ''} style={{ width: '100%', height: '100%', objectFit: fit, objectPosition: fit === 'cover' ? focusPos(media) : undefined, display: 'block' }} />
      ) : (
        <video src={`${media.url}#t=0.5`} muted playsInline preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: focusPos(media), display: 'block' }} />
      )}
      {(media.kind === 'video' || embed) && <PlayBadge onClick={isVideo ? (e) => { e.stopPropagation(); setPlaying(true); } : undefined} />}
    </div>
  );
}

/** Platform embed player for a post permalink: TikTok's player (needs the numeric video id) or Instagram's /embed page. */
export function embedUrlOf(href: unknown): string | null {
  if (typeof href !== 'string') return null;
  const tt = href.match(/tiktok\.com\/.*\/video\/(\d+)/);
  if (tt) return `https://www.tiktok.com/player/v1/${tt[1]}?autoplay=1&music_info=0&description=0&rel=0`;
  const ig = href.match(/instagram\.com\/(p|reel|reels|tv)\/([A-Za-z0-9_-]+)/);
  if (ig) return `https://www.instagram.com/${ig[1] === 'reels' ? 'reel' : ig[1]}/${ig[2]}/embed/`;
  return null;
}

/** Portrait aspect for a media item: explicit `aspect`, else vertical for videos (reels, stories, TikToks). */
export function mediaAspect(media: any): '9:16' | '4:5' | null {
  const a = media?.aspect;
  if (a === '9:16' || a === '4:5') return a;
  if (a === '1:1' || a === '16:9' || a === 'fill') return null; // 'fill' = cover the whole slot (covers, side photos)
  // No explicit aspect → mobile 9:16 frame (user preference: everything is shown phone-sized).
  return '9:16';
}

/** Creator thumbnail beside a name — a circle with the initial when the image is missing or fails. */
function Avatar({ url, name, size = '2.2cqw', color }: { url?: string | null; name?: string | null; size?: string; color: string }) {
  const initial = String(name || '?').trim().charAt(0).toUpperCase() || '?';
  return (
    <span data-avatar="" style={{ position: 'relative', display: 'inline-grid', placeItems: 'center', width: size, height: size, borderRadius: '50%', flexShrink: 0, overflow: 'hidden', background: color, color: '#fff', fontWeight: 700, fontSize: `calc(${size} * 0.45)`, verticalAlign: 'middle' }}>
      {initial}
      {url && <img src={url} alt="" referrerPolicy="no-referrer" onError={(e) => { e.currentTarget.style.display = 'none'; }} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
    </span>
  );
}

function PlayBadge({ onClick }: { onClick?: (e: React.MouseEvent) => void }) {
  return (
    <div
      role={onClick ? 'button' : undefined}
      title={onClick ? 'Play video' : undefined}
      onClick={onClick}
      style={{ position: 'absolute', left: '50%', top: '50%', width: '5.5cqw', height: '5.5cqw', margin: '-2.75cqw 0 0 -2.75cqw', borderRadius: '50%', background: 'rgba(0,0,0,0.55)', border: '0.18cqw solid rgba(255,255,255,0.9)', cursor: onClick ? 'pointer' : undefined, pointerEvents: 'auto' }}
    >
      <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-35%, -50%)', borderStyle: 'solid', borderWidth: '1.1cqw 0 1.1cqw 1.9cqw', borderColor: 'transparent transparent transparent #fff' }} />
    </div>
  );
}

function Eyebrow({ path, text, color, placeholder = 'Add eyebrow' }: { path: string; text?: string; color: string; placeholder?: string }) {
  return <EText path={path} value={text} placeholder={placeholder} style={{ ...TYPE.caption, color, marginBottom: '1.2cqh' }} />;
}

function BrandMark({ theme, resolved }: { theme: Theme; resolved: ResolvedTheme }) {
  if (!theme.brandName) return null;
  return (
    <div style={{ position: 'absolute', bottom: '4cqh', right: '4cqw', ...TYPE.caption, color: resolved.isInverse ? 'rgba(255,255,255,0.7)' : theme.primary, opacity: 0.85 }}>
      {theme.brandName}
    </div>
  );
}

/** Brand logo on a dark surface: wordmarks are inverted to white; square icons (which inversion would
 *  turn into a blob) are shown as-is on a small white plate. Decided from the image's own proportions. */
function LogoImg({ src, inverse, style }: { src: string; inverse: boolean; style?: CSSProperties }) {
  const [square, setSquare] = useState(false);
  const plate = inverse && square;
  return (
    <img
      src={src}
      alt=""
      onLoad={(e) => { const img = e.currentTarget; if (img.naturalHeight && img.naturalWidth / img.naturalHeight < 1.6) setSquare(true); }}
      style={{ ...style, filter: inverse && !plate ? 'brightness(0) invert(1)' : 'none', ...(plate ? { background: '#fff', padding: '0.5cqh 0.5cqw', borderRadius: '0.8cqw', boxSizing: 'content-box' } : {}) }}
    />
  );
}

function CornerLogo({ url, isInverse }: { url?: string; isInverse: boolean }) {
  if (!url) return null;
  return (
    <LogoImg inverse={isInverse} src={url} style={{ position: 'absolute', top: '5cqh', left: '4cqw', maxHeight: '5cqh', maxWidth: '14cqw', objectFit: 'contain', objectPosition: 'left center', opacity: isInverse ? 0.95 : 1 }} />
  );
}

function ProminentLogo({ url, isInverse }: { url?: string; isInverse: boolean }) {
  if (!url) return null;
  return (
    <LogoImg inverse={isInverse} src={url} style={{ position: 'absolute', top: '8cqh', left: GUTTER, maxHeight: '7cqh', maxWidth: '20cqw', objectFit: 'contain', objectPosition: 'left center' }} />
  );
}

function baseStyle(resolved: ResolvedTheme, font: string): CSSProperties {
  return { position: 'absolute', inset: 0, background: resolved.bg, color: resolved.text, fontFamily: font, overflow: 'hidden' };
}

function SlideHeader({ slide, eyebrowColor, resolved, inline }: { slide: any; eyebrowColor: string; resolved: ResolvedTheme; inline?: boolean }) {
  const edit = useEdit();
  if (!edit.editable && !slide.title && !slide.eyebrow) return null;
  return (
    <div style={inline ? { position: 'relative' } : { position: 'absolute', top: '16cqh', left: GUTTER, right: GUTTER }}>
      <Eyebrow path="eyebrow" text={slide.eyebrow} color={eyebrowColor} />
      <EText path="title" value={slide.title} tag="h2" placeholder="Add a title" style={{ ...TYPE.headline, color: resolved.text, margin: 0, maxWidth: '76cqw' }} />
      <EText path="kicker" value={slide.kicker} placeholder="Add a kicker line" style={{ ...TYPE.body, color: resolved.textMuted, marginTop: '1.6cqh', maxWidth: '64cqw' }} />
    </div>
  );
}

// ─── Router ─────────────────────────────────────────────────────────────────

const DEFAULT_THEME_KIND: Record<string, ThemeKind> = { section: 'brand', quote: 'brand', headline: 'soft' };

/** Per-page colour overrides (`bgColor` / `textColor`): every theme kind then resolves to these colours. Mirrors pdfRenderer.slideTheme. */
export function slideTheme(slide: any, theme: Theme): Theme {
  let t: Theme = slide.accent ? { ...theme, primary: withHash(slide.accent) || theme.primary, bgBrand: withHash(slide.accent) || theme.bgBrand } : theme;
  const bg = withHash(slide.bgColor); const tx = withHash(slide.textColor);
  if (!bg && !tx) return t;
  const base = bg || t.bg; const dark = lum(base) < 0.5;
  const text = tx || (dark ? '#FFFFFF' : t.text);
  const muted = tx ? hexToRgba(tx, 0.72) : (dark ? 'rgba(255,255,255,0.72)' : t.textMuted);
  const subtle = tx ? hexToRgba(tx, 0.5) : (dark ? 'rgba(255,255,255,0.5)' : t.textSubtle);
  t = { ...t, bg: base, bgSoft: base, bgDark: base, bgBrand: bg ? base : t.bgBrand, surface: hexToRgba(text, 0.06), text, textMuted: muted, textSubtle: subtle, textInverse: text, textInverseMuted: muted, border: hexToRgba(text, 0.14), borderStrong: hexToRgba(text, 0.28) };
  return t;
}

export function SlideRenderer({ slide, index, theme, title }: { slide: any; index: number; theme: Theme; title: string }) {
  const inferredType = slide.type ?? (index === 0 ? 'title' : (slide.chart ? 'chart' : (slide.metrics ? 'metrics' : (slide.columns ? 'columns' : 'content'))));
  const t: Theme = slideTheme(slide, theme);
  const kind: ThemeKind = slide.theme ?? DEFAULT_THEME_KIND[inferredType] ?? 'light';
  const mark = theme.beatlyMark && slide.beatlyMark !== false ? <BeatlyMark isInverse={kind === 'dark' || kind === 'brand' || slide.variant === 'photo'} /> : null;
  return <>{inferredType === 'free' ? <FreePage slide={slide} theme={t} /> : renderSlideBody(inferredType, slide, index, t, title)}<TextBoxes slide={slide} resolved={resolveTheme(t, kind)} theme={t} />{mark}</>;
}

// ─── Free elements (percent coordinates of the 16:9 stage) ──────────────────────────────────
// Text, chart, image, shape and metric placed anywhere. Legacy `boxes` (text only) render as text
// elements; edits to them write back to `boxes`, new elements live in `elements`.

const BOX_DEFAULT = { x: 10, y: 10, w: 40, h: 12, size: 1.6 };
const clampPct = (v: unknown, d: number) => { const n = Number(v); return Number.isFinite(n) && v !== null && v !== '' ? Math.max(0, Math.min(100, n)) : d; };

/** Selection is shared across the page so the toolbar and the keyboard know which element is active. */
export const ElementSelection = createContext<{ selected: string | null; select: (id: string | null, add?: boolean) => void; multi?: string[] }>({ selected: null, select: () => {}, multi: [] });

function TextBoxes({ slide, resolved, theme }: { slide: any; resolved: ResolvedTheme; theme: Theme }) {
  const edit = useEdit();
  const sel = useContext(ElementSelection);
  const legacy: any[] = Array.isArray(slide.boxes) ? slide.boxes : [];
  const els: FreeElement[] = Array.isArray(slide.elements) ? slide.elements.filter((e: any) => e && typeof e === 'object' && e.kind) : [];
  if (!legacy.length && !els.length) return null;
  const sorted = els.map((e, i) => ({ e, i })).sort((a, b) => (a.e.z ?? 1) - (b.e.z ?? 1) || a.i - b.i);
  return (
    <div data-tboxes="" style={{ position: 'absolute', inset: 0, pointerEvents: edit.editable ? 'auto' : 'none', zIndex: 5 }} onPointerDown={(e) => { if (e.target === e.currentTarget) sel.select(null); }}>
      {legacy.map((b, i) => <FreeBox key={b.id || `legacy-${i}`} el={{ id: b.id || `legacy-${i}`, kind: 'text', x: b.x, y: b.y, w: b.w, h: b.h, text: b.text || '', size: b.size, color: b.color, bold: b.bold, align: b.align, background: b.background } as FreeElement} listKey="boxes" i={i} resolved={resolved} theme={theme} />)}
      {sorted.map(({ e, i }) => <FreeBox key={e.id} el={e} listKey="elements" i={i} resolved={resolved} theme={theme} />)}
    </div>
  );
}

/** Snap a moving edge to the page's guide lines (edges, centre, thirds) within `tol` percent. */
function snap(v: number, tol = 1.2): number {
  for (const g of [0, 25, 33.3, 50, 66.7, 75, 100]) if (Math.abs(v - g) <= tol) return g;
  return v;
}

type Corner = 'nw' | 'ne' | 'sw' | 'se';
/** Controls inside an element that handle their own presses (handles, media tools, image positioning, buttons, fields). */
const OWN_POINTER = '[data-ebox-handle],[data-ebox-tools],[data-emedia-tools],[data-emedia-pos],button,input,select,textarea,a';

function FreeBox({ el, listKey, i, resolved, theme }: { el: FreeElement; listKey: 'boxes' | 'elements'; i: number; resolved: ResolvedTheme; theme: Theme }) {
  const edit = useEdit();
  const sel = useContext(ElementSelection);
  const ref = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [guides, setGuides] = useState<{ v?: number; h?: number } | null>(null);
  const geo = drag || { x: clampPct(el.x, BOX_DEFAULT.x), y: clampPct(el.y, BOX_DEFAULT.y), w: clampPct(el.w, BOX_DEFAULT.w), h: clampPct(el.h, BOX_DEFAULT.h) };
  const selected = sel.selected === el.id || (sel.multi || []).includes(el.id);
  const patch = (fields: Record<string, unknown>) => edit.update((s) => { const list = [...(Array.isArray(s[listKey]) ? s[listKey] : [])]; list[i] = { ...list[i], ...fields }; return { ...s, [listKey]: list }; });

  // Canva-style: press anywhere on the element and drag to move it (mouse or touch); a press without movement
  // selects it, and a press on an already selected text puts the caret there. Corner handles resize.
  const start = (mode: 'move' | Corner, onClick?: (ev: PointerEvent) => void) => (e: React.PointerEvent) => {
    if (!edit.editable) return;
    e.preventDefault(); e.stopPropagation(); sel.select(el.id, e.shiftKey);
    const stage = ref.current?.parentElement?.getBoundingClientRect(); if (!stage) return;
    const startX = e.clientX; const startY = e.clientY; const g0 = { ...geo };
    const keepRatio = el.kind === 'image' && e.shiftKey;
    let moved = false;
    const next = (ev: PointerEvent) => {
      const dx = ((ev.clientX - startX) / stage.width) * 100; const dy = ((ev.clientY - startY) / stage.height) * 100;
      if (mode === 'move') {
        let x = Math.max(0, Math.min(100 - g0.w, g0.x + dx)); let y = Math.max(0, Math.min(100 - g0.h, g0.y + dy));
        const sx = snap(x); const sxr = snap(x + g0.w); const sxc = snap(x + g0.w / 2);
        const sy = snap(y); const syb = snap(y + g0.h); const syc = snap(y + g0.h / 2);
        let gv: number | undefined; let gh: number | undefined;
        if (sx !== x) { x = sx; gv = sx; } else if (sxr !== x + g0.w) { x = sxr - g0.w; gv = sxr; } else if (sxc !== x + g0.w / 2) { x = sxc - g0.w / 2; gv = sxc; }
        if (sy !== y) { y = sy; gh = sy; } else if (syb !== y + g0.h) { y = syb - g0.h; gh = syb; } else if (syc !== y + g0.h / 2) { y = syc - g0.h / 2; gh = syc; }
        setGuides(gv != null || gh != null ? { v: gv, h: gh } : null);
        return { ...g0, x, y };
      }
      // Resize from the corner held: the opposite corner stays put.
      const left = mode === 'nw' || mode === 'sw'; const top = mode === 'nw' || mode === 'ne';
      const right0 = g0.x + g0.w; const bottom0 = g0.y + g0.h;
      let w = left ? Math.max(4, Math.min(right0, g0.w - dx)) : Math.max(4, Math.min(100 - g0.x, g0.w + dx));
      let h = top ? Math.max(3, Math.min(bottom0, g0.h - dy)) : Math.max(3, Math.min(100 - g0.y, g0.h + dy));
      if (keepRatio) {
        const room = top ? bottom0 : 100 - g0.y;
        h = Math.max(3, (w / g0.w) * g0.h);
        if (h > room) { h = room; w = (h / g0.h) * g0.w; }
      }
      if (!left) { const sr = snap(g0.x + w); if (sr !== g0.x + w) w = sr - g0.x; }
      return { x: left ? right0 - w : g0.x, y: top ? bottom0 - h : g0.y, w, h };
    };
    const onMove = (ev: PointerEvent) => { if (!moved && Math.hypot(ev.clientX - startX, ev.clientY - startY) < 4) return; moved = true; setDrag(next(ev)); };
    const onUp = (ev: PointerEvent) => {
      window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp); window.removeEventListener('pointercancel', onUp);
      if (!moved) { setDrag(null); setGuides(null); if (ev.type === 'pointerup') onClick?.(ev); return; }
      // The click that follows a drag must not open an image picker or place a caret.
      const swallow = (c: Event) => { c.stopPropagation(); c.preventDefault(); };
      window.addEventListener('click', swallow, { capture: true, once: true }); setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 0);
      const g = next(ev); setDrag(null); setGuides(null);
      if (Math.abs(g.x - g0.x) > 0.2 || Math.abs(g.y - g0.y) > 0.2 || Math.abs(g.w - g0.w) > 0.2 || Math.abs(g.h - g0.h) > 0.2) patch(Object.fromEntries(Object.entries(g).map(([k, v]) => [k, Math.round(v * 10) / 10])));
    };
    window.addEventListener('pointermove', onMove); window.addEventListener('pointerup', onUp); window.addEventListener('pointercancel', onUp);
  };

  // Typing mode: the caret is in one of this element's texts. Then presses select text instead of moving the box.
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (!editing) return;
    const off = () => { if (!ref.current?.contains(document.activeElement)) setEditing(false); };
    document.addEventListener('focusout', off); document.addEventListener('focusin', off);
    return () => { document.removeEventListener('focusout', off); document.removeEventListener('focusin', off); };
  }, [editing]);
  const typeAt = (x: number, y: number) => {
    const box = ref.current; if (!box) return;
    const hit = (document.elementFromPoint(x, y) as HTMLElement | null)?.closest('[data-etext]') as HTMLElement | null;
    const target = hit && box.contains(hit) ? hit : (el.kind === 'text' ? box.querySelector('[data-etext]') as HTMLElement | null : null);
    if (!target) return;
    setEditing(true); target.focus();
    const range = (document as any).caretRangeFromPoint?.(x, y) as Range | undefined;
    const selection = window.getSelection();
    if (selection) { selection.removeAllRanges(); if (range && target.contains(range.startContainer)) selection.addRange(range); else { const r = document.createRange(); r.selectNodeContents(target); r.collapse(false); selection.addRange(r); } }
  };
  const press = (e: React.PointerEvent) => {
    if (!edit.editable || e.button > 0 || editing) return;
    if ((e.target as HTMLElement).closest(OWN_POINTER)) return;
    // Typing in another element ends when this one is pressed (commits it, so Delete acts on the new selection).
    const active = document.activeElement as HTMLElement | null;
    if (active && active !== document.body && !ref.current?.contains(active)) active.blur();
    const wasSelected = selected;
    start('move', (ev) => { if (wasSelected && !ev.shiftKey) typeAt(ev.clientX, ev.clientY); })(e);
  };

  const frame: CSSProperties = {
    position: 'absolute', left: `${geo.x}%`, top: `${geo.y}%`, width: `${geo.w}%`, height: `${geo.h}%`,
    zIndex: el.z ?? 1, opacity: el.opacity ?? 1, transform: el.rotate ? `rotate(${el.rotate}deg)` : undefined,
    pointerEvents: edit.editable ? 'auto' : 'none', outline: edit.editable && selected ? '0.15cqw solid #be43ff' : undefined,
    cursor: edit.editable && !editing ? 'move' : undefined,
  };
  const box: CSSProperties = {
    width: '100%', height: '100%', boxSizing: 'border-box', overflow: 'hidden',
    background: el.background || undefined, borderRadius: el.radius != null ? `${el.radius}cqw` : undefined,
    border: el.border ? `${el.border.width ?? 0.15}cqw solid ${el.border.color}` : undefined,
    boxShadow: el.shadow ? '0 1cqw 3cqw rgba(16,24,40,0.18)' : undefined,
    padding: el.pad != null ? `${el.pad}cqw` : (el.background && (el.kind === 'text' || el.kind === 'metric') ? '0.6cqw 0.9cqw' : undefined),
  };
  return (
    <div ref={ref} data-ebox="" data-ebox-editing={editing ? '' : undefined} style={frame} onPointerDownCapture={press}
      onDoubleClick={(e) => { if (edit.editable && !editing) typeAt(e.clientX, e.clientY); }}>
      <div style={box}><ElementBody el={el} listKey={listKey} i={i} resolved={resolved} theme={theme} /></div>
      {edit.editable && selected && !editing && !sel.multi?.length && (['nw', 'ne', 'sw', 'se'] as Corner[]).map((c) => (
        <div key={c} data-ebox-handle="" title="Drag to resize (shift keeps the image ratio)" onPointerDown={start(c)}
          style={{ position: 'absolute', [c[0] === 'n' ? 'top' : 'bottom']: -7, [c[1] === 'w' ? 'left' : 'right']: -7, width: 14, height: 14, borderRadius: '50%', background: '#fff', border: '2px solid #be43ff', boxShadow: '0 1px 3px rgba(16,24,40,.25)', boxSizing: 'border-box', cursor: c === 'nw' || c === 'se' ? 'nwse-resize' : 'nesw-resize', zIndex: 6, touchAction: 'none' }} />
      ))}
      {guides && (
        <>
          {guides.v != null && <div style={{ position: 'fixed', pointerEvents: 'none' }} />}
        </>
      )}
    </div>
  );
}

function ElementBody({ el, listKey, i, resolved, theme }: { el: FreeElement; listKey: 'boxes' | 'elements'; i: number; resolved: ResolvedTheme; theme: Theme }) {
  const edit = useEdit();
  switch (el.kind) {
    case 'text': {
      const size = Math.max(0.6, Math.min(14, Number(el.size) || BOX_DEFAULT.size));
      const style: CSSProperties = {
        fontSize: `${size}cqw`, lineHeight: el.lineHeight ?? 1.3, color: el.color || resolved.text, textAlign: el.align || 'left', fontWeight: el.bold ? 700 : 400, fontStyle: el.italic ? 'italic' : undefined,
        fontFamily: el.font ? `'${el.font}', ${theme.fontDisplay}` : undefined, letterSpacing: el.letterSpacing != null ? `${el.letterSpacing}em` : undefined, textTransform: el.uppercase ? 'uppercase' : undefined,
        width: '100%', height: '100%', overflow: 'hidden', whiteSpace: 'pre-wrap', display: 'flex', flexDirection: 'column', justifyContent: el.valign === 'middle' ? 'center' : el.valign === 'bottom' ? 'flex-end' : 'flex-start',
      };
      return <EText path={`${listKey}.${i}.text`} value={el.text} multiline placeholder="Text" style={style} />;
    }
    case 'metric': {
      const size = Math.max(2, Math.min(14, Number(el.size) || 5));
      const color = el.color || theme.primary;
      const align = el.align || 'left';
      return (
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: align, fontFamily: el.font ? `'${el.font}', ${theme.fontDisplay}` : undefined }}>
          <EText path={`${listKey}.${i}.value`} value={el.value} placeholder="12.4M" style={{ fontSize: `${size}cqw`, lineHeight: 1, fontWeight: 800, color, letterSpacing: '-0.02em' }} />
          <EText path={`${listKey}.${i}.label`} value={el.label} placeholder="Label" style={{ fontSize: `${size * 0.26}cqw`, marginTop: '0.6cqw', fontWeight: 600, color: resolved.text, textTransform: 'uppercase', letterSpacing: '0.08em' }} />
          {(el.caption || edit.editable) && <EText path={`${listKey}.${i}.caption`} value={el.caption} placeholder="Caption" style={{ fontSize: `${size * 0.24}cqw`, marginTop: '0.4cqw', color: resolved.textMuted }} />}
        </div>
      );
    }
    case 'chart': {
      const svg = chartSvg(el.chart || {}, { primary: theme.primary, accent: theme.accent, text: resolved.text, textMuted: resolved.textMuted, grid: resolved.isInverse ? 'rgba(255,255,255,0.15)' : theme.border, ...(theme.palette?.length ? { palette: theme.palette } : {}) }).replace('<svg ', '<svg style="width:100%;height:100%" ');
      return (
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', fontFamily: theme.fontDisplay }}>
          {(el.title || edit.editable) && <EText path={`${listKey}.${i}.title`} value={el.title} placeholder="Chart title" style={{ ...TYPE.bodySmall, fontWeight: 600, color: resolved.text, marginBottom: '0.6cqw' }} />}
          <div style={{ flex: 1, minHeight: 0 }} dangerouslySetInnerHTML={{ __html: svg }} />
        </div>
      );
    }
    case 'image': {
      const media = el.media || {};
      return <EMedia path={`${listKey}.${i}.media`} media={media.url ? { ...media, aspect: media.fit === 'contain' ? undefined : 'fill' } : media} style={{ width: '100%', height: '100%' }} frameRadius={el.radius != null ? `${el.radius}cqw` : '0'} placeholder="Add image" />;
    }
    case 'shape': {
      const fill = el.fill || theme.primary; const stroke = el.stroke; const sw = el.strokeWidth ?? 0.2;
      if (el.shape === 'line') return <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}><div style={{ width: '100%', height: `${sw * 2}cqw`, background: fill, borderRadius: 999 }} /></div>;
      const radius = el.shape === 'circle' ? '50%' : el.shape === 'pill' ? '999px' : `${el.radius ?? 0.8}cqw`;
      return <div style={{ width: '100%', height: '100%', background: fill, borderRadius: radius, border: stroke ? `${sw}cqw solid ${stroke}` : undefined, boxSizing: 'border-box' }} />;
    }
    default: return null;
  }
}

/** A page that is only elements: the stage is the page background, nothing else is laid out. */
function FreePage({ slide, theme }: { slide: any; theme: Theme }) {
  const edit = useEdit();
  const resolved = resolveTheme(theme, slide.theme ?? 'light');
  const empty = !(Array.isArray(slide.elements) && slide.elements.length) && !(Array.isArray(slide.boxes) && slide.boxes.length);
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      {slide.showLogo !== false && <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />}
      {empty && edit.editable && <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: resolved.textMuted, opacity: 0.7, ...TYPE.body }}>Free Style — add text, a number, a chart, an image or a shape from the toolbox and put it anywhere.</div>}
    </div>
  );
}

function renderSlideBody(inferredType: string, slide: any, index: number, t: Theme, title: string) {
  switch (inferredType) {
    case 'title':      return <TitleSlide slide={slide} theme={t} title={title} />;
    case 'section':    return <SectionSlide slide={slide} theme={t} index={index} />;
    case 'headline':   return <HeadlineSlide slide={slide} theme={t} />;
    case 'bigStat':    return <BigStatSlide slide={slide} theme={t} />;
    case 'metrics':    return <MetricsSlide slide={slide} theme={t} />;
    case 'comparison': return <ComparisonSlide slide={slide} theme={t} />;
    case 'timeline':   return <TimelineSlide slide={slide} theme={t} />;
    case 'columns':    return <ColumnsSlide slide={slide} theme={t} />;
    case 'quote':      return <QuoteSlide slide={slide} theme={t} />;
    case 'chart':      return <ChartSlide slide={slide} theme={t} />;
    case 'closing':    return <ClosingSlide slide={slide} theme={t} />;
    case 'image':      return <ImageSlide slide={slide} theme={t} />;
    case 'showcase':   return <ShowcaseSlide slide={slide} theme={t} />;
    case 'gallery':    return <GallerySlide slide={slide} theme={t} />;
    case 'boxes':      return <BoxesSlide slide={slide} theme={t} />;
    case 'funnel':     return <FunnelSlide slide={slide} theme={t} />;
    case 'table':      return <TableSlide slide={slide} theme={t} />;
    case 'embed':      return <EmbedSlide slide={slide} theme={t} />;
    case 'comments':   return <CommentsSlide slide={slide} theme={t} />;
    case 'content':
    default:           return <ContentSlide slide={slide} theme={t} />;
  }
}

// ─── 1. Title ───────────────────────────────────────────────────────────────

function TitleSlide({ slide, theme, title }: { slide: any; theme: Theme; title: string }) {
  if (slide.variant === 'report') return <CoverReportSlide slide={slide} theme={theme} title={title} />;
  if (slide.variant === 'cobrand') return <CoverCobrandSlide slide={slide} theme={theme} title={title} />;
  if (slide.variant === 'photo') return <CoverPhotoSlide slide={slide} theme={theme} title={title} />;
  if (slide.variant === 'poster') return <CoverPosterSlide slide={slide} theme={theme} title={title} />;
  const resolved = resolveTheme(theme, slide.theme ?? 'light');
  const align: 'left' | 'center' = (slide.layout ?? 'left') === 'center' ? 'center' : 'left';
  const eyebrowColor = resolved.isInverse ? 'rgba(255,255,255,0.85)' : theme.primary;
  const split = slide.media?.url && (slide.layout === 'split-left' || slide.layout === 'split-right') ? slide.layout : null;

  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <ProminentLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      {!resolved.isInverse && !split && (
        <div style={{ position: 'absolute', left: '-10cqw', bottom: '-12cqh', width: '55cqw', height: '55cqw', borderRadius: '50%', background: theme.primarySoft }} />
      )}
      {split && (mediaAspect(slide.media) ? (
        <div style={{ position: 'absolute', top: '8cqh', bottom: '8cqh', width: '40cqw', ...(split === 'split-left' ? { right: '4cqw' } : { left: '4cqw' }) }}>
          <EMedia path="media" media={slide.media} style={{ width: '100%', height: '100%' }} frameRadius="2cqw" />
        </div>
      ) : (
        <div style={{ position: 'absolute', top: 0, bottom: 0, width: '42cqw', ...(split === 'split-left' ? { right: 0 } : { left: 0 }) }}>
          <EMedia path="media" media={slide.media} style={{ width: '100%', height: '100%' }} frameRadius="0" />
        </div>
      ))}
      <div style={{ position: 'absolute', left: split === 'split-left' ? GUTTER : (align === 'center' ? '12cqw' : (split ? '48cqw' : GUTTER)), right: align === 'center' ? '12cqw' : (split === 'split-left' ? '48cqw' : (split ? GUTTER : '20cqw')), top: '40cqh', textAlign: align }}>
        <Eyebrow path="eyebrow" text={slide.eyebrow} color={eyebrowColor} />
        <EText path="title" value={slide.title} fallback={title} tag="h1" placeholder="Report title" style={{ ...TYPE.displaySm, color: resolved.text, margin: 0, marginBottom: '2.5cqh' }} />
        <EText path="subtitle" value={slide.subtitle} placeholder="Add a subtitle" style={{ ...TYPE.lead, color: resolved.textMuted, maxWidth: align === 'center' ? '76cqw' : '60cqw', margin: align === 'center' ? '0 auto' : 0 }} />
      </div>
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

// ─── 2. Section ─────────────────────────────────────────────────────────────

function SectionSlide({ slide, theme, index }: { slide: any; theme: Theme; index: number }) {
  const edit = useEdit();
  if (slide.variant === 'number') return <SectionNumberSlide slide={slide} theme={theme} index={index} />;
  if (slide.variant === 'photo') return <SectionPhotoSlide slide={slide} theme={theme} index={index} />;
  if (slide.variant === 'split') return <SectionSplitSlide slide={slide} theme={theme} index={index} />;
  if (slide.variant === 'side') return <SectionSideSlide slide={slide} theme={theme} index={index} />;
  const resolved = resolveTheme(theme, slide.theme ?? 'brand');
  const eyebrowColor = resolved.isInverse ? 'rgba(255,255,255,0.7)' : theme.primary;
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <div style={{ position: 'absolute', left: GUTTER, right: GUTTER, top: '32cqh' }}>
        {slide.eyebrow || edit.editable ? (
          <EText path="eyebrow" value={slide.eyebrow} placeholder="01" style={{ ...TYPE.statistic, color: resolved.isInverse ? 'rgba(255,255,255,0.5)' : theme.primary, marginBottom: '2cqh', lineHeight: 0.9 }} />
        ) : (
          <div style={{ ...TYPE.caption, color: eyebrowColor, marginBottom: '1.2cqh' }}>{`Section ${String(index + 1).padStart(2, '0')}`}</div>
        )}
        <EText path="title" value={slide.title} tag="h1" placeholder="Section name" style={{ ...TYPE.display, color: resolved.text, margin: 0, maxWidth: '80cqw' }} />
        <EText path="subtitle" value={slide.subtitle} placeholder="Add a subtitle" style={{ ...TYPE.lead, color: resolved.textMuted, marginTop: '3cqh', maxWidth: '70cqw' }} />
      </div>
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

// ─── 2b. Shiny covers, section covers and quotes ────────────────────────────

/** The Beatly wordmark at any size (the corner watermark is BeatlyMark). */
function BigBeatlyMark({ color, style }: { color: string; style?: CSSProperties }) {
  return (
    <svg viewBox={BEATLY_MARK_VIEWBOX} aria-hidden="true" style={{ display: 'block', height: 'auto', color, ...style }}>
      {BEATLY_MARK_PATHS.map((d, i) => <path key={i} fill="currentColor" d={d} />)}
    </svg>
  );
}
/** Full-bleed background media with a gradient so white copy stays readable. Picks any media; always cropped to the page. */
function FullBleed({ path, media, strength = 0.72 }: { path: string; media: any; strength?: number }) {
  const edit = useEdit();
  const m = media && (media.url || media.posterUrl) ? { ...media, aspect: '16:9', frame: undefined } : media;
  return (
    <>
      <div style={{ position: 'absolute', inset: 0, background: edit.editable && !(m?.url || m?.posterUrl) ? 'transparent' : '#111' }}>
        <EMedia path={path} media={m} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} frameRadius="0" placeholder="Add a background photo or video" />
      </div>
      {(m?.url || m?.posterUrl) && <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: `linear-gradient(to top, rgba(0,0,0,${strength}) 0%, rgba(0,0,0,${strength * 0.35}) 50%, rgba(0,0,0,0.05) 100%)` }} />}
    </>
  );
}
const WHITE = { text: '#fff', muted: 'rgba(255,255,255,0.78)' };
const beatlyColor = (inverse: boolean) => (inverse ? '#fff' : '#be43ff');

/** Cover · co-branded lockup: brand logo × Beatly, big and centred. */
function CoverCobrandSlide({ slide, theme, title }: { slide: any; theme: Theme; title: string }) {
  const resolved = resolveTheme(theme, slide.theme ?? 'light');
  const eyebrowColor = resolved.isInverse ? 'rgba(255,255,255,0.85)' : theme.primary;
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      {!resolved.isInverse && <div style={{ position: 'absolute', right: '-14cqw', top: '-30cqh', width: '58cqw', height: '58cqw', borderRadius: '50%', background: theme.primarySoft }} />}
      {!resolved.isInverse && <div style={{ position: 'absolute', left: '-18cqw', bottom: '-46cqh', width: '46cqw', height: '46cqw', borderRadius: '50%', background: theme.primarySoft, opacity: 0.7 }} />}
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: `0 ${GUTTER}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '3cqw', marginBottom: '7cqh' }}>
          {resolved.logoUrl && <LogoImg inverse={resolved.isInverse} src={resolved.logoUrl} style={{ maxHeight: '11cqh', maxWidth: '24cqw', objectFit: 'contain' }} />}
          {resolved.logoUrl && <span style={{ fontSize: '4cqw', fontWeight: 300, color: resolved.textMuted, lineHeight: 1 }}>×</span>}
          <BigBeatlyMark color={beatlyColor(resolved.isInverse)} style={{ width: '22cqw' }} />
        </div>
        <Eyebrow path="eyebrow" text={slide.eyebrow} color={eyebrowColor} />
        <EText path="title" value={slide.title} fallback={title} tag="h1" placeholder="Report title" style={{ ...TYPE.display, color: resolved.text, margin: 0, maxWidth: '80cqw' }} />
        <EText path="subtitle" value={slide.subtitle} placeholder="Add a subtitle" style={{ ...TYPE.lead, color: resolved.textMuted, marginTop: '2.5cqh', maxWidth: '64cqw' }} />
      </div>
    </div>
  );
}

/** Cover · full-bleed photo or video with the title in the lower left. */
function CoverPhotoSlide({ slide, theme, title }: { slide: any; theme: Theme; title: string }) {
  const resolved = resolveTheme(theme, 'dark');
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <FullBleed path="media" media={slide.media} />
      {resolved.logoUrl && <LogoImg inverse src={resolved.logoUrl} style={{ position: 'absolute', top: '7cqh', left: GUTTER, maxHeight: '8cqh', maxWidth: '22cqw', objectFit: 'contain', zIndex: 2 }} />}
      <div style={{ position: 'absolute', left: GUTTER, right: '18cqw', bottom: '12cqh', zIndex: 2 }}>
        <Eyebrow path="eyebrow" text={slide.eyebrow} color={WHITE.muted} />
        <EText path="title" value={slide.title} fallback={title} tag="h1" placeholder="Report title" style={{ ...TYPE.displayLg, color: WHITE.text, margin: 0, textShadow: '0 0.4cqh 2cqh rgba(0,0,0,0.35)' }} />
        <EText path="subtitle" value={slide.subtitle} placeholder="Add a subtitle" style={{ ...TYPE.lead, color: WHITE.muted, marginTop: '2cqh', maxWidth: '60cqw' }} />
      </div>
    </div>
  );
}

/** Cover · poster: brand colour, huge title, oversized Beatly wordmark as a watermark. */
function CoverPosterSlide({ slide, theme, title }: { slide: any; theme: Theme; title: string }) {
  const resolved = resolveTheme(theme, slide.theme ?? 'brand');
  const eyebrowColor = resolved.isInverse ? 'rgba(255,255,255,0.8)' : theme.primary;
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <BigBeatlyMark color={resolved.isInverse ? '#fff' : theme.primary} style={{ position: 'absolute', right: '-6cqw', bottom: '-6cqh', width: '70cqw', opacity: resolved.isInverse ? 0.14 : 0.08, pointerEvents: 'none' }} />
      <ProminentLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <div style={{ position: 'absolute', left: GUTTER, right: '12cqw', bottom: '14cqh' }}>
        <Eyebrow path="eyebrow" text={slide.eyebrow} color={eyebrowColor} />
        <EText path="title" value={slide.title} fallback={title} tag="h1" placeholder="Report title" style={{ ...TYPE.displayLg, color: resolved.text, margin: 0, maxWidth: '78cqw' }} />
        <EText path="subtitle" value={slide.subtitle} placeholder="Add a subtitle" style={{ ...TYPE.lead, color: resolved.textMuted, marginTop: '2.5cqh', maxWidth: '60cqw' }} />
      </div>
    </div>
  );
}

/** Section · huge outlined number on the right, title bottom-left. */
function SectionNumberSlide({ slide, theme, index }: { slide: any; theme: Theme; index: number }) {
  const resolved = resolveTheme(theme, slide.theme ?? 'brand');
  const num = slide.eyebrow || String(index + 1).padStart(2, '0');
  const stroke = resolved.isInverse ? 'rgba(255,255,255,0.55)' : theme.primary;
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <div style={{ position: 'absolute', right: '4cqw', top: '-4cqh', fontSize: '44cqw', fontWeight: 800, lineHeight: 1, letterSpacing: '-0.06em', color: 'transparent', WebkitTextStroke: `0.22cqw ${stroke}`, opacity: 0.55, pointerEvents: 'none', userSelect: 'none' } as CSSProperties}>{num}</div>
      <div style={{ position: 'absolute', left: GUTTER, right: '30cqw', bottom: '14cqh' }}>
        <EText path="eyebrow" value={slide.eyebrow} placeholder={String(index + 1).padStart(2, '0')} style={{ ...TYPE.caption, color: resolved.isInverse ? 'rgba(255,255,255,0.7)' : theme.primary, marginBottom: '1.4cqh' }} />
        <EText path="title" value={slide.title} tag="h1" placeholder="Section name" style={{ ...TYPE.displayLg, color: resolved.text, margin: 0 }} />
        <EText path="subtitle" value={slide.subtitle} placeholder="Add a subtitle" style={{ ...TYPE.lead, color: resolved.textMuted, marginTop: '2.5cqh', maxWidth: '56cqw' }} />
      </div>
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

/** Section · full-bleed photo with the section name bottom-left. */
function SectionPhotoSlide({ slide, theme, index }: { slide: any; theme: Theme; index: number }) {
  const resolved = resolveTheme(theme, 'dark');
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <FullBleed path="media" media={slide.media} strength={0.78} />
      {resolved.logoUrl && <LogoImg inverse src={resolved.logoUrl} style={{ position: 'absolute', top: '5cqh', left: '4cqw', maxHeight: '5cqh', maxWidth: '14cqw', objectFit: 'contain', zIndex: 2 }} />}
      <div style={{ position: 'absolute', left: GUTTER, right: '20cqw', bottom: '12cqh', zIndex: 2 }}>
        <EText path="eyebrow" value={slide.eyebrow} placeholder={`SECTION ${String(index + 1).padStart(2, '0')}`} style={{ ...TYPE.statistic, fontSize: '6cqw', color: 'rgba(255,255,255,0.6)', marginBottom: '1.5cqh', lineHeight: 0.9 }} />
        <EText path="title" value={slide.title} tag="h1" placeholder="Section name" style={{ ...TYPE.display, color: WHITE.text, margin: 0, textShadow: '0 0.4cqh 2cqh rgba(0,0,0,0.35)' }} />
        <EText path="subtitle" value={slide.subtitle} placeholder="Add a subtitle" style={{ ...TYPE.lead, color: WHITE.muted, marginTop: '2cqh', maxWidth: '60cqw' }} />
      </div>
    </div>
  );
}

/** Section · brand-colour block with the number on the left, title on the right. */
function SectionSplitSlide({ slide, theme, index }: { slide: any; theme: Theme; index: number }) {
  const resolved = resolveTheme(theme, 'light');
  const num = slide.eyebrow || String(index + 1).padStart(2, '0');
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: '40cqw', background: theme.primary }}>
        {resolved.logoUrl && <LogoImg inverse src={resolved.logoUrl} style={{ position: 'absolute', top: '7cqh', left: GUTTER, maxHeight: '7cqh', maxWidth: '20cqw', objectFit: 'contain' }} />}
        <div style={{ position: 'absolute', left: GUTTER, bottom: '12cqh', right: '3cqw' }}>
          <EText path="eyebrow" value={slide.eyebrow} placeholder={num} style={{ ...TYPE.statistic, fontSize: '16cqw', color: '#fff', lineHeight: 0.9 }} />
        </div>
      </div>
      <div style={{ position: 'absolute', left: '47cqw', right: GUTTER, top: '50%', transform: 'translateY(-50%)' }}>
        <div style={{ ...TYPE.caption, color: theme.primary, marginBottom: '1.4cqh' }}>{`Section ${num}`}</div>
        <EText path="title" value={slide.title} tag="h1" placeholder="Section name" style={{ ...TYPE.display, color: resolved.text, margin: 0 }} />
        <EText path="subtitle" value={slide.subtitle} placeholder="Add a subtitle" style={{ ...TYPE.lead, color: resolved.textMuted, marginTop: '2.5cqh' }} />
      </div>
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

/** Round avatar next to an attribution — for quotes from a creator. */
function QuoteBy({ slide, color, muted, size = '3.6cqw', center }: { slide: any; color: string; muted: string; size?: string; center?: boolean }) {
  const edit = useEdit();
  const avatar = slide.avatar?.url || slide.avatar?.posterUrl ? { url: slide.avatar.posterUrl || slide.avatar.url, kind: 'image', aspect: '1:1' } : slide.avatarUrl ? { url: slide.avatarUrl, kind: 'image', aspect: '1:1' } : null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '1cqw', justifyContent: center ? 'center' : 'flex-start' }}>
      {(avatar || edit.editable) && (
        <div style={{ width: size, height: size, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, background: 'rgba(127,127,127,0.2)' }}>
          <EMedia path="avatar" media={avatar} placeholder="Photo" frameRadius="50%" style={{ width: '100%', height: '100%' }} />
        </div>
      )}
      <div style={{ textAlign: center ? 'center' : 'left' }}>
        <EText path="attribution" value={slide.attribution} placeholder="Name, role" style={{ ...TYPE.bodyLarge, fontWeight: 600, color }} />
        <EText path="handle" value={slide.handle} placeholder={edit.editable ? '@handle · platform' : undefined} style={{ ...TYPE.bodySmall, color: muted }} />
      </div>
    </div>
  );
}

/** Quote · big centred quote with oversized marks. */
function QuoteBigSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const resolved = resolveTheme(theme, slide.theme ?? 'soft');
  const markColor = resolved.isInverse ? 'rgba(255,255,255,0.14)' : theme.primaryTint;
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <div style={{ position: 'absolute', left: '6cqw', top: '4cqh', fontFamily: theme.fontSerif, fontSize: '34cqw', lineHeight: 0.7, color: markColor, fontWeight: 700, pointerEvents: 'none' }}>“</div>
      <div style={{ position: 'absolute', right: '6cqw', bottom: '-18cqh', fontFamily: theme.fontSerif, fontSize: '34cqw', lineHeight: 0.7, color: markColor, fontWeight: 700, pointerEvents: 'none' }}>”</div>
      <div style={{ position: 'absolute', inset: 0, padding: `0 ${GUTTER}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
        <EText path="quote" value={slide.quote ?? slide.body} tag="blockquote" placeholder="Add the quote" multiline style={{ ...TYPE.displaySm, fontFamily: theme.fontSerif, fontWeight: 500, fontStyle: 'italic', color: resolved.text, margin: 0, maxWidth: '78cqw' }} />
        <div style={{ marginTop: '5cqh' }}><QuoteBy slide={slide} color={resolved.text} muted={resolved.textMuted} center /></div>
      </div>
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

/** Quote · over a full-bleed photo. */
function QuotePhotoSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const resolved = resolveTheme(theme, 'dark');
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <FullBleed path="media" media={slide.media} strength={0.8} />
      {resolved.logoUrl && <LogoImg inverse src={resolved.logoUrl} style={{ position: 'absolute', top: '5cqh', left: '4cqw', maxHeight: '5cqh', maxWidth: '14cqw', objectFit: 'contain', zIndex: 2 }} />}
      <div style={{ position: 'absolute', left: GUTTER, right: '22cqw', bottom: '12cqh', zIndex: 2 }}>
        <div style={{ fontFamily: theme.fontSerif, fontSize: '9cqw', lineHeight: 0.6, color: 'rgba(255,255,255,0.5)', marginBottom: '2cqh' }}>“</div>
        <EText path="quote" value={slide.quote ?? slide.body} tag="blockquote" placeholder="Add the quote" multiline style={{ ...TYPE.headline, fontFamily: theme.fontSerif, fontWeight: 500, fontStyle: 'italic', color: WHITE.text, margin: 0, textShadow: '0 0.4cqh 2cqh rgba(0,0,0,0.35)' }} />
        <div style={{ marginTop: '3cqh' }}><QuoteBy slide={slide} color={WHITE.text} muted={WHITE.muted} /></div>
      </div>
    </div>
  );
}

/** Quote · in a card with the creator's photo and handle. */
function QuoteCardSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const resolved = resolveTheme(theme, slide.theme ?? 'light');
  const cardBg = resolved.isInverse ? 'rgba(255,255,255,0.08)' : '#fff';
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      {!resolved.isInverse && <div style={{ position: 'absolute', inset: 0, background: theme.primarySoft }} />}
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <div style={{ position: 'absolute', left: '14cqw', right: '14cqw', top: '50%', transform: 'translateY(-50%)', padding: '5cqh 4cqw 4.5cqh', borderRadius: '1.6cqw', background: cardBg, boxShadow: resolved.isInverse ? 'none' : '0 2cqh 6cqh rgba(16,24,40,0.10)' }}>
        <div style={{ fontFamily: theme.fontSerif, fontSize: '8cqw', lineHeight: 0.6, color: theme.primary, marginBottom: '2.5cqh' }}>“</div>
        <EText path="quote" value={slide.quote ?? slide.body} tag="blockquote" placeholder="Add the quote" multiline style={{ ...TYPE.headline, fontFamily: theme.fontSerif, fontWeight: 500, fontStyle: 'italic', color: resolved.text, margin: 0 }} />
        <div style={{ marginTop: '3.5cqh', paddingTop: '2.5cqh', borderTop: `1px solid ${resolved.isInverse ? 'rgba(255,255,255,0.18)' : theme.border}` }}><QuoteBy slide={slide} color={resolved.text} muted={resolved.textMuted} size="4.2cqw" /></div>
      </div>
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

// ─── 3. Headline ────────────────────────────────────────────────────────────

function HeadlineSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const resolved = resolveTheme(theme, slide.theme ?? 'soft');
  const align: 'left' | 'center' = (slide.layout ?? 'center') === 'left' ? 'left' : 'center';
  const eyebrowColor = resolved.isInverse ? 'rgba(255,255,255,0.85)' : theme.primary;
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <div style={{ position: 'absolute', inset: 0, padding: `${TOP_PAD} ${GUTTER}`, display: 'flex', flexDirection: 'column', alignItems: align === 'center' ? 'center' : 'flex-start', justifyContent: 'center', textAlign: align }}>
        <Eyebrow path="eyebrow" text={slide.eyebrow} color={eyebrowColor} />
        <EText path="title" value={slide.title} tag="h1" placeholder="One big idea" multiline style={{ ...TYPE.displayLg, color: resolved.text, margin: 0, maxWidth: '84cqw' }} />
        <EText path="kicker" value={slide.kicker} placeholder="Add a supporting line" style={{ ...TYPE.bodyLarge, color: resolved.textMuted, marginTop: '4cqh', maxWidth: '70cqw' }} />
      </div>
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

// ─── 4. Big stat ────────────────────────────────────────────────────────────

function BigStatSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const resolved = resolveTheme(theme, slide.theme ?? 'light');
  const eyebrowColor = resolved.isInverse ? 'rgba(255,255,255,0.85)' : theme.primary;
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <div style={{ position: 'absolute', inset: 0, padding: `${TOP_PAD} ${GUTTER}`, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start' }}>
        <Eyebrow path="eyebrow" text={slide.eyebrow} color={eyebrowColor} />
        <EText path="value" value={slide.value} fallback="—" placeholder="3.1M" style={{ ...TYPE.statistic, color: theme.primary, marginBottom: '2.5cqh' }} />
        <EText path="label" value={slide.label} placeholder="What it measures" style={{ ...TYPE.title, color: resolved.text, maxWidth: '70cqw' }} />
        <EText path="kicker" value={slide.kicker} placeholder="Add context" style={{ ...TYPE.bodyLarge, color: resolved.textMuted, marginTop: '3cqh', maxWidth: '60cqw' }} />
        <EText path="source" value={slide.source} placeholder="Add a source" style={{ ...TYPE.caption, color: resolved.textMuted, marginTop: '5cqh', opacity: 0.8 }} />
      </div>
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

// ─── 5. Metrics ─────────────────────────────────────────────────────────────

function Ring({ pct, color, track }: { pct: number; color: string; track: string }) {
  const r = 42, c = 2 * Math.PI * r;
  const p = Math.max(0, Math.min(100, pct));
  return (
    <svg viewBox="0 0 100 100" style={{ width: '9cqw', height: '9cqw', display: 'block', marginBottom: '1.2cqh' }}>
      <circle cx="50" cy="50" r={r} fill="none" stroke={track} strokeWidth="9" />
      <circle cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth="9" strokeLinecap="round" strokeDasharray={`${(p / 100) * c} ${c}`} transform="rotate(-90 50 50)" />
      <text x="50" y="55" textAnchor="middle" fontSize="20" fontWeight="800" fill={color}>{Math.round(p)}%</text>
    </svg>
  );
}

function MetricsSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const edit = useEdit();
  if (slide.variant === 'glance') return <MetricsGlanceSlide slide={slide} theme={theme} />;
  if (slide.variant === 'channel') return <MetricsChannelSlide slide={slide} theme={theme} />;
  const resolved = resolveTheme(theme, slide.theme ?? 'light');
  const metrics: any[] = (slide.metrics ?? []).slice(0, 4);
  const variant: string = slide.variant ?? 'numbers';
  const cols = Math.max(metrics.length + 0, 1);
  const eyebrowColor = resolved.isInverse ? 'rgba(255,255,255,0.85)' : theme.primary;
  const track = resolved.isInverse ? 'rgba(255,255,255,0.18)' : theme.surface;
  const valueColor = resolved.isInverse ? resolved.text : theme.primary;
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <SlideHeader slide={slide} eyebrowColor={eyebrowColor} resolved={resolved} />
      <div style={{ position: 'absolute', left: GUTTER, right: GUTTER, bottom: '11cqh', top: '38cqh', display: 'grid', gap: '2.4cqw', gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
        {metrics.map((m, i) => {
          const dRaw = m.delta != null ? String(m.delta) : '';
          const isUp = dRaw && (dRaw.startsWith('+') || (!dRaw.startsWith('-') && parseFloat(dRaw) > 0));
          const pct = Number(m.percent);
          return (
            <ECard key={i} listPath="metrics" index={i} style={{ borderTop: variant === 'numbers' ? `2px solid ${resolved.isInverse ? 'rgba(255,255,255,0.25)' : theme.borderStrong}` : 'none', paddingTop: variant === 'numbers' ? '2.5cqh' : 0, display: 'flex', flexDirection: 'column' }}>
              {variant === 'circles' && <Ring pct={Number.isFinite(pct) ? pct : 0} color={valueColor} track={track} />}
              <EText path={`metrics.${i}.label`} value={m.label} placeholder="Label" style={{ ...TYPE.caption, color: resolved.textMuted, marginBottom: '1.2cqh' }} />
              <EText path={`metrics.${i}.value`} value={m.value} fallback="—" placeholder="0" style={{ ...(variant === 'circles' ? TYPE.title : TYPE.display), color: valueColor, marginBottom: '0.8cqh' }} />
              {variant === 'bars' && (
                <div style={{ height: '1.6cqh', borderRadius: 999, background: track, overflow: 'hidden', margin: '0.6cqh 0 1cqh' }}>
                  <div style={{ width: `${Math.max(0, Math.min(100, Number.isFinite(pct) ? pct : 0))}%`, height: '100%', background: valueColor, borderRadius: 999 }} />
                </div>
              )}
              {variant !== 'numbers' && edit.editable && (
                <div style={{ display: 'flex', gap: '0.4cqw', ...TYPE.bodySmall, color: resolved.textMuted }}>
                  <EText path={`metrics.${i}.percent`} value={m.percent} placeholder="0" tag="span" style={{ display: 'inline' }} /><span>% of target</span>
                </div>
              )}
              {edit.editable ? (
                <EText path={`metrics.${i}.delta`} value={m.delta} placeholder="+0% delta" style={{ ...TYPE.bodySmall, fontWeight: 600, color: isUp ? '#16A34A' : dRaw ? '#DC2626' : resolved.textMuted }} />
              ) : dRaw ? (
                <div style={{ ...TYPE.bodySmall, fontWeight: 600, color: isUp ? '#16A34A' : '#DC2626' }}>{isUp ? '↑' : '↓'} {dRaw.replace(/^[+-]/, '')}</div>
              ) : null}
              <EText path={`metrics.${i}.caption`} value={m.caption} placeholder="Add caption" style={{ ...TYPE.bodySmall, color: resolved.textMuted, marginTop: '1cqh', opacity: 0.85 }} />
            </ECard>
          );
        })}
        <EAddCard listPath="metrics" count={metrics.length} max={4} label="Add metric" blank={() => ({ value: '—', label: 'Metric', percent: 50 })} />
      </div>
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

// ─── 6. Comparison ──────────────────────────────────────────────────────────

/** Bullet list inside a card (columns, comparison): "**Lead.** detail" renders with a bold lead; editable per bullet. */
function SubBullets({ path, bullets, resolved, small }: { path: string; bullets: unknown; resolved: ResolvedTheme; small?: boolean }) {
  const edit = useEdit();
  // Templates ship empty bullets as placeholders: keep them editable, hide them when viewing.
  const list: string[] = (Array.isArray(bullets) ? bullets.map(String) : []).filter((b) => edit.editable || b.trim());
  if (!list.length) return null;
  return (
    <ul style={{ paddingLeft: '1.4cqw', margin: '1.2cqh 0 0', ...(small ? TYPE.bodySmall : TYPE.body), color: resolved.textMuted }}>
      {list.map((b, i) => {
        const lead = !edit.editable ? splitLead(b) : null;
        return (
          <li key={i} style={{ marginBottom: small ? '0.8cqh' : '1cqh' }}>
            {lead ? (<><strong style={{ color: resolved.text, fontWeight: 700 }}>{lead[0]}</strong>{lead[1] ? <span style={{ marginLeft: '0.5em' }}>{richNodes(lead[1])}</span> : null}</>) : (
              <EText path={`${path}.${i}`} value={b} placeholder="Bullet" tag="span" style={{ display: 'inline' }} />
            )}
          </li>
        );
      })}
    </ul>
  );
}

function ComparisonSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const resolved = resolveTheme(theme, slide.theme ?? 'light');
  const items: any[] = (slide.items ?? []).slice(0, 2);
  const eyebrowColor = resolved.isInverse ? 'rgba(255,255,255,0.85)' : theme.primary;
  // Dense = a side has a paragraph plus bullets (or long text): smaller value/type and tighter padding so
  // both cards stay inside the page; the cards can never grow past the body area either way.
  const dense = items.some((it) => (Array.isArray(it.bullets) && it.bullets.length >= 2 && String(it.body || '').length > 60) || String(it.body || '').length > 220 || (Array.isArray(it.bullets) && it.bullets.length >= 4));
  const hasHeader = !!(slide.title || slide.eyebrow) || items.length === 0;
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <SlideHeader slide={slide} eyebrowColor={eyebrowColor} resolved={resolved} />
      <div style={{ position: 'absolute', left: GUTTER, right: GUTTER, bottom: '11cqh', top: hasHeader ? (dense ? '33cqh' : '38cqh') : '14cqh', display: 'grid', gap: '3cqw', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gridTemplateRows: 'minmax(0, 1fr)' }}>
        {items.map((item, i) => {
          const isHero = !!item.isHero;
          return (
            <ECard key={i} listPath="items" index={i} style={{ background: isHero ? theme.primarySoft : 'transparent', borderRadius: '1cqw', border: isHero ? `1.5px solid ${theme.primaryTint}` : `1px solid ${resolved.isInverse ? 'rgba(255,255,255,0.18)' : theme.border}`, padding: dense ? '2.6cqh 2.4cqw' : '4cqh 3.2cqw', display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
              <EText path={`items.${i}.label`} value={item.label} fallback={`Option ${i + 1}`} placeholder="Label" style={{ ...TYPE.caption, color: isHero ? theme.primary : resolved.textMuted, marginBottom: dense ? '1.2cqh' : '2cqh' }} />
              <EText path={`items.${i}.value`} value={item.value} placeholder="Value" style={{ ...(dense ? { ...TYPE.displaySm, fontSize: '3cqw' } : TYPE.displaySm), color: isHero ? theme.primary : resolved.text, marginBottom: dense ? '1.4cqh' : '2.5cqh' }} />
              <EText path={`items.${i}.body`} value={item.body} placeholder="Add a description" multiline style={{ ...(dense ? TYPE.bodySmall : TYPE.body), color: resolved.textMuted }} />
              <SubBullets path={`items.${i}.bullets`} bullets={item.bullets} resolved={resolved} small={dense} />
            </ECard>
          );
        })}
        <EAddCard listPath="items" count={items.length} max={2} label="Add side" blank={() => ({ label: 'Option', value: '', body: '' })} />
      </div>
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

// ─── 7. Timeline ────────────────────────────────────────────────────────────

function TimelineSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const resolved = resolveTheme(theme, slide.theme ?? 'light');
  const edit = useEdit();
  const steps: any[] = (slide.steps ?? []).slice(0, 6);
  const n = Math.max(steps.length, 1);
  const eyebrowColor = resolved.isInverse ? 'rgba(255,255,255,0.85)' : theme.primary;
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <SlideHeader slide={slide} eyebrowColor={eyebrowColor} resolved={resolved} />
      <div style={{ position: 'absolute', left: GUTTER, right: GUTTER, bottom: '11cqh', top: '40cqh' }}>
        <div style={{ position: 'absolute', left: 0, right: 0, top: '0.8cqw', height: 2, background: resolved.isInverse ? 'rgba(255,255,255,0.18)' : theme.border }} />
        <div style={{ position: 'relative', display: 'grid', gap: '2cqw', gridTemplateColumns: `repeat(${n + 0}, 1fr)` }}>
          {steps.map((s, i) => (
            <ECard key={i} listPath="steps" index={i} style={{ paddingTop: '6cqh' }}>
              {/* Small marker on the track — it must never reach the label below it. */}
              <div style={{ position: 'absolute', top: 0, left: 0, width: '1.6cqw', height: '1.6cqw', borderRadius: '50%', background: theme.primary, boxShadow: `0 0 0 0.45cqw ${theme.primarySoft}` }} />
              <EText path={`steps.${i}.label`} value={s.label} fallback={`STEP ${String(i + 1).padStart(2, '0')}`} placeholder="Q1" style={{ ...TYPE.caption, color: theme.primary, marginBottom: '1cqh' }} />
              <EText path={`steps.${i}.title`} value={s.title} placeholder="Step title" style={{ ...TYPE.title, fontSize: '1.7cqw', color: resolved.text, marginBottom: '1cqh', maxWidth: '90%' }} />
              <EText path={`steps.${i}.body`} value={s.body} placeholder="Add detail" multiline style={{ ...TYPE.bodySmall, color: resolved.textMuted, maxWidth: '90%' }} />
            </ECard>
          ))}
          <EAddCard listPath="steps" count={steps.length} max={6} label="Add step" blank={() => ({ label: 'Next', title: 'Step', body: '' })} style={{ marginTop: '6cqh', minHeight: '8cqh' }} />
        </div>
      </div>
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

// ─── 8. Columns ─────────────────────────────────────────────────────────────

function ColumnsSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const edit = useEdit();
  if (slide.variant === 'tiers') return <ColumnsTiersSlide slide={slide} theme={theme} />;
  if (slide.variant === 'stat') return <ColumnsStatSlide slide={slide} theme={theme} />;
  const resolved = resolveTheme(theme, slide.theme ?? 'light');
  const columns: any[] = (slide.columns ?? []).slice(0, 6);
  const cols = Math.max(columns.length + 0, 1);
  const withImages = slide.variant === 'images' || columns.some((c) => c.media?.url);
  const tight = columns.length >= 5 || slide.variant === 'text'; // five or six across, or a text-heavy page: smaller type, same rhythm
  const textOnly = slide.variant === 'text'; // dense text columns: no rules, body starts higher
  const eyebrowColor = resolved.isInverse ? 'rgba(255,255,255,0.85)' : theme.primary;
  const hasHeader = !!(slide.title || slide.eyebrow) || edit.editable;
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <SlideHeader slide={slide} eyebrowColor={eyebrowColor} resolved={resolved} />
      <div style={{ position: 'absolute', left: GUTTER, right: GUTTER, bottom: calloutInset(slide), top: hasHeader ? (withImages || slide.callout || textOnly ? '30cqh' : '38cqh') : '14cqh', display: 'grid', gap: tight ? '1.6cqw' : '2.4cqw', gridTemplateColumns: `repeat(${cols}, 1fr)${slide.aside ? ' 24%' : ''}` }}>
        {columns.map((c, i) => (
          <ECard key={i} listPath="columns" index={i} style={{ borderTop: withImages || textOnly ? 'none' : `2px solid ${resolved.isInverse ? 'rgba(255,255,255,0.25)' : theme.borderStrong}`, paddingTop: withImages || textOnly ? 0 : '2.5cqh', display: 'flex', flexDirection: 'column' }}>
            {withImages && <EMedia path={`columns.${i}.media`} media={c.media} style={{ height: tight ? '20cqh' : '26cqh', marginBottom: '2cqh', flexShrink: 0 }} placeholder="Add image" />}
            <EText path={`columns.${i}.eyebrow`} value={c.eyebrow} placeholder="01" style={{ ...TYPE.caption, color: theme.primary, marginBottom: '1.2cqh' }} />
            <EText path={`columns.${i}.title`} value={c.title} placeholder="Column title" style={{ ...TYPE.title, fontSize: tight ? '1.5cqw' : '1.9cqw', color: resolved.text, marginBottom: '1.5cqh' }} />
            <EText path={`columns.${i}.body`} value={c.body} placeholder="Add text" multiline style={{ ...(tight ? TYPE.bodySmall : TYPE.body), color: resolved.textMuted }} />
            <SubBullets path={`columns.${i}.bullets`} bullets={c.bullets} resolved={resolved} small />
          </ECard>
        ))}
        <EAddCard listPath="columns" count={columns.length} max={6} label="Add column" blank={() => ({ eyebrow: '', title: 'Pillar', body: '' })} style={slide.aside ? { right: 'calc(24% + 3.4cqw)' } : undefined} />
        {slide.aside && <AsidePanel slide={slide} resolved={resolved} theme={theme} />}
      </div>
      <CalloutBox slide={slide} resolved={resolved} theme={theme} />
      <Footnote slide={slide} resolved={resolved} />
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

// ─── 9. Quote ───────────────────────────────────────────────────────────────

function QuoteSlide({ slide, theme }: { slide: any; theme: Theme }) {
  if (slide.variant === 'big') return <QuoteBigSlide slide={slide} theme={theme} />;
  if (slide.variant === 'photo') return <QuotePhotoSlide slide={slide} theme={theme} />;
  if (slide.variant === 'card') return <QuoteCardSlide slide={slide} theme={theme} />;
  const resolved = resolveTheme(theme, slide.theme ?? 'brand');
  return (
    <div style={baseStyle(resolved, theme.fontSerif)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <div style={{ position: 'absolute', left: '5cqw', top: '12cqh', fontFamily: theme.fontSerif, fontSize: '20cqw', lineHeight: 0.8, color: resolved.isInverse ? 'rgba(255,255,255,0.18)' : theme.primaryTint, fontWeight: 700 }}>“</div>
      <div style={{ position: 'absolute', left: '12cqw', right: '12cqw', top: '34cqh' }}>
        <EText path="quote" value={slide.quote ?? slide.body} tag="blockquote" placeholder="Add the quote" multiline style={{ ...TYPE.headline, fontFamily: theme.fontSerif, fontWeight: 500, fontStyle: 'italic', color: resolved.text, margin: 0 }} />
        <div style={{ ...TYPE.caption, color: resolved.textMuted, marginTop: '4cqh', display: 'flex', gap: '0.5cqw' }}>
          <span>—</span>
          <EText path="attribution" value={slide.attribution} placeholder="Name, role" tag="span" style={{ ...TYPE.caption, color: resolved.textMuted }} />
        </div>
      </div>
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

// ─── 10. Chart ──────────────────────────────────────────────────────────────

function ChartSlide({ slide, theme }: { slide: any; theme: Theme }) {
  if (slide.variant === 'overview') return <ChartOverviewSlide slide={slide} theme={theme} />;
  const resolved = resolveTheme(theme, slide.theme ?? 'light');
  const chart = slide.chart ?? {};
  const eyebrowColor = resolved.isInverse ? 'rgba(255,255,255,0.85)' : theme.primary;
  const svg = chartSvg(chart, { primary: theme.primary, accent: theme.accent, text: resolved.text, textMuted: resolved.textMuted, grid: resolved.isInverse ? 'rgba(255,255,255,0.15)' : theme.border, ...(theme.palette?.length ? { palette: theme.palette } : {}) });
  // Side panel on any chart style: 'text-left' / 'text-right' (body, bullets, table) or 'image-left' / 'image-right' (one photo).
  const panel: string | null = ['text-left', 'text-right', 'image-left', 'image-right'].includes(slide.layout) ? slide.layout : null;
  const panelLeft = !!panel && panel.endsWith('left'); const panelImage = !!panel && panel.startsWith('image');
  // Columns in cqw: [panel 28] [chart]. The data editor lives in the right-hand panel (ChartDataEditor), never on the stage,
  // so what the editor shows is what the presentation shows.
  const chartBox = !panel ? { left: GUTTER, right: GUTTER } : panelLeft ? { left: '38.5cqw', right: GUTTER } : { left: GUTTER, right: '38.5cqw' };
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <SlideHeader slide={slide} eyebrowColor={eyebrowColor} resolved={resolved} />
      <div style={{ position: 'absolute', ...chartBox, bottom: '9cqh', top: '36cqh' }}>
        <div style={{ width: '100%', height: '100%', fontFamily: theme.fontDisplay }} dangerouslySetInnerHTML={{ __html: svg.replace('<svg ', '<svg style="width:100%;height:100%" ') }} />
      </div>
      {panel && (
        <div style={{ position: 'absolute', top: '36cqh', bottom: '9cqh', width: '28cqw', ...(panelLeft ? { left: GUTTER } : { right: GUTTER }) }}>
          {panelImage
            ? <EMedia path="media" media={slide.media?.url ? { ...slide.media, aspect: 'fill', frame: undefined } : slide.media} style={{ width: '100%', height: '100%' }} frameRadius="1cqw" placeholder="Add a photo" />
            : <div style={{ color: resolved.textMuted, ...TYPE.bodySmall, height: '100%', overflow: 'hidden', ...(slide.panelTone === 'panel' ? { background: resolved.isInverse ? 'rgba(255,255,255,0.08)' : theme.surface, borderRadius: '1cqw', padding: '2.2cqh 1.6cqw', boxSizing: 'border-box' } : {}) }}><ContentBody slide={slide} resolved={resolved} theme={theme} /></div>}
        </div>
      )}
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

// ─── 11. Closing ────────────────────────────────────────────────────────────

function ClosingSlide({ slide, theme }: { slide: any; theme: Theme }) {
  if (slide.variant === 'about') return <ClosingAboutSlide slide={slide} theme={theme} />;
  if (slide.variant === 'contacts') return <ClosingContactsSlide slide={slide} theme={theme} />;
  const resolved = resolveTheme(theme, slide.theme ?? 'light');
  const eyebrowColor = resolved.isInverse ? 'rgba(255,255,255,0.85)' : theme.primary;
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <div style={{ position: 'absolute', inset: 0, padding: `${TOP_PAD} ${GUTTER}`, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center' }}>
        <Eyebrow path="eyebrow" text={slide.eyebrow} color={eyebrowColor} />
        <EText path="title" value={slide.title} fallback="Thank you." tag="h1" placeholder="Thank you." style={{ ...TYPE.display, color: resolved.text, margin: 0, maxWidth: '80cqw' }} />
        <EText path="subtitle" value={slide.subtitle ?? slide.cta} placeholder="Add a closing line" style={{ ...TYPE.bodyLarge, color: resolved.textMuted, marginTop: '3cqh', maxWidth: '60cqw' }} />
        <EText path="contact" value={slide.contact} placeholder="Add contact" style={{ ...TYPE.body, fontWeight: 600, color: theme.primary, marginTop: '5cqh', padding: '1.5cqh 2.4cqw', border: `1.5px solid ${theme.primaryTint}`, borderRadius: '999px', display: 'inline-block' }} />
      </div>
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

// ─── 12. Content ────────────────────────────────────────────────────────────

function ContentBody({ slide, resolved, theme }: { slide: any; resolved: ResolvedTheme; theme: Theme }) {
  const edit = useEdit();
  const bullets: string[] = Array.isArray(slide.bullets) ? slide.bullets : [];
  return (
    <>
      {(bullets.length > 0 || edit.editable) && (
        <ul style={{ paddingLeft: '1.8cqw', margin: 0 }}>
          {bullets.map((b: string, i: number) => {
            const lead = !edit.editable ? splitLead(String(b)) : null;
            if (lead) {
              return (
                <li key={i} style={{ marginBottom: '1.6cqh' }}>
                  <strong style={{ color: resolved.text, fontWeight: 700 }}>{lead[0]}</strong>
                  {lead[1] ? <span style={{ marginLeft: '0.6em' }}>{richNodes(lead[1])}</span> : null}
                </li>
              );
            }
            return (
              <li key={i} style={{ marginBottom: '1.6cqh', position: 'relative' }}>
                <EText path={`bullets.${i}`} value={b} placeholder="Bullet" tag="span" style={{ display: 'inline' }} />
              </li>
            );
          })}
          {edit.editable && (
            <li data-eadd="" onClick={() => edit.update((s) => ({ ...s, bullets: [...(s.bullets || []), 'New point'] }))} style={{ cursor: 'pointer', listStyle: 'none', marginLeft: '-1.8cqw', color: '#7C3AED', ...TYPE.bodySmall, fontWeight: 600 }}>+ Add bullet</li>
          )}
        </ul>
      )}
      <EText path="body" value={slide.body} placeholder="Add a paragraph" multiline tag="p" style={{ marginTop: 0, maxWidth: '76cqw' }} />
      {slide.table?.headers && Array.isArray(slide.table?.rows) && (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '2cqh', ...TYPE.bodySmall }}>
          <thead>
            <tr>
              {slide.table.headers.map((h: string, i: number) => (
                <th key={i} style={{ ...TYPE.caption, color: resolved.textMuted, textAlign: 'left', padding: '1.4cqh 0', borderBottom: `1.5px solid ${theme.borderStrong}` }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {slide.table.rows.map((row: string[], ri: number) => (
              <tr key={ri}>
                {row.map((c: any, ci: number) => (
                  <td key={ci} style={{ padding: '1.4cqh 0', borderBottom: `1px solid ${theme.border}`, color: ci === 0 ? resolved.text : resolved.textMuted, fontWeight: ci === 0 ? 600 : 400 }}>
                    {c && typeof c === 'object' ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.7cqw' }}><Avatar url={c.avatarUrl} name={c.text} size="2.2cqw" color={theme.primary} />{c.text}</span> : c}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function CtaButton({ slide, theme }: { slide: any; theme: Theme }) {
  const edit = useEdit();
  if (!slide.cta && !edit.editable) return null;
  return (
    <div style={{ marginTop: '3cqh', display: 'flex', alignItems: 'center', gap: '1cqw' }}>
      <EText path="cta" value={slide.cta} placeholder="+ Add button" style={{ ...TYPE.body, fontWeight: 600, color: '#fff', background: theme.primary, padding: '1.4cqh 2.4cqw', borderRadius: 999, display: 'inline-block' }} />
      {edit.editable && slide.cta && <EText path="ctaHref" value={slide.ctaHref} placeholder="https://link" style={{ ...TYPE.bodySmall, color: theme.textSubtle }} />}
    </div>
  );
}

function ContentSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const resolved = resolveTheme(theme, slide.theme ?? 'light');
  const edit = useEdit();
  const eyebrowColor = resolved.isInverse ? 'rgba(255,255,255,0.85)' : theme.primary;
  const hasMedia = !!slide.media?.url;
  const layout: string = slide.layout ?? 'left';
  const wantsMedia = hasMedia || edit.editable;
  // 'image-left' / 'image-right' = text + one photo that fills a tall, near-square box (no phone frame, whatever the picker returns).
  const photo = wantsMedia && (layout === 'image-left' || layout === 'image-right') ? layout : null;
  const split = wantsMedia && (layout === 'split-left' || layout === 'split-right') ? layout : photo;
  const accent = wantsMedia && (layout === 'accent-left' || layout === 'accent-right' || layout === 'accent-top') ? layout : null;

  if (accent === 'accent-top') {
    return (
      <div style={baseStyle(resolved, theme.fontDisplay)}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '40cqh' }}>
          <EMedia path="media" media={slide.media} style={{ width: '100%', height: '100%' }} frameRadius="0" />
        </div>
        <div style={{ position: 'absolute', top: '46cqh', left: GUTTER, right: GUTTER, bottom: '9cqh' }}>
          <SlideHeader inline slide={slide} eyebrowColor={eyebrowColor} resolved={resolved} />
          <div style={{ color: resolved.textMuted, ...TYPE.body, marginTop: '2cqh' }}>
            <ContentBody slide={slide} resolved={resolved} theme={theme} />
            <CtaButton slide={slide} theme={theme} />
          </div>
        </div>
        <BrandMark theme={theme} resolved={resolved} />
      </div>
    );
  }

  if (split || accent) {
    const mediaLeft = split === 'split-right' || split === 'image-left' || accent === 'accent-left';
    const full = !!accent;
    const mediaW = full ? '42cqw' : '40cqw';
    const media = photo && hasMedia ? { ...slide.media, aspect: 'fill', frame: undefined } : slide.media;
    return (
      <div style={baseStyle(resolved, theme.fontDisplay)}>
        {!mediaLeft || !full ? <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} /> : null}
        <div style={{ position: 'absolute', top: '16cqh', bottom: '11cqh', width: full ? '44cqw' : '40cqw', ...(mediaLeft ? { right: GUTTER } : { left: GUTTER }) }}>
          <SlideHeader inline slide={slide} eyebrowColor={eyebrowColor} resolved={resolved} />
          <div style={{ color: resolved.textMuted, ...TYPE.body, marginTop: '3cqh' }}>
            <ContentBody slide={slide} resolved={resolved} theme={theme} />
            <CtaButton slide={slide} theme={theme} />
          </div>
        </div>
        <div style={full
          ? { position: 'absolute', top: 0, bottom: 0, width: mediaW, ...(mediaLeft ? { left: 0 } : { right: 0 }) }
          : { position: 'absolute', top: '12cqh', bottom: '11cqh', width: mediaW, ...(mediaLeft ? { left: GUTTER } : { right: GUTTER }) }}>
          <EMedia path="media" media={media} style={{ width: '100%', height: '100%' }} frameRadius={full ? '0' : '1cqw'} placeholder={photo ? 'Add a photo' : 'Add image'} />
        </div>
        <BrandMark theme={theme} resolved={resolved} />
      </div>
    );
  }

  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <SlideHeader slide={slide} eyebrowColor={eyebrowColor} resolved={resolved} />
      <div style={{ position: 'absolute', left: GUTTER, right: GUTTER, bottom: '11cqh', top: '38cqh', color: resolved.textMuted, ...TYPE.body }}>
        <ContentBody slide={slide} resolved={resolved} theme={theme} />
        <CtaButton slide={slide} theme={theme} />
      </div>
      {edit.editable && !hasMedia && (
        <div
          data-eadd=""
          onClick={() => edit.pickMedia((m) => edit.update((s) => ({ ...s, media: m, layout: 'split-left' })))}
          style={{ position: 'absolute', top: '11cqh', right: '4cqw', cursor: 'pointer', color: '#7C3AED', ...TYPE.caption, padding: '0.8cqh 1.2cqw', border: '0.12cqw dashed rgba(124,58,237,0.6)', borderRadius: 999 }}
        >
          + Add image
        </div>
      )}
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

// ─── 13. Image ──────────────────────────────────────────────────────────────

function ImageSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const resolved = resolveTheme(theme, slide.theme ?? 'light');
  const edit = useEdit();
  const eyebrowColor = resolved.isInverse ? 'rgba(255,255,255,0.85)' : theme.primary;
  const hasHeader = !!(slide.title || slide.eyebrow) || edit.editable;
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <SlideHeader slide={slide} eyebrowColor={eyebrowColor} resolved={resolved} />
      <div style={{ position: 'absolute', left: GUTTER, right: GUTTER, top: hasHeader ? '36cqh' : '14cqh', bottom: '17cqh' }}>
        <EMedia path="media" media={slide.media} style={{ width: '100%', height: '100%' }} placeholder="Add an image or video from the Content Hub" />
      </div>
      <EText path="caption" value={slide.caption} placeholder="Add a caption" style={{ position: 'absolute', left: GUTTER, right: GUTTER, bottom: '11cqh', color: resolved.textMuted, ...TYPE.bodySmall }} />
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

// ─── 14. Showcase ───────────────────────────────────────────────────────────

function ShowcaseSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const edit = useEdit();
  if (slide.variant === 'creator-results') return <CreatorResultsSlide slide={slide} theme={theme} />;
  const resolved = resolveTheme(theme, slide.theme ?? 'light');
  const eyebrowColor = resolved.isInverse ? 'rgba(255,255,255,0.85)' : theme.primary;
  if (slide.variant === 'spotlight') return <SpotlightSlide slide={slide} theme={theme} resolved={resolved} eyebrowColor={eyebrowColor} />;
  if (slide.variant === 'profiles') return <ProfilesSlide slide={slide} theme={theme} resolved={resolved} eyebrowColor={eyebrowColor} />;
  if (slide.variant === 'creator-cards') return <CreatorCardsSlide slide={slide} theme={theme} resolved={resolved} eyebrowColor={eyebrowColor} />;
  if (slide.variant === 'creator-reels') return <CreatorReelsSlide slide={slide} theme={theme} resolved={resolved} eyebrowColor={eyebrowColor} />;
  if (slide.variant === 'creator-split') return <CreatorSplitSlide slide={slide} theme={theme} resolved={resolved} eyebrowColor={eyebrowColor} />;
  if (slide.variant === 'creator-feature') return <CreatorFeatureSlide slide={slide} theme={theme} resolved={resolved} eyebrowColor={eyebrowColor} />;
  if (slide.variant === 'team') return <TeamSlide slide={slide} theme={theme} resolved={resolved} eyebrowColor={eyebrowColor} />;
  const items: any[] = (slide.items ?? []).slice(0, 4);
  const portrait = items.some((it) => mediaAspect(it.media));
  const cols = Math.max(items.length + 0, 1);
  const cardBg = resolved.isInverse ? 'rgba(255,255,255,0.08)' : theme.surface;
  const chipBg = resolved.isInverse ? 'rgba(255,255,255,0.14)' : theme.primarySoft;
  const chipColor = resolved.isInverse ? resolved.text : theme.primary;
  const hasHeader = !!(slide.title || slide.eyebrow) || edit.editable;
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <SlideHeader slide={slide} eyebrowColor={eyebrowColor} resolved={resolved} />
      <div style={{ position: 'absolute', left: GUTTER, right: GUTTER, bottom: '11cqh', top: hasHeader ? '34cqh' : '14cqh', display: 'grid', gap: '2cqw', gridTemplateColumns: `repeat(${cols}, 1fr)`, gridTemplateRows: 'minmax(0, 1fr)', alignItems: 'stretch' }}>
        {items.map((it, i) => {
          const stats: any[] = Array.isArray(it.stats) ? it.stats.slice(0, 3) : [];
          return (
            <ECard key={i} listPath="items" index={i} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, borderRadius: '1.2cqw', overflow: edit.editable ? 'visible' : 'hidden', background: cardBg }}>
              {/* The media flexes into whatever height the text below leaves — the body is never clipped. */}
              <EMedia path={`items.${i}.media`} media={it.media} style={{ flex: '1 1 0%', minHeight: portrait ? '18cqh' : '14cqh', overflow: 'hidden', ...(portrait ? { padding: '1.6cqh 1cqw 0' } : {}) }} frameRadius={portrait ? '1cqw' : '1.2cqw 1.2cqw 0 0'} placeholder="Add media" />
              <div style={{ padding: portrait ? '1.4cqh 1.4cqw 1.8cqh' : '2cqh 1.6cqw 2.2cqh', display: 'flex', flexDirection: 'column', gap: '0.7cqh', minWidth: 0, flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.7cqw', minWidth: 0 }}>
                  {(it.avatarUrl || edit.editable) && <Avatar url={it.avatarUrl} name={it.title} size="2.4cqw" color={theme.primary} />}
                  <EText path={`items.${i}.title`} value={it.title} placeholder="Name" style={{ ...TYPE.title, fontSize: '1.7cqw', color: resolved.text, minWidth: 0 }} />
                </div>
                <EText path={`items.${i}.subtitle`} value={it.subtitle} placeholder="@handle · campaign" style={{ ...TYPE.bodySmall, color: resolved.textMuted }} />
                <EText path={`items.${i}.body`} value={it.body} placeholder={edit.editable ? 'Add a note' : undefined} multiline style={{ ...TYPE.bodySmall, color: resolved.textMuted, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' } as CSSProperties} />
                {(stats.length > 0 || edit.editable) && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6cqw', marginTop: '1cqh', alignItems: 'center' }}>
                    {stats.map((st: any, si: number) => (
                      <span key={si} data-ecard="" style={{ display: 'inline-flex', alignItems: 'baseline', gap: '0.4cqw', padding: '0.7cqh 0.9cqw', borderRadius: 999, fontSize: '1.05cqw', fontWeight: 600, background: chipBg, color: chipColor, position: 'relative' }}>
                        <EText path={`items.${i}.stats.${si}.label`} value={st.label} tag="span" placeholder="LABEL" style={{ fontSize: '0.85cqw', letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.75 }} />
                        <EText path={`items.${i}.stats.${si}.value`} value={st.value} tag="span" placeholder="0" style={{}} />
                        {edit.editable && (
                          <button type="button" data-ecard-x="" title="Remove stat" onMouseDown={(e) => e.preventDefault()} onClick={(e) => { e.stopPropagation(); edit.update((s) => { const list = [...(s.items || [])]; const item = { ...list[i] }; item.stats = (item.stats || []).filter((_: any, j: number) => j !== si); list[i] = item; return { ...s, items: list }; }); }} style={{ position: 'absolute', top: '-0.9cqh', right: '-0.5cqw', width: '1.6cqw', height: '1.6cqw', borderRadius: '50%', border: 'none', background: 'rgba(180,35,24,0.9)', color: '#fff', fontSize: '0.9cqw', lineHeight: 1, cursor: 'pointer' }}>×</button>
                        )}
                      </span>
                    ))}
                    {edit.editable && stats.length < 3 && (
                      <span data-eadd="" onClick={() => edit.update((s) => { const list = [...(s.items || [])]; const item = { ...list[i] }; item.stats = [...(item.stats || []), { label: 'Stat', value: '0' }]; list[i] = item; return { ...s, items: list }; })} style={{ cursor: 'pointer', color: '#7C3AED', fontSize: '1.05cqw', fontWeight: 600 }}>+ stat</span>
                    )}
                  </div>
                )}
              </div>
            </ECard>
          );
        })}
        <EAddCard listPath="items" count={items.length} max={4} label="Add card" blank={() => ({ title: 'Name', subtitle: '', stats: [] })} style={{ minHeight: '30cqh' }} />
      </div>
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

// ─── 14b. Creator profiles (3–4 creators, round photo, long description, facts) ──

/** The avatar slot takes any picked media; only its still is kept, cropped to a circle. */
function profileAvatar(it: any): any {
  const m = it?.avatar;
  // Explicit 1:1 — without it the frame defaults to a 9:16 phone column, which inside a circle shows as a thin strip.
  if (m?.url || m?.posterUrl) return { url: m.posterUrl || m.url, kind: 'image', aspect: '1:1', alt: m.alt };
  return it?.avatarUrl ? { url: it.avatarUrl, kind: 'image', aspect: '1:1' } : null;
}

function useAddCreator(listPath = 'items') {
  const edit = useEdit();
  return edit.pickCreator ? () => edit.pickCreator!((f) => edit.update((s) => ({ ...s, [listPath]: [...(s[listPath] || []), itemFromCreator(f)] }))) : undefined;
}
/** "Fill from creator database" for a card that was typed by hand. */
function FillFromCreator({ index }: { index: number }) {
  const edit = useEdit();
  if (!edit.pickCreator) return null;
  return (
    <span data-eadd="" onClick={() => edit.pickCreator!((f) => edit.update((s) => { const list = [...(s.items || [])]; list[index] = itemFromCreator(f, list[index] || {}); return { ...s, items: list }; }))} style={{ cursor: 'pointer', color: '#7C3AED', fontSize: '1.05cqw', fontWeight: 600 }}>+ Fill from creator database</span>
  );
}
function FactLines({ item, index, fields, dense, resolved, editable }: { item: any; index: number; fields: Record<string, boolean>; dense: boolean; resolved: ResolvedTheme; editable: boolean }) {
  const edit = useEdit();
  const lines = creatorLines(item, fields);
  const manualCount = Array.isArray(item.stats) ? item.stats.length : 0;
  return (
    <>
      {lines.map((ln, k) => (
        <div key={k} data-ecard="" style={{ position: 'relative', display: 'flex', alignItems: 'baseline', gap: '0.5cqw', ...(dense ? TYPE.bodySmall : TYPE.body), color: resolved.text }}>
          {ln.auto ? (
            <><span style={{ color: resolved.textMuted }}>{ln.label}:</span><span style={{ fontWeight: 600 }}>{ln.value}</span></>
          ) : (
            <>
              <EText path={`items.${index}.stats.${ln.index}.label`} value={ln.label} tag="span" placeholder="Followers" style={{ color: resolved.textMuted }} />
              <span style={{ color: resolved.textMuted }}>:</span>
              <EText path={`items.${index}.stats.${ln.index}.value`} value={ln.value} tag="span" placeholder="144k" style={{ fontWeight: 600 }} />
              {editable && (
                <button type="button" data-ecard-x="" title="Remove line" onMouseDown={(e) => e.preventDefault()} onClick={(e) => { e.stopPropagation(); edit.update((s) => { const list = [...(s.items || [])]; const it = { ...list[index] }; it.stats = (it.stats || []).filter((_: any, j: number) => j !== ln.index); list[index] = it; return { ...s, items: list }; }); }} style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', border: 0, background: 'rgba(0,0,0,0.5)', color: '#fff', width: '1.4cqw', height: '1.4cqw', borderRadius: '50%', fontSize: '0.9cqw', lineHeight: 1, cursor: 'pointer' }}>×</button>
              )}
            </>
          )}
        </div>
      ))}
      {editable && manualCount < 4 && (
        <span data-eadd="" onClick={() => edit.update((s) => { const list = [...(s.items || [])]; const it = { ...list[index] }; it.stats = [...(it.stats || []), { label: 'Fact', value: '' }]; list[index] = it; return { ...s, items: list }; })} style={{ cursor: 'pointer', color: '#7C3AED', fontSize: '1.05cqw', fontWeight: 600 }}>+ Add fact</span>
      )}
      {editable && !item.creator && <FillFromCreator index={index} />}
    </>
  );
}

function ProfilesSlide({ slide, theme, resolved, eyebrowColor }: { slide: any; theme: Theme; resolved: ResolvedTheme; eyebrowColor: string }) {
  const edit = useEdit();
  const fields = creatorFieldsOf(slide);
  const items: any[] = (slide.items ?? []).slice(0, 4);
  const cols = Math.max(items.length + 0, 1);
  const dense = cols >= 4;
  const hasHeader = !!(slide.title || slide.eyebrow) || edit.editable;
  const rule = resolved.isInverse ? 'rgba(255,255,255,0.35)' : theme.primary;
  const photo = dense ? '6.4cqw' : '8cqw';
  const addCreator = useAddCreator();
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <SlideHeader slide={slide} eyebrowColor={eyebrowColor} resolved={resolved} />
      <div style={{ position: 'absolute', left: GUTTER, right: GUTTER, bottom: '11cqh', top: hasHeader ? '32cqh' : '14cqh', display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gridTemplateRows: 'minmax(0, 1fr)' }}>
        {items.map((it, i) => {
          const avatar = fields.photo ? profileAvatar(it) : null;
          const body = it.body || (fields.bio && it.creator?.bio) || '';
          return (
            <ECard key={i} listPath="items" index={i} style={{ display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, padding: `0.5cqh ${dense ? '1.4cqw' : '2.2cqw'} 0`, borderLeft: i > 0 ? `0.22cqw solid ${rule}` : 'none', ...(i === 0 ? { paddingLeft: 0 } : {}) }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1cqw', minWidth: 0 }}>
                <EText path={`items.${i}.title`} value={it.title} placeholder="Creator name" style={{ ...TYPE.title, fontSize: dense ? '1.9cqw' : '2.3cqw', lineHeight: 1.08, color: resolved.text, minWidth: 0, flex: 1 }} />
                {fields.photo && (
                  <div style={{ width: photo, height: photo, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, background: resolved.isInverse ? 'rgba(255,255,255,0.1)' : theme.primarySoft }}>
                    {avatar || edit.editable
                      ? <EMedia path={`items.${i}.avatar`} media={avatar} placeholder="Photo" frameRadius="50%" style={{ width: '100%', height: '100%' }} />
                      : <Avatar url={null} name={it.title} size={photo} color={theme.primary} />}
                  </div>
                )}
              </div>
              {fields.handle && <EText path={`items.${i}.subtitle`} value={it.subtitle} placeholder={edit.editable ? '@handle · Instagram' : undefined} style={{ ...TYPE.bodySmall, color: resolved.textMuted, marginTop: '0.8cqh' }} />}
              <EText path={`items.${i}.body`} value={body} placeholder="Who they are and why they fit — a few sentences." multiline style={{ ...(dense ? TYPE.bodySmall : TYPE.body), color: resolved.text, marginTop: '2.2cqh', overflow: 'hidden', flex: '0 1 auto', minHeight: 0 }} />
              <div style={{ marginTop: 'auto', paddingTop: '2.4cqh', display: 'flex', flexDirection: 'column', gap: '0.5cqh' }}>
                <FactLines item={it} index={i} fields={fields} dense={dense} resolved={resolved} editable={edit.editable} />
              </div>
            </ECard>
          );
        })}
        <EAddCard listPath="items" count={items.length} max={4} label="Add creator" onAdd={addCreator} blank={() => ({ title: 'Creator name', subtitle: '@handle · Instagram', body: '', stats: [{ label: 'Age', value: '' }, { label: 'Followers', value: '' }, { label: 'Engagement rate', value: '' }] })} style={{ minHeight: '30cqh', marginLeft: '1.2cqw' }} />
      </div>
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

// ─── 14c. Creator cards (up to 3 in one row, photo centred, facts as chips) ──

function CreatorCardsSlide({ slide, theme, resolved, eyebrowColor }: { slide: any; theme: Theme; resolved: ResolvedTheme; eyebrowColor: string }) {
  const edit = useEdit();
  const fields = creatorFieldsOf(slide);
  const items: any[] = (slide.items ?? []).slice(0, 3);
  const n = items.length + 0;
  const cols = Math.max(n, 1);
  const twoRows = false; // max 3 cards → always one row (user preference)
  const hasHeader = !!(slide.title || slide.eyebrow) || edit.editable;
  const cardBg = resolved.isInverse ? 'rgba(255,255,255,0.08)' : theme.surface;
  const chipBg = resolved.isInverse ? 'rgba(255,255,255,0.14)' : theme.primarySoft;
  const chipColor = resolved.isInverse ? resolved.text : theme.primary;
  const photo = twoRows ? '5.6cqw' : '9cqw';
  const addCreator = useAddCreator();
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <SlideHeader slide={slide} eyebrowColor={eyebrowColor} resolved={resolved} />
      <div style={{ position: 'absolute', left: GUTTER, right: GUTTER, bottom: '11cqh', top: hasHeader ? '32cqh' : '14cqh', display: 'grid', gap: twoRows ? '1.2cqw' : '1.8cqw', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gridAutoRows: 'minmax(0, 1fr)' }}>
        {items.map((it, i) => {
          const avatar = fields.photo ? profileAvatar(it) : null;
          const body = it.body || (fields.bio && it.creator?.bio) || '';
          const lines = creatorLines(it, fields);
          return (
            <ECard key={i} listPath="items" index={i} style={{ display: 'flex', flexDirection: twoRows ? 'row' : 'column', alignItems: twoRows ? 'center' : 'stretch', textAlign: twoRows ? 'left' : 'center', gap: twoRows ? '1.2cqw' : '1cqh', minWidth: 0, minHeight: 0, padding: twoRows ? '1.4cqh 1.4cqw' : '2.6cqh 1.8cqw 2.2cqh', borderRadius: '1.2cqw', background: cardBg, overflow: edit.editable ? 'visible' : 'hidden' }}>
              {fields.photo && (
                <div style={{ width: photo, height: photo, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, alignSelf: 'center', background: resolved.isInverse ? 'rgba(255,255,255,0.1)' : theme.primarySoft }}>
                  {avatar || edit.editable
                    ? <EMedia path={`items.${i}.avatar`} media={avatar} placeholder="Photo" frameRadius="50%" style={{ width: '100%', height: '100%' }} />
                    : <Avatar url={null} name={it.title} size={photo} color={theme.primary} />}
                </div>
              )}
              <div style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5cqh', alignItems: twoRows ? 'flex-start' : 'center' }}>
                <EText path={`items.${i}.title`} value={it.title} placeholder="Creator name" style={{ ...TYPE.title, fontSize: twoRows ? '1.6cqw' : '2cqw', lineHeight: 1.1, color: resolved.text, minWidth: 0 }} />
                {fields.handle && <EText path={`items.${i}.subtitle`} value={it.subtitle} placeholder={edit.editable ? '@handle · Instagram' : undefined} style={{ ...TYPE.bodySmall, color: resolved.textMuted }} />}
                {(body || edit.editable) && !twoRows && <EText path={`items.${i}.body`} value={body} placeholder="One line on who they are" multiline style={{ ...TYPE.bodySmall, color: resolved.textMuted, marginTop: '0.6cqh', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' } as CSSProperties} />}
                {(lines.length > 0 || edit.editable) && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5cqw', marginTop: '0.8cqh', justifyContent: twoRows ? 'flex-start' : 'center' }}>
                    {lines.map((ln, k) => (
                      <span key={k} style={{ display: 'inline-flex', alignItems: 'baseline', gap: '0.4cqw', padding: '0.55cqh 0.8cqw', borderRadius: 999, fontSize: twoRows ? '0.95cqw' : '1.05cqw', fontWeight: 600, background: chipBg, color: chipColor }}>
                        <span style={{ fontSize: '0.8cqw', letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.75 }}>{ln.label}</span>{ln.value}
                      </span>
                    ))}
                    {edit.editable && !it.creator && <FillFromCreator index={i} />}
                  </div>
                )}
              </div>
            </ECard>
          );
        })}
        <EAddCard listPath="items" count={items.length} max={3} label="Add creator" onAdd={addCreator} blank={() => ({ title: 'Creator name', subtitle: '@handle · Instagram', body: '', stats: [] })} style={{ minHeight: '30cqh' }} />
      </div>
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

// ─── 14d. Creator + video designs (reels / split / feature) ─────────────────

/** Any picked media in a creator design sits in a 9:16 phone frame. */
function phoneMedia(m: any): any { return m && (m.url || m.posterUrl) ? { ...m, aspect: m.aspect || '9:16', frame: 'phone' } : m; }

/** Round photo + name + handle (+ optional body) + fact chips — the profile block shared by the video designs. */
function CreatorProfile({ item, index, fields, theme, resolved, photo, nameSize, align = 'left', body, chipSize = '1cqw', clampBody = 3 }: {
  item: any; index: number; fields: Record<string, boolean>; theme: Theme; resolved: ResolvedTheme; photo: string; nameSize: string; align?: 'left' | 'center'; body?: boolean; chipSize?: string; clampBody?: number;
}) {
  const edit = useEdit();
  const avatar = fields.photo ? profileAvatar(item) : null;
  const lines = creatorLines(item, fields);
  const chipBg = resolved.isInverse ? 'rgba(255,255,255,0.14)' : theme.primarySoft;
  const chipColor = resolved.isInverse ? resolved.text : theme.primary;
  const text = item.body || (fields.bio && item.creator?.bio) || '';
  const center = align === 'center';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5cqh', minWidth: 0, alignItems: center ? 'center' : 'flex-start', textAlign: center ? 'center' : 'left' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.9cqw', minWidth: 0, maxWidth: '100%', flexDirection: center ? 'column' : 'row' }}>
        {fields.photo && (
          <div style={{ width: photo, height: photo, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, background: resolved.isInverse ? 'rgba(255,255,255,0.1)' : theme.primarySoft }}>
            {avatar || edit.editable
              ? <EMedia path={`items.${index}.avatar`} media={avatar} placeholder="Photo" frameRadius="50%" style={{ width: '100%', height: '100%' }} />
              : <Avatar url={null} name={item.title} size={photo} color={theme.primary} />}
          </div>
        )}
        <div style={{ minWidth: 0 }}>
          <EText path={`items.${index}.title`} value={item.title} placeholder="Creator name" style={{ ...TYPE.title, fontSize: nameSize, lineHeight: 1.1, color: resolved.text, minWidth: 0 }} />
          {fields.handle && <EText path={`items.${index}.subtitle`} value={item.subtitle} placeholder={edit.editable ? '@handle · Instagram' : undefined} style={{ ...TYPE.bodySmall, color: resolved.textMuted, marginTop: '0.3cqh' }} />}
        </div>
      </div>
      {body && (text || edit.editable) && <EText path={`items.${index}.body`} value={text} placeholder="Who they are and why they fit" multiline style={{ ...TYPE.bodySmall, color: resolved.textMuted, marginTop: '0.6cqh', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: clampBody, WebkitBoxOrient: 'vertical' } as CSSProperties} />}
      {(lines.length > 0 || edit.editable) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.45cqw', marginTop: '0.8cqh', justifyContent: center ? 'center' : 'flex-start' }}>
          {lines.map((ln, k) => (
            <span key={k} style={{ display: 'inline-flex', alignItems: 'baseline', gap: '0.35cqw', padding: '0.5cqh 0.75cqw', borderRadius: 999, fontSize: chipSize, fontWeight: 600, background: chipBg, color: chipColor, whiteSpace: 'nowrap' }}>
              <span style={{ fontSize: `calc(${chipSize} * 0.78)`, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.75 }}>{ln.label}</span>{ln.value}
            </span>
          ))}
          {edit.editable && !item.creator && <FillFromCreator index={index} />}
        </div>
      )}
    </div>
  );
}

const CREATOR_BLANK = () => ({ title: 'Creator name', subtitle: '@handle · Instagram', body: '', stats: [] });

/** Up to 3 columns: phone-framed video on top, profile row below. */
function CreatorReelsSlide({ slide, theme, resolved, eyebrowColor }: { slide: any; theme: Theme; resolved: ResolvedTheme; eyebrowColor: string }) {
  const edit = useEdit();
  const fields = creatorFieldsOf(slide);
  const items: any[] = (slide.items ?? []).slice(0, 3);
  const cols = Math.max(items.length + 0, 1);
  const hasHeader = !!(slide.title || slide.eyebrow) || edit.editable;
  const cardBg = resolved.isInverse ? 'rgba(255,255,255,0.08)' : theme.surface;
  const addCreator = useAddCreator();
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <SlideHeader slide={slide} eyebrowColor={eyebrowColor} resolved={resolved} />
      <div style={{ position: 'absolute', left: GUTTER, right: GUTTER, bottom: '10cqh', top: hasHeader ? '30cqh' : '12cqh', display: 'grid', gap: '1.8cqw', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gridTemplateRows: 'minmax(0, 1fr)' }}>
        {items.map((it, i) => (
          <ECard key={i} listPath="items" index={i} style={{ display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, borderRadius: '1.2cqw', background: cardBg, padding: '1.4cqh 1.2cqw 1.6cqh', overflow: edit.editable ? 'visible' : 'hidden' }}>
            <EMedia path={`items.${i}.media`} media={phoneMedia(it.media)} style={{ flex: '1 1 0%', minHeight: '22cqh', overflow: 'hidden' }} frameRadius="1cqw" placeholder="Add reel or video" />
            <div style={{ marginTop: '1.4cqh', flexShrink: 0 }}>
              <CreatorProfile item={it} index={i} fields={fields} theme={theme} resolved={resolved} photo="3.2cqw" nameSize="1.5cqw" chipSize="0.9cqw" />
            </div>
          </ECard>
        ))}
        <EAddCard listPath="items" count={items.length} max={3} label="Add creator" onAdd={addCreator} blank={CREATOR_BLANK} style={{ minHeight: '30cqh' }} />
      </div>
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

/** Up to 2 wide cards: video left, profile + bio + facts right. */
function CreatorSplitSlide({ slide, theme, resolved, eyebrowColor }: { slide: any; theme: Theme; resolved: ResolvedTheme; eyebrowColor: string }) {
  const edit = useEdit();
  const fields = creatorFieldsOf(slide);
  const items: any[] = (slide.items ?? []).slice(0, 2);
  const cols = Math.max(items.length + 0, 1);
  const hasHeader = !!(slide.title || slide.eyebrow) || edit.editable;
  const cardBg = resolved.isInverse ? 'rgba(255,255,255,0.08)' : theme.surface;
  const addCreator = useAddCreator();
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <SlideHeader slide={slide} eyebrowColor={eyebrowColor} resolved={resolved} />
      <div style={{ position: 'absolute', left: GUTTER, right: GUTTER, bottom: '10cqh', top: hasHeader ? '30cqh' : '12cqh', display: 'grid', gap: '2cqw', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gridTemplateRows: 'minmax(0, 1fr)' }}>
        {items.map((it, i) => (
          <ECard key={i} listPath="items" index={i} style={{ display: 'flex', gap: '1.4cqw', minWidth: 0, minHeight: 0, borderRadius: '1.2cqw', background: cardBg, padding: '1.6cqh 1.4cqw', overflow: edit.editable ? 'visible' : 'hidden' }}>
            <EMedia path={`items.${i}.media`} media={phoneMedia(it.media)} style={{ width: '38%', flexShrink: 0, minHeight: 0, overflow: 'hidden' }} frameRadius="1cqw" placeholder="Add reel" />
            <div style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <CreatorProfile item={it} index={i} fields={fields} theme={theme} resolved={resolved} photo="4.4cqw" nameSize="1.9cqw" body clampBody={5} chipSize="0.95cqw" />
            </div>
          </ECard>
        ))}
        <EAddCard listPath="items" count={items.length} max={2} label="Add creator" onAdd={addCreator} blank={CREATOR_BLANK} style={{ minHeight: '30cqh' }} />
      </div>
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

/** One creator: hero profile with fact tiles on the left, their video on the right. */
function CreatorFeatureSlide({ slide, theme, resolved, eyebrowColor }: { slide: any; theme: Theme; resolved: ResolvedTheme; eyebrowColor: string }) {
  const edit = useEdit();
  const fields = creatorFieldsOf(slide);
  const it: any = (slide.items ?? [])[0];
  const tileBg = resolved.isInverse ? 'rgba(255,255,255,0.08)' : theme.surface;
  const addCreator = useAddCreator();
  if (!it) {
    return (
      <div style={baseStyle(resolved, theme.fontDisplay)}>
        <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
        <SlideHeader slide={slide} eyebrowColor={eyebrowColor} resolved={resolved} />
        <div style={{ position: 'absolute', left: GUTTER, right: GUTTER, top: '32cqh', bottom: '11cqh' }}>
          <EAddCard listPath="items" count={0} max={1} label="Add creator" onAdd={addCreator} blank={CREATOR_BLANK} style={{ height: '100%' }} />
        </div>
        <BrandMark theme={theme} resolved={resolved} />
      </div>
    );
  }
  const avatar = fields.photo ? profileAvatar(it) : null;
  const lines = creatorLines(it, fields).slice(0, 6);
  const text = it.body || (fields.bio && it.creator?.bio) || '';
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <div style={{ position: 'absolute', left: GUTTER, top: '12cqh', width: '52cqw', bottom: '11cqh', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Eyebrow path="eyebrow" text={slide.eyebrow} color={eyebrowColor} placeholder="CREATOR" />
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.4cqw', minWidth: 0 }}>
          {fields.photo && (
            <div style={{ width: '7.5cqw', height: '7.5cqw', borderRadius: '50%', overflow: 'hidden', flexShrink: 0, background: resolved.isInverse ? 'rgba(255,255,255,0.1)' : theme.primarySoft }}>
              {avatar || edit.editable
                ? <EMedia path="items.0.avatar" media={avatar} placeholder="Photo" frameRadius="50%" style={{ width: '100%', height: '100%' }} />
                : <Avatar url={null} name={it.title} size="7.5cqw" color={theme.primary} />}
            </div>
          )}
          <div style={{ minWidth: 0 }}>
            <EText path="items.0.title" value={it.title} placeholder="Creator name" tag="h2" style={{ ...TYPE.headline, color: resolved.text, margin: 0 }} />
            {fields.handle && <EText path="items.0.subtitle" value={it.subtitle} placeholder="@handle · Instagram" style={{ ...TYPE.bodyLarge, color: resolved.textMuted, marginTop: '0.6cqh' }} />}
          </div>
        </div>
        {(text || edit.editable) && <EText path="items.0.body" value={text} placeholder="Who they are, what they make and why they fit this brief." multiline style={{ ...TYPE.body, color: resolved.textMuted, marginTop: '2.4cqh', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical' } as CSSProperties} />}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '1.2cqw', marginTop: '2.6cqh' }}>
          {lines.map((ln, k) => (
            <div key={k} style={{ padding: '1.8cqh 1.3cqw', borderRadius: '1cqw', background: tileBg, minWidth: 0 }}>
              <div style={{ ...TYPE.caption, color: resolved.textMuted, marginBottom: '0.7cqh', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ln.label}</div>
              <div style={{ fontSize: ln.value.length > 14 ? '1.35cqw' : ln.value.length > 8 ? '1.9cqw' : '2.5cqw', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.1, color: theme.primary, overflowWrap: 'anywhere' }}>{ln.value}</div>
            </div>
          ))}
        </div>
        {edit.editable && (
          <div style={{ display: 'flex', gap: '1.2cqw', marginTop: '1.4cqh', alignItems: 'center' }}>
            {(Array.isArray(it.stats) ? it.stats.length : 0) < 4 && <span data-eadd="" onClick={() => edit.update((s) => { const list = [...(s.items || [])]; const item = { ...list[0] }; item.stats = [...(item.stats || []), { label: 'Fact', value: '' }]; list[0] = item; return { ...s, items: list }; })} style={{ cursor: 'pointer', color: '#7C3AED', fontSize: '1.05cqw', fontWeight: 600 }}>+ Add fact</span>}
            {!it.creator && <FillFromCreator index={0} />}
          </div>
        )}
        {edit.editable && Array.isArray(it.stats) && it.stats.length > 0 && (
          <div style={{ marginTop: '1cqh', display: 'flex', flexDirection: 'column', gap: '0.4cqh' }}>
            {it.stats.map((st: any, si: number) => (
              <div key={si} data-ecard="" style={{ position: 'relative', display: 'flex', gap: '0.5cqw', ...TYPE.bodySmall, color: resolved.textMuted }}>
                <EText path={`items.0.stats.${si}.label`} value={st.label} tag="span" placeholder="Label" style={{}} /><span>:</span>
                <EText path={`items.0.stats.${si}.value`} value={st.value} tag="span" placeholder="Value" style={{ fontWeight: 600, color: resolved.text }} />
                <button type="button" data-ecard-x="" title="Remove" onMouseDown={(e) => e.preventDefault()} onClick={(e) => { e.stopPropagation(); edit.update((s) => { const list = [...(s.items || [])]; const item = { ...list[0] }; item.stats = (item.stats || []).filter((_: any, j: number) => j !== si); list[0] = item; return { ...s, items: list }; }); }} style={{ marginLeft: '0.6cqw', border: 0, background: 'rgba(0,0,0,0.5)', color: '#fff', width: '1.3cqw', height: '1.3cqw', borderRadius: '50%', fontSize: '0.85cqw', lineHeight: 1, cursor: 'pointer' }}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div style={{ position: 'absolute', right: '5cqw', top: '10cqh', bottom: '9cqh', width: '32cqw' }}>
        <EMedia path="items.0.media" media={phoneMedia(it.media)} style={{ width: '100%', height: '100%' }} frameRadius="2cqw" placeholder="Add their reel or video" />
      </div>
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

// ─── 14e. Your team (Beatly people: photo, name, role, contact) ─────────────

/** A card item from a team member (what the picker inserts). */
export function itemFromTeamMember(m: any): any {
  return { title: m?.name || 'Name', subtitle: m?.title || 'Role', body: [m?.email, m?.phone].filter(Boolean).join(' · '), avatarUrl: m?.pictureUrl || undefined, avatar: m?.pictureUrl ? { url: m.pictureUrl, kind: 'image', aspect: '1:1' } : undefined };
}

function TeamSlide({ slide, theme, resolved, eyebrowColor }: { slide: any; theme: Theme; resolved: ResolvedTheme; eyebrowColor: string }) {
  const edit = useEdit();
  const items: any[] = (slide.items ?? []).slice(0, 6);
  const n = items.length + 0;
  const cols = n <= 4 ? Math.max(n, 1) : 3;
  const twoRows = n > 4;
  const hasHeader = !!(slide.title || slide.eyebrow) || edit.editable;
  const photo = twoRows ? '9cqw' : n <= 2 ? '14cqw' : '11cqw';
  const add = edit.pickTeamMember
    ? () => edit.pickTeamMember!((m) => edit.update((s) => ({ ...s, items: [...(s.items || []), itemFromTeamMember(m)] })))
    : undefined;
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <SlideHeader slide={slide} eyebrowColor={eyebrowColor} resolved={resolved} />
      <div style={{ position: 'absolute', left: GUTTER, right: GUTTER, bottom: '11cqh', top: hasHeader ? '34cqh' : '16cqh', display: 'grid', gap: twoRows ? '2cqh 2cqw' : '3cqw', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gridAutoRows: 'minmax(0, 1fr)', alignItems: 'start' }}>
        {items.map((it, i) => {
          const avatar = profileAvatar(it);
          return (
            <ECard key={i} listPath="items" index={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', minWidth: 0, minHeight: 0, gap: '0.6cqh' }}>
              <div style={{ width: photo, height: photo, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, background: resolved.isInverse ? 'rgba(255,255,255,0.1)' : theme.primarySoft, boxShadow: `0 0 0 0.4cqw ${resolved.isInverse ? 'rgba(255,255,255,0.12)' : theme.primarySoft}` }}>
                {avatar || edit.editable
                  ? <EMedia path={`items.${i}.avatar`} media={avatar} placeholder="Photo" frameRadius="50%" style={{ width: '100%', height: '100%' }} />
                  : <Avatar url={null} name={it.title} size={photo} color={theme.primary} />}
              </div>
              <EText path={`items.${i}.title`} value={it.title} placeholder="Full name" style={{ ...TYPE.title, fontSize: twoRows ? '1.5cqw' : '1.9cqw', lineHeight: 1.15, color: resolved.text, marginTop: '1.4cqh', minWidth: 0 }} />
              <EText path={`items.${i}.subtitle`} value={it.subtitle} placeholder="Title" style={{ ...TYPE.bodySmall, color: theme.primary, fontWeight: 600 }} />
              <EText path={`items.${i}.body`} value={it.body} placeholder={edit.editable ? 'email · phone' : undefined} style={{ ...TYPE.bodySmall, color: resolved.textMuted, marginTop: '0.4cqh', overflowWrap: 'anywhere' }} />
            </ECard>
          );
        })}
        <EAddCard listPath="items" count={items.length} max={6} label="Add team member" onAdd={add} blank={() => itemFromTeamMember(null)} style={{ minHeight: twoRows ? '18cqh' : '32cqh' }} />
      </div>
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

// ─── 15. Gallery ────────────────────────────────────────────────────────────

function GallerySlide({ slide, theme }: { slide: any; theme: Theme }) {
  const resolved = resolveTheme(theme, slide.theme ?? 'light');
  const edit = useEdit();
  const eyebrowColor = resolved.isInverse ? 'rgba(255,255,255,0.85)' : theme.primary;
  const items: any[] = (slide.items ?? []).slice(0, 6);
  const n = items.length + 0;
  const cols = n <= 2 ? 2 : n === 4 ? 2 : 3;
  const hasHeader = !!(slide.title || slide.eyebrow) || edit.editable;
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <SlideHeader slide={slide} eyebrowColor={eyebrowColor} resolved={resolved} />
      <div style={{ position: 'absolute', left: GUTTER, right: GUTTER, bottom: '11cqh', top: hasHeader ? '34cqh' : '14cqh', display: 'grid', gap: '1.6cqw', gridTemplateColumns: `repeat(${cols}, 1fr)`, gridAutoRows: '1fr' }}>
        {items.map((it, i) => (
          <ECard key={i} listPath="items" index={i} style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <EMedia path={`items.${i}.media`} media={it.media} style={{ flex: 1, minHeight: '12cqh' }} placeholder="Add image" />
            <EText path={`items.${i}.caption`} value={it.caption} placeholder={edit.editable ? 'Caption' : undefined} style={{ ...TYPE.bodySmall, color: resolved.textMuted, marginTop: '0.8cqh' }} />
          </ECard>
        ))}
        <EAddCard listPath="items" count={items.length} max={6} label="Add image" blank={() => ({ caption: '' })} style={{ minHeight: '12cqh' }} />
      </div>
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

// ─── 16. Boxes (text boxes / icons / arrows / toggles) ──────────────────────

const isRowVariant = (v: string) => v === 'arrows';
/** Box grid columns by count — the same rule in the PDF renderer. */
const boxGridCols = (n: number) => (n <= 2 ? Math.max(n, 1) : n === 3 ? 3 : n === 4 ? 2 : n <= 6 ? 3 : 4);

function BoxesSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const edit = useEdit();
  const [open, setOpen] = useState<Record<number, boolean>>({});
  if (slide.variant === 'audience') return <BoxesAudienceSlide slide={slide} theme={theme} />;
  if (slide.variant === 'stacked') return <BoxesStackedSlide slide={slide} theme={theme} />;
  if (slide.variant === 'agenda') return <BoxesAgendaSlide slide={slide} theme={theme} />;
  if (slide.variant === 'petals') return <BoxesPetalsSlide slide={slide} theme={theme} />;
  if (slide.variant === 'points') return <BoxesPointsSlide slide={slide} theme={theme} />;
  if (slide.variant === 'checklist') return <BoxesChecklistSlide slide={slide} theme={theme} />;
  if (slide.variant === 'rows') return <BoxesRowsSlide slide={slide} theme={theme} />;
  if (slide.variant === 'transition') return <BoxesTransitionSlide slide={slide} theme={theme} />;
  if (slide.variant === 'approach') return <BoxesApproachSlide slide={slide} theme={theme} />;
  const resolved = resolveTheme(theme, slide.theme ?? 'light');
  const variant: string = slide.variant ?? 'boxes';
  const hasAside = !!slide.aside && isRowVariant(variant);
  const hl = Number.isInteger(slide.highlight) ? Number(slide.highlight) : -1;
  const items: any[] = (slide.items ?? []).slice(0, 8);
  const tight = items.length >= 7;
  const eyebrowColor = resolved.isInverse ? 'rgba(255,255,255,0.85)' : theme.primary;
  const cardBg = resolved.isInverse ? 'rgba(255,255,255,0.08)' : theme.surface;
  const hasHeader = !!(slide.title || slide.eyebrow) || edit.editable;
  const top = hasHeader ? '36cqh' : '14cqh';
  const isRow = variant === 'arrows';
  const isList = variant === 'icons-small' || variant === 'toggles';
  // The grid re-flows with the count: 3 → one row of three, 4 → 2×2, 5–6 → three across, 7–8 → four across.
  const cols = isRow ? Math.max(items.length, 1) : isList ? 1 : boxGridCols(items.length);
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <SlideHeader slide={slide} eyebrowColor={eyebrowColor} resolved={resolved} />
      <div style={{ position: 'absolute', left: GUTTER, right: GUTTER, bottom: calloutInset(slide), top, display: 'grid', gridTemplateColumns: hasAside ? 'minmax(0, 1fr) 24%' : 'minmax(0, 1fr)', gap: '2.4cqw', overflow: 'hidden' }}>
      <div style={{ display: isList ? 'flex' : 'grid', flexDirection: 'column', gap: isList ? '1.2cqh' : '1.6cqw', gridTemplateColumns: isList ? undefined : `repeat(${cols}, 1fr)`, alignContent: 'start', overflow: 'hidden', minWidth: 0 }}>
        {items.map((it, i) => {
          const showBody = variant !== 'toggles' || edit.editable || open[i] !== false;
          const lit = isRow && hl === i; const dim = isRow && hl >= 0 && hl !== i;
          return (
            <ECard key={i} listPath="items" index={i} style={isRow
              ? { display: 'flex', flexDirection: 'column', padding: '2.4cqh 1.6cqw', borderRadius: '1cqw', background: lit ? theme.primary : cardBg, position: 'relative', opacity: dim ? 0.72 : 1 }
              : isList
                ? { display: 'flex', gap: '1.4cqw', alignItems: 'flex-start', padding: variant === 'toggles' ? '1.6cqh 1.6cqw' : '0.6cqh 0', borderRadius: '0.8cqw', background: variant === 'toggles' ? cardBg : 'transparent', cursor: variant === 'toggles' && !edit.editable ? 'pointer' : undefined }
                : { display: 'flex', flexDirection: 'column', padding: tight ? '1.4cqh 1.3cqw' : items.length >= 4 ? '2cqh 1.8cqw' : '2.6cqh 2cqw', borderRadius: '1.2cqw', background: cardBg, minWidth: 0 }}>
              {(variant === 'icons' || variant === 'icons-small') && (
                <EText path={`items.${i}.icon`} value={it.icon} placeholder="★" style={{ fontSize: variant === 'icons' ? (items.length >= 4 ? '2.4cqw' : '3.2cqw') : '2cqw', lineHeight: 1, marginBottom: variant === 'icons' ? (items.length >= 4 ? '1cqh' : '1.6cqh') : 0, flexShrink: 0, minWidth: '2.4cqw', color: theme.primary }} />
              )}
              {variant === 'toggles' && (
                <div onClick={() => !edit.editable && setOpen((o) => ({ ...o, [i]: o[i] === false }))} style={{ flexShrink: 0, width: '2.2cqw', height: '2.2cqw', borderRadius: '50%', background: theme.primary, color: '#fff', display: 'grid', placeItems: 'center', fontSize: '1.3cqw', fontWeight: 700, marginTop: '0.2cqh' }}>{showBody ? '−' : '+'}</div>
              )}
              {isRow && i > 0 && <div style={{ position: 'absolute', left: '-1.6cqw', top: '50%', transform: 'translateY(-50%)', width: 0, height: 0, borderStyle: 'solid', borderWidth: '1.2cqh 0 1.2cqh 1.4cqw', borderColor: `transparent transparent transparent ${theme.primary}` }} />}
              <div style={{ minWidth: 0, flex: 1 }}>
                {isRow && <EText path={`items.${i}.label`} value={it.label} fallback={String(i + 1).padStart(2, '0')} placeholder="01" style={{ ...TYPE.caption, color: lit ? 'rgba(255,255,255,0.85)' : theme.primary, marginBottom: '1cqh' }} />}
                <EText path={`items.${i}.title`} value={it.title} placeholder="Title" style={{ ...TYPE.title, fontSize: isList ? '1.7cqw' : tight ? '1.5cqw' : '1.9cqw', color: lit ? '#fff' : resolved.text, marginBottom: '0.8cqh' }} />
                {showBody && <EText path={`items.${i}.body`} value={it.body} placeholder="Add text" multiline style={{ ...(isList || items.length >= 4 ? TYPE.bodySmall : TYPE.body), color: lit ? 'rgba(255,255,255,0.85)' : resolved.textMuted }} />}
                {isRow && <SubBullets path={`items.${i}.bullets`} bullets={it.bullets} resolved={lit ? { ...resolved, text: '#fff', textMuted: 'rgba(255,255,255,0.85)' } : resolved} small />}
              </div>
            </ECard>
          );
        })}
        <EAddCard listPath="items" count={items.length} max={isRow ? 6 : 8} label={variant === 'toggles' ? 'Add toggle' : variant === 'arrows' ? 'Add step' : 'Add box'} blank={() => ({ icon: variant.startsWith('icons') ? '★' : undefined, title: 'Title', body: '' })} style={hasAside ? { right: 'calc(24% + 3.4cqw)' } : undefined} />
      </div>
      {hasAside && <AsidePanel slide={slide} resolved={resolved} theme={theme} />}
      </div>
      <CalloutBox slide={slide} resolved={resolved} theme={theme} />
      <Footnote slide={slide} resolved={resolved} />
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

// ─── 16a. Shared: aside panel, callout, rows, transition ────────────────────

/** `slide.aside: { title, bullets }` — a quiet panel on the right of a boxes / columns page ("Key benefits", "Guiding principles"). */
function AsidePanel({ slide, resolved, theme }: { slide: any; resolved: ResolvedTheme; theme: Theme }) {
  const aside = slide.aside; if (!aside || typeof aside !== 'object') return null;
  const bg = resolved.isInverse ? 'rgba(255,255,255,0.08)' : theme.surface;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, borderRadius: '1cqw', background: bg, padding: '2.2cqh 1.6cqw', overflow: 'hidden' }}>
      <EText path="aside.title" value={aside.title} placeholder="Aside title" style={{ ...TYPE.caption, color: theme.primary, marginBottom: '1.2cqh' }} />
      {aside.body && <EText path="aside.body" value={aside.body} multiline style={{ ...TYPE.bodySmall, color: resolved.textMuted }} />}
      <SubBullets path="aside.bullets" bullets={aside.bullets} resolved={resolved} small />
    </div>
  );
}

/** `slide.callout: { label, text }` — the "key question" / "so what" box along the bottom of a page. */
function CalloutBox({ slide, resolved, theme }: { slide: any; resolved: ResolvedTheme; theme: Theme }) {
  const co = slide.callout; if (!co || typeof co !== 'object') return null;
  return (
    <div style={{ position: 'absolute', left: GUTTER, right: GUTTER, bottom: '11cqh', borderRadius: '0.9cqw', border: `0.18cqw solid ${resolved.isInverse ? 'rgba(255,255,255,0.4)' : resolved.text}`, background: resolved.isInverse ? 'rgba(255,255,255,0.06)' : 'transparent', padding: '1.6cqh 1.6cqw', display: 'flex', gap: '1.4cqw', alignItems: 'baseline' }}>
      <EText path="callout.label" value={co.label} placeholder="Key question" style={{ ...TYPE.caption, color: theme.primary, flexShrink: 0 }} />
      <EText path="callout.text" value={co.text} placeholder="The one question this page leads to" multiline style={{ ...TYPE.body, fontWeight: 600, color: resolved.text, flex: 1 }} />
    </div>
  );
}

/** How far up the callout pushes the body area. */
const calloutInset = (slide: any) => (slide.callout && typeof slide.callout === 'object' ? '25cqh' : '11cqh');

/** Numbered / lettered rows: `items: [{ label?, title, body?, bullets?, aside? }]`, `numbering: 'numbers' | 'letters' | 'none'`, optional page `aside`. */
function BoxesRowsSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const resolved = resolveTheme(theme, slide.theme ?? 'light');
  const edit = useEdit();
  const items: any[] = (slide.items ?? []).slice(0, 8);
  const eyebrowColor = resolved.isInverse ? 'rgba(255,255,255,0.85)' : theme.primary;
  const hasHeader = !!(slide.title || slide.eyebrow) || edit.editable;
  const numbering: string = slide.numbering ?? 'numbers';
  const badge = (i: number, label?: string) => label || (numbering === 'letters' ? String.fromCharCode(65 + i) : numbering === 'none' ? '' : String(i + 1));
  const dense = items.length >= 5;
  const labelCol = numbering === 'labels'; // definition list: the label sits in its own column on the left
  const split = slide.layout === 'split'; // two equal text columns per row (question → action), with headers
  const heads: string[] = Array.isArray(slide.headers) ? slide.headers : [];
  const hasAside = !!slide.aside;
  const tint = resolved.isInverse ? 'rgba(255,255,255,0.1)' : theme.primaryTint;
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <SlideHeader slide={slide} eyebrowColor={eyebrowColor} resolved={resolved} />
      <div style={{ position: 'absolute', left: GUTTER, right: GUTTER, bottom: calloutInset(slide), top: hasHeader ? '34cqh' : '14cqh', display: 'grid', gridTemplateColumns: hasAside ? 'minmax(0, 1fr) 26%' : 'minmax(0, 1fr)', gap: '2.4cqw', overflow: 'hidden' }}>
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {split && (heads.length > 0 || edit.editable) && (
            <div style={{ display: 'flex', gap: '1.4cqw', paddingBottom: '0.8cqh', borderBottom: `0.15cqw solid ${theme.borderStrong}` }}>
              {numbering !== 'none' && !labelCol && <div style={{ flexShrink: 0, width: '2.6cqw' }} />}
              <EText path="headers.0" value={heads[0]} placeholder="Left column" style={{ ...TYPE.caption, color: resolved.text, flex: 1 }} />
              <EText path="headers.1" value={heads[1]} placeholder="Right column" style={{ ...TYPE.caption, color: resolved.text, flex: 1 }} />
            </div>
          )}
          {items.map((it, i) => (
            <ECard key={i} listPath="items" index={i} style={{ display: 'flex', gap: '1.4cqw', alignItems: 'flex-start', padding: `${dense ? 1.2 : 1.8}cqh 0`, borderBottom: i < items.length - 1 ? `0.12cqw dotted ${resolved.isInverse ? 'rgba(255,255,255,0.3)' : theme.borderStrong}` : 'none' }}>
              {labelCol && (
                <div style={{ flexShrink: 0, width: '20%', paddingTop: '0.3cqh' }}>
                  <EText path={`items.${i}.label`} value={it.label} placeholder="Label" style={{ ...TYPE.bodySmall, fontWeight: 700, color: theme.primary }} />
                </div>
              )}
              {numbering !== 'none' && !labelCol && (
                <div style={{ flexShrink: 0, width: '2.6cqw', height: '2.6cqw', borderRadius: '50%', background: theme.primary, color: '#fff', display: 'grid', placeItems: 'center', fontSize: '1.15cqw', fontWeight: 800 }}>
                  <EText path={`items.${i}.label`} value={it.label} fallback={badge(i)} placeholder={badge(i)} tag="span" style={{ color: '#fff', textAlign: 'center' }} />
                </div>
              )}
              <div style={{ minWidth: 0, flex: 1 }}>
                <EText path={`items.${i}.title`} value={it.title} placeholder={labelCol ? 'Heading (optional)' : 'Point'} style={{ ...TYPE.title, fontSize: dense || labelCol ? '1.5cqw' : '1.8cqw', color: resolved.text, marginBottom: it.body || it.bullets?.length ? '0.5cqh' : 0 }} />
                <EText path={`items.${i}.body`} value={it.body} placeholder="Add text" multiline style={{ ...TYPE.bodySmall, color: resolved.textMuted }} />
                <SubBullets path={`items.${i}.bullets`} bullets={it.bullets} resolved={resolved} small />
              </div>
              {split ? (
                <div style={{ flex: 1, minWidth: 0 }}>
                  <EText path={`items.${i}.aside`} value={it.aside} placeholder="Add text" multiline style={{ ...TYPE.bodySmall, color: resolved.textMuted }} />
                </div>
              ) : (it.aside || (edit.editable && !labelCol)) && (
                <div style={{ flexShrink: 0, width: '22%', borderRadius: '0.7cqw', background: tint, padding: '1cqh 1cqw' }}>
                  <EText path={`items.${i}.aside`} value={it.aside} placeholder="Example / note" multiline style={{ ...TYPE.bodySmall, color: resolved.isInverse ? resolved.text : theme.primary, fontWeight: 600 }} />
                </div>
              )}
            </ECard>
          ))}
          <div style={{ position: 'relative', minHeight: edit.editable ? '5cqh' : 0 }}>
            <EAddCard listPath="items" count={items.length} max={8} label="Add row" blank={() => (labelCol ? { label: 'Label', body: '' } : { title: 'Point', body: '' })} />
          </div>
        </div>
        {hasAside && <AsidePanel slide={slide} resolved={resolved} theme={theme} />}
      </div>
      <CalloutBox slide={slide} resolved={resolved} theme={theme} />
      <Footnote slide={slide} resolved={resolved} />
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

/** Today → future: `items: [{ from, to }]`, `fromLabel` / `toLabel` over the columns, optional page `aside` ("Key benefits"). */
function BoxesTransitionSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const resolved = resolveTheme(theme, slide.theme ?? 'light');
  const edit = useEdit();
  const items: any[] = (slide.items ?? []).slice(0, 5);
  const eyebrowColor = resolved.isInverse ? 'rgba(255,255,255,0.85)' : theme.primary;
  const hasHeader = !!(slide.title || slide.eyebrow) || edit.editable;
  const hasAside = !!slide.aside;
  const fromBg = resolved.isInverse ? 'rgba(255,255,255,0.08)' : theme.surface;
  const toBg = resolved.isInverse ? '#fff' : resolved.text;
  const toText = resolved.isInverse ? '#101828' : '#fff';
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <SlideHeader slide={slide} eyebrowColor={eyebrowColor} resolved={resolved} />
      <div style={{ position: 'absolute', left: GUTTER, right: GUTTER, bottom: calloutInset(slide), top: hasHeader ? '34cqh' : '14cqh', display: 'grid', gridTemplateColumns: hasAside ? 'minmax(0, 1fr) 26%' : 'minmax(0, 1fr)', gap: '2.4cqw', overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 3cqw minmax(0, 1fr)', gap: '1.4cqh 0.8cqw', alignContent: 'start' }}>
          <EText path="fromLabel" value={slide.fromLabel} fallback="Today" placeholder="Today" style={{ ...TYPE.caption, color: resolved.text, textAlign: 'center', paddingBottom: '0.6cqh', borderBottom: `0.12cqw solid ${theme.borderStrong}` }} />
          <div />
          <EText path="toLabel" value={slide.toLabel} fallback="Future" placeholder="Future" style={{ ...TYPE.caption, color: resolved.text, textAlign: 'center', paddingBottom: '0.6cqh', borderBottom: `0.12cqw solid ${theme.borderStrong}` }} />
          {items.map((it, i) => (
            <ECard key={i} listPath="items" index={i} style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 3cqw minmax(0, 1fr)', gap: '0.8cqw', position: 'relative' }}>
              <div style={{ borderRadius: '0.8cqw', background: fromBg, padding: '1.6cqh 1.4cqw', display: 'flex', alignItems: 'center' }}>
                <EText path={`items.${i}.from`} value={it.from} placeholder="Current state" multiline style={{ ...TYPE.bodySmall, color: resolved.textMuted, width: '100%' }} />
              </div>
              <div style={{ display: 'grid', placeItems: 'center', color: theme.primary, fontSize: '2cqw', lineHeight: 1 }}>▶</div>
              <div style={{ borderRadius: '0.8cqw', background: toBg, padding: '1.6cqh 1.4cqw', display: 'flex', alignItems: 'center' }}>
                <EText path={`items.${i}.to`} value={it.to} placeholder="Desired state" multiline style={{ ...TYPE.bodySmall, fontWeight: 600, color: toText, width: '100%' }} />
              </div>
            </ECard>
          ))}
          <div style={{ gridColumn: '1 / -1', position: 'relative', minHeight: edit.editable ? '5cqh' : 0 }}>
            <EAddCard listPath="items" count={items.length} max={5} label="Add row" blank={() => ({ from: '', to: '' })} />
          </div>
        </div>
        {hasAside && <AsidePanel slide={slide} resolved={resolved} theme={theme} />}
      </div>
      <CalloutBox slide={slide} resolved={resolved} theme={theme} />
      <Footnote slide={slide} resolved={resolved} />
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

/** Approach: 2–3 side-by-side boxes joined by "+", each with an arrow down to its output; `highlight` tints the one this chapter is about.
 *  `items: [{ title, body, output? }]`, `plus?: string` (the joiner, default "+"). */
function BoxesApproachSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const resolved = resolveTheme(theme, slide.theme ?? 'light');
  const edit = useEdit();
  const items: any[] = (slide.items ?? []).slice(0, 3);
  const eyebrowColor = resolved.isInverse ? 'rgba(255,255,255,0.85)' : theme.primary;
  const hasHeader = !!(slide.title || slide.eyebrow) || edit.editable;
  const hl = Number.isInteger(slide.highlight) ? Number(slide.highlight) : -1;
  const cardBg = resolved.isInverse ? 'rgba(255,255,255,0.08)' : theme.surface;
  const litBg = resolved.isInverse ? 'rgba(255,255,255,0.16)' : theme.primaryTint;
  const cols = items.map((_, i) => `minmax(0, 1fr)${i < items.length - 1 ? ' 3cqw' : ''}`).join(' ');
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <SlideHeader slide={slide} eyebrowColor={eyebrowColor} resolved={resolved} />
      <div style={{ position: 'absolute', left: GUTTER, right: GUTTER, bottom: calloutInset(slide), top: hasHeader ? '34cqh' : '14cqh', display: 'grid', gridTemplateColumns: cols || 'minmax(0, 1fr)', gridTemplateRows: 'minmax(0, 1fr) auto auto', columnGap: '1cqw', rowGap: '1.2cqh', overflow: 'hidden' }}>
        {items.map((it, i) => (
          <Fragment key={i}>
            <ECard listPath="items" index={i} style={{ gridColumn: `${i * 2 + 1}`, gridRow: 1, borderRadius: '1cqw', background: hl === i ? litBg : cardBg, padding: '2.4cqh 1.8cqw', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <EText path={`items.${i}.title`} value={it.title} placeholder="Option" style={{ ...TYPE.title, fontSize: '1.8cqw', color: resolved.text, textAlign: 'center', marginBottom: '1.4cqh', paddingBottom: '1cqh', borderBottom: `0.12cqw solid ${resolved.isInverse ? 'rgba(255,255,255,0.25)' : theme.borderStrong}` }} />
              <EText path={`items.${i}.body`} value={it.body} placeholder="What this covers" multiline style={{ ...TYPE.bodySmall, color: resolved.textMuted }} />
            </ECard>
            {i < items.length - 1 && (
              <div style={{ gridColumn: `${i * 2 + 2}`, gridRow: 1, display: 'grid', placeItems: 'center' }}>
                <div style={{ width: '2.6cqw', height: '2.6cqw', borderRadius: '50%', border: `0.15cqw solid ${theme.borderStrong}`, display: 'grid', placeItems: 'center', fontSize: '1.6cqw', fontWeight: 700, color: resolved.textMuted }}>{slide.plus || '+'}</div>
              </div>
            )}
            <div style={{ gridColumn: `${i * 2 + 1}`, gridRow: 2, textAlign: 'center', color: theme.primary, fontSize: '1.8cqw', lineHeight: 1 }}>▼</div>
            <div style={{ gridColumn: `${i * 2 + 1}`, gridRow: 3, textAlign: 'center' }}>
              <EText path={`items.${i}.output`} value={it.output} placeholder="→ what it produces" style={{ ...TYPE.caption, color: resolved.text }} />
            </div>
          </Fragment>
        ))}
        <div style={{ gridColumn: '1 / -1', gridRow: 4, position: 'relative', minHeight: edit.editable ? '5cqh' : 0 }}>
          <EAddCard listPath="items" count={items.length} max={3} label="Add box" blank={() => ({ title: 'Option', body: '', output: '' })} />
        </div>
      </div>
      <CalloutBox slide={slide} resolved={resolved} theme={theme} />
      <Footnote slide={slide} resolved={resolved} />
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

// ─── 16b. Audience comments ─────────────────────────────────────────────────

function CommentsSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const resolved = resolveTheme(theme, slide.theme ?? 'light');
  const edit = useEdit();
  const variant: string = slide.variant ?? 'grid';
  const stats: any[] = (slide.stats ?? []).slice(0, 4);
  const maxItems = variant === 'list' ? 4 : 6; // grid = 3 × 2 equal rows under the chip row
  const items: any[] = (slide.items ?? []).slice(0, maxItems);
  const eyebrowColor = resolved.isInverse ? 'rgba(255,255,255,0.85)' : theme.primary;
  const cardBg = resolved.isInverse ? 'rgba(255,255,255,0.08)' : theme.surface;
  const hasHeader = !!(slide.title || slide.eyebrow) || edit.editable;
  const cols = variant === 'list' ? 1 : 3;
  const rows = Math.max(1, Math.ceil((items.length + (edit.editable && items.length < maxItems ? 1 : 0)) / cols));
  const chipBg = resolved.isInverse ? 'rgba(255,255,255,0.12)' : theme.surface;
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <SlideHeader slide={slide} eyebrowColor={eyebrowColor} resolved={resolved} />
      <div style={{ position: 'absolute', left: GUTTER, right: GUTTER, bottom: '11cqh', top: hasHeader ? '31cqh' : '14cqh', display: 'grid', gap: '1.6cqh 1.4cqw', gridTemplateColumns: `repeat(${cols}, 1fr)`, gridTemplateRows: `${stats.length > 0 || edit.editable ? 'auto ' : ''}repeat(${rows}, 1fr)`, overflow: 'hidden' }}>
        {(stats.length > 0 || edit.editable) && (
          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '0.8cqw', flexWrap: 'wrap', marginBottom: '0.4cqh' }}>
            {stats.map((st, i) => (
              <ECard key={i} listPath="stats" index={i} style={{ display: 'inline-flex', alignItems: 'baseline', gap: '0.5cqw', padding: '0.9cqh 1.2cqw', borderRadius: '999px', background: chipBg, border: `0.08cqw solid ${resolved.isInverse ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.06)'}` }}>
                <EText path={`stats.${i}.label`} value={st.label} placeholder="Label" style={{ ...TYPE.caption, fontSize: '0.85cqw', color: resolved.textMuted }} />
                <EText path={`stats.${i}.value`} value={st.value} placeholder="—" style={{ fontSize: '1.5cqw', fontWeight: 700, color: theme.primary }} />
              </ECard>
            ))}
            <EAddCard listPath="stats" count={stats.length} max={4} label="Add stat" blank={() => ({ label: 'Positive', value: '—' })} style={{ minHeight: '4cqh', padding: '0 1.2cqw' }} />
          </div>
        )}
        {items.map((it, i) => {
          const initial = String(it.author ?? it.handle ?? '?').trim().charAt(0).toUpperCase() || '?';
          return (
            <ECard key={i} listPath="items" index={i} style={{ display: 'flex', gap: '1cqw', alignItems: 'flex-start', padding: '1.8cqh 1.4cqw', borderRadius: '1cqw', background: cardBg, minWidth: 0, overflow: 'hidden' }}>
              <div style={{ position: 'relative', width: '3.2cqw', height: '3.2cqw', borderRadius: '50%', flexShrink: 0, background: theme.primary, color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: '1.4cqw', overflow: 'hidden' }}>
                {initial}
                {/* Social CDN avatar links expire — the initial stays behind as the fallback. */}
                {it.avatarUrl && <img src={it.avatarUrl} alt="" referrerPolicy="no-referrer" onError={(e) => { e.currentTarget.style.display = 'none'; }} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.8cqw' }}>
                  <EText path={`items.${i}.author`} value={it.author} placeholder="Name" style={{ fontSize: '1.2cqw', fontWeight: 700, color: resolved.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} />
                  <EText path={`items.${i}.handle`} value={it.handle ? `@${String(it.handle).replace(/^@/, '')}` : ''} placeholder="@handle" style={{ fontSize: '0.95cqw', color: resolved.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }} />
                  {it.likes != null && Number(it.likes) > 0 && <span style={{ marginLeft: 'auto', fontSize: '1.05cqw', color: resolved.textMuted, whiteSpace: 'nowrap' }}>♥ {it.likes}</span>}
                </div>
                <EText path={`items.${i}.text`} value={it.text} placeholder="Comment" multiline style={{ color: resolved.text, fontSize: '1.3cqw', lineHeight: 1.5, marginTop: '1cqh', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: variant === 'list' ? 2 : 6, WebkitBoxOrient: 'vertical' } as CSSProperties} />
              </div>
            </ECard>
          );
        })}
        <EAddCard listPath="items" count={items.length} max={maxItems} label="Add comment" blank={() => ({ author: 'Name', handle: '', text: '' })} style={{ minHeight: '10cqh' }} />
      </div>
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

// ─── 14b. Showcase · spotlight (one creator: KPI tiles + their post) ────────

function SpotlightSlide({ slide, theme, resolved, eyebrowColor }: { slide: any; theme: Theme; resolved: ResolvedTheme; eyebrowColor: string }) {
  const edit = useEdit();
  const it: any = (slide.items ?? [])[0] ?? {};
  const stats: any[] = Array.isArray(it.stats) ? it.stats.slice(0, 6) : [];
  const tileBg = resolved.isInverse ? 'rgba(255,255,255,0.08)' : theme.surface;
  const media = it.media ? { ...it.media, frame: it.media.frame ?? 'phone' } : it.media;
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <div style={{ position: 'absolute', left: GUTTER, top: '12cqh', width: '52cqw', bottom: '11cqh', display: 'flex', flexDirection: 'column' }}>
        <Eyebrow path="eyebrow" text={slide.eyebrow} color={eyebrowColor} placeholder="CREATOR 1 / 8" />
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.2cqw' }}>
          {(it.avatarUrl || edit.editable) && <Avatar url={it.avatarUrl} name={it.title} size="4.6cqw" color={theme.primary} />}
          <EText path="items.0.title" value={it.title} placeholder="Creator name" tag="h2" style={{ ...TYPE.headline, color: resolved.text, margin: 0 }} />
        </div>
        <EText path="items.0.subtitle" value={it.subtitle} placeholder="@handle · platform · followers" style={{ ...TYPE.bodyLarge, color: resolved.textMuted, marginTop: '1.2cqh' }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.2cqw', marginTop: '3cqh' }}>
          {stats.map((st, si) => (
            <ECard key={si} listPath="items.0.stats" index={si} style={{ padding: '2cqh 1.4cqw', borderRadius: '1cqw', background: tileBg, minWidth: 0 }}>
              <EText path={`items.0.stats.${si}.label`} value={st.label} placeholder="LABEL" style={{ ...TYPE.caption, color: resolved.textMuted, marginBottom: '0.8cqh' }} />
              <EText path={`items.0.stats.${si}.value`} value={st.value} placeholder="0" style={{ fontSize: '2.6cqw', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.05, color: theme.primary }} />
            </ECard>
          ))}
          <EAddCard listPath="items.0.stats" count={stats.length} max={6} label="Add stat" blank={() => ({ label: 'Stat', value: '0' })} style={{ minHeight: '9cqh' }} />
        </div>
        <EText path="items.0.body" value={it.body} placeholder={edit.editable ? 'One line of analysis' : undefined} multiline style={{ ...TYPE.body, color: resolved.textMuted, marginTop: '3cqh' }} />
      </div>
      <div style={{ position: 'absolute', right: '5cqw', top: '10cqh', bottom: '9cqh', width: '32cqw' }}>
        <EMedia path="items.0.media" media={media} style={{ width: '100%', height: '100%' }} frameRadius="2cqw" placeholder="Add their post" />
      </div>
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

// ─── 17. Funnel / pyramid ───────────────────────────────────────────────────

function FunnelSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const resolved = resolveTheme(theme, slide.theme ?? 'light');
  const edit = useEdit();
  const variant: string = slide.variant ?? 'funnel';
  const steps: any[] = (slide.steps ?? []).slice(0, 6);
  const n = Math.max(steps.length, 1);
  const eyebrowColor = resolved.isInverse ? 'rgba(255,255,255,0.85)' : theme.primary;
  const hasHeader = !!(slide.title || slide.eyebrow) || edit.editable;
  const shade = (i: number) => {
    const t = n <= 1 ? 1 : 1 - (i / (n - 1)) * 0.55;
    return resolved.isInverse ? `rgba(255,255,255,${0.9 - i * 0.12})` : mixWithWhite(theme.primary, t);
  };
  const widthFor = (i: number) => {
    const min = 0.42;
    if (variant === 'pyramid' || variant === 'stepped' || variant === 'left-stepped') return `${(min + ((1 - min) * i) / Math.max(n - 1, 1)) * 100}%`;
    return `${(1 - ((1 - min) * i) / Math.max(n - 1, 1)) * 100}%`;
  };
  const align = variant === 'left-stepped' ? 'flex-start' : 'center';
  const outline = variant === 'pyramid';
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <SlideHeader slide={slide} eyebrowColor={eyebrowColor} resolved={resolved} />
      <div style={{ position: 'absolute', left: GUTTER, right: GUTTER, bottom: '11cqh', top: hasHeader ? '36cqh' : '14cqh', display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: '3cqw' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: align, justifyContent: 'center', gap: '0.6cqh' }}>
          {steps.map((st, i) => (
            <div key={i} style={{ width: widthFor(i), minHeight: `calc((100% - ${(n - 1) * 0.6}cqh) / ${n})`, background: outline ? 'transparent' : shade(i), border: outline ? `0.18cqw solid ${theme.primary}` : 'none', clipPath: variant === 'funnel' ? 'polygon(0 0, 100% 0, 92% 100%, 8% 100%)' : variant === 'pyramid' ? undefined : undefined, borderRadius: variant === 'funnel' ? 0 : '0.5cqw', display: 'grid', placeItems: 'center', color: outline ? resolved.text : (i < n / 2 && !resolved.isInverse ? '#fff' : resolved.text), ...TYPE.caption, fontSize: '1.1cqw' }}>
              <EText path={`steps.${i}.label`} value={st.label} fallback={String(i + 1)} placeholder={String(i + 1)} style={{ textAlign: 'center' }} />
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '1.2cqh' }}>
          {steps.map((st, i) => (
            <ECard key={i} listPath="steps" index={i} style={{ display: 'flex', gap: '1cqw', alignItems: 'flex-start' }}>
              <div style={{ width: '1.2cqw', height: '1.2cqw', borderRadius: '50%', background: outline ? theme.primary : shade(i), border: outline ? 'none' : `0.1cqw solid ${theme.borderStrong}`, marginTop: '0.6cqh', flexShrink: 0 }} />
              <div style={{ minWidth: 0 }}>
                <EText path={`steps.${i}.title`} value={st.title} placeholder="Stage" style={{ ...TYPE.title, fontSize: '1.7cqw', color: resolved.text }} />
                <EText path={`steps.${i}.body`} value={st.body} placeholder="Add detail" multiline style={{ ...TYPE.bodySmall, color: resolved.textMuted }} />
              </div>
            </ECard>
          ))}
          <EAddCard listPath="steps" count={steps.length} max={6} label="Add step" blank={() => ({ label: String(steps.length + 1), title: 'Stage', body: '' })} />
        </div>
      </div>
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

function mixWithWhite(hex: string, t: number): string {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const ch = (i: number) => parseInt(full.slice(i, i + 2), 16);
  const mix = (v: number) => Math.round(255 - (255 - v) * t);
  return `rgb(${mix(ch(0))}, ${mix(ch(2))}, ${mix(ch(4))})`;
}

// ─── 18. Table ──────────────────────────────────────────────────────────────

/** A table cell: plain editable text, or a status chip around it / a traffic-light dot when the text is a status. */
function StatusCell({ path, value }: { path: string; value: unknown }) {
  const edit = useContext(SlideEditContext);
  const st = cellStatus(value);
  if (!st) return <EText path={path} value={value} placeholder="—" tag="span" style={{ display: 'inline-block', minWidth: '3cqw' }} />;
  const col = STATUS_COLORS[st.tone];
  if (st.kind === 'dot') {
    const next: Record<string, string> = { green: 'amber', amber: 'red', red: 'grey', grey: 'green' };
    return (
      <span
        role={edit.editable ? 'button' : undefined}
        title={edit.editable ? `${st.tone} — click to change` : st.tone}
        onClick={edit.editable ? (e) => { e.stopPropagation(); edit.update((sl) => setPath(sl, path, `status:${next[st.tone]}`)); } : undefined}
        style={{ display: 'inline-block', width: '1.4cqw', height: '1.4cqw', borderRadius: '50%', background: col.dot, verticalAlign: 'middle', cursor: edit.editable ? 'pointer' : undefined }}
      />
    );
  }
  return (
    <span style={{ display: 'inline-block', padding: '0.4cqh 0.9cqw', borderRadius: 999, background: col.bg, color: col.text, fontWeight: 700, fontSize: '0.95em', whiteSpace: 'nowrap' }}>
      <EText path={path} value={value} placeholder="—" tag="span" style={{ display: 'inline-block', color: col.text }} />
    </span>
  );
}

function TableSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const edit = useEdit();
  if (slide.variant === 'matrix') return <TableMatrixSlide slide={slide} theme={theme} />;
  if (slide.variant === 'glossary') return <TableGlossarySlide slide={slide} theme={theme} />;
  const resolved = resolveTheme(theme, slide.theme ?? 'light');
  const eyebrowColor = resolved.isInverse ? 'rgba(255,255,255,0.85)' : theme.primary;
  const headers: string[] = slide.table?.headers ?? [];
  const rows: any[][] = slide.table?.rows ?? [];
  const total: any[] | null = Array.isArray(slide.table?.total) ? slide.table.total : null;
  // Row height and type shrink with the row count to fit the content area (tableFit: same rule as the PDF and PPTX export).
  const fit = tableLayout(slide.table || { headers: [], rows: [] });
  const cols = fit.cols;
  const body = Math.round(TABLE_FIT.BODY * fit.fontScale * 1000) / 1000;
  const head = Math.min(TABLE_FIT.HEAD, body);
  // One line of type plus the fitted padding makes a row; each row's rule comes out of its bottom padding (the
  // thicker rule over the totals row out of its top), so the rows add up to exactly what the fit counted.
  const pad = (ci: number, first = false, ruleTop = 0, ruleBottom = 1): CSSProperties => ({ padding: `calc(${fit.pad}cqh - ${ruleTop}px) ${ci === cols - 1 ? 0 : '1cqw'} calc(${fit.pad}cqh - ${ruleBottom}px) ${fit.numeric[ci] || first ? '1cqw' : 0}`, lineHeight: TABLE_FIT.LINE });
  // Figures right-aligned on one line.
  const figure = (ci: number): CSSProperties => (fit.numeric[ci] ? { textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' } : {});
  // Two-level headers ("2020" over two scenario columns): groups = [{ label, span }] read left to right over the header row.
  const groups: Array<{ label?: string; span?: number }> = Array.isArray(slide.table?.groups) ? slide.table.groups : [];
  // Rows to set in bold on a tint (subtotals, the line the page is about); "Total"-led rows and the totals row count by themselves.
  const emphasis = new Set<number>([...(Array.isArray(slide.table?.emphasis) ? slide.table.emphasis.map(Number) : []), ...rows.map((r, i) => (/^total\b/i.test(String(typeof r[0] === 'object' ? r[0]?.text ?? '' : r[0] ?? '')) ? i : -1)).filter((i) => i >= 0), ...(fit.totalIndex >= 0 ? [fit.totalIndex] : [])]);
  const mut = (fn: (t: { headers: string[]; rows: any[][]; total?: any[] }) => void) => edit.update((s) => { const t = { ...(s.table || {}), headers: [...(s.table?.headers || [])], rows: (s.table?.rows || []).map((r: any[]) => [...r]), ...(Array.isArray(s.table?.total) ? { total: [...s.table.total] } : {}) }; fn(t); return { ...s, table: t }; });
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <SlideHeader slide={slide} eyebrowColor={eyebrowColor} resolved={resolved} />
      <div style={{ position: 'absolute', left: GUTTER, right: GUTTER, bottom: `${TABLE_FIT.BOTTOM}cqh`, top: `${TABLE_FIT.TOP}cqh`, overflow: 'hidden' }}>
        <table data-font-scale={fit.fontScale} data-row-h={fit.rowH} style={{ width: '100%', borderCollapse: 'collapse', ...TYPE.body, fontSize: `${body}cqw`, lineHeight: TABLE_FIT.LINE }}>
          <thead>
            {groups.length > 0 && (
              <tr>
                {groups.map((g, gi) => (
                  <th key={gi} colSpan={Math.max(1, Number(g.span) || 1)} style={{ ...TYPE.caption, fontSize: `${head}cqw`, color: resolved.text, textAlign: 'left', padding: `${fit.pad}cqh 1cqw calc(${fit.pad}cqh - 1px) 0`, lineHeight: TABLE_FIT.LINE, borderBottom: g.label ? `1px solid ${theme.borderStrong}` : 'none' }}>
                    <EText path={`table.groups.${gi}.label`} value={g.label} placeholder="" tag="span" style={{ display: 'inline-block' }} />
                  </th>
                ))}
                {edit.editable && <th />}
              </tr>
            )}
            <tr>
              {Array.from({ length: cols }).map((_, ci) => (
                <th key={ci} style={{ ...TYPE.caption, fontSize: `${head}cqw`, color: resolved.textMuted, textAlign: fit.numeric[ci] ? 'right' : 'left', ...pad(ci, false, 0, 1.5), borderBottom: `1.5px solid ${theme.borderStrong}` }}>
                  <EText path={`table.headers.${ci}`} value={headers[ci]} placeholder="Header" tag="span" style={{ display: 'inline-block', minWidth: '4cqw' }} />
                </th>
              ))}
              {edit.editable && <th style={{ width: '4cqw', padding: 0 }}><span data-eadd="" onClick={() => mut((t) => { t.headers.push('Header'); t.rows.forEach((r) => r.push('')); if (t.total) t.total.push(''); })} style={{ cursor: 'pointer', color: '#7C3AED', fontSize: `${head}cqw`, fontWeight: 600 }}>+ col</span></th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => {
              const em = emphasis.has(ri); const isTotal = ri === fit.totalIndex;
              return (
                <tr key={ri} data-ecard="" style={em ? { background: resolved.isInverse ? 'rgba(255,255,255,0.08)' : theme.primarySoft } : undefined}>
                  {Array.from({ length: cols }).map((_, ci) => (
                    <td key={ci} style={{ ...pad(ci, em && ci === 0, isTotal ? 0.5 : 0), ...figure(ci), borderBottom: `1px solid ${theme.border}`, borderTop: isTotal ? `1.5px solid ${theme.borderStrong}` : undefined, color: ci === 0 || em ? resolved.text : resolved.textMuted, fontWeight: ci === 0 || em ? 700 : 400, verticalAlign: 'top' }}>
                      {row[ci] && typeof row[ci] === 'object' ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.7cqw' }}>
                          <Avatar url={(row[ci] as any).avatarUrl} name={(row[ci] as any).text} size={`${Math.round(body * TABLE_FIT.LINE * 1000) / 1000}cqw`} color={theme.primary} />
                          <EText path={`table.rows.${ri}.${ci}.text`} value={(row[ci] as any).text} placeholder="—" tag="span" style={{ display: 'inline-block', minWidth: '3cqw' }} />
                        </span>
                      ) : (
                        <StatusCell path={`table.rows.${ri}.${ci}`} value={row[ci]} />
                      )}
                    </td>
                  ))}
                  {/* The delete button is one line of type tall, so editing never makes a row taller than the fit. */}
                  {edit.editable && <td style={{ position: 'relative', padding: 0, verticalAlign: 'middle' }}><button type="button" data-ecard-x="" onMouseDown={(e) => e.preventDefault()} onClick={() => mut((t) => { t.rows.splice(ri, 1); })} style={{ display: 'block', width: `${Math.min(1.8, body * TABLE_FIT.LINE)}cqw`, height: `${Math.min(1.8, body * TABLE_FIT.LINE)}cqw`, padding: 0, borderRadius: '50%', border: 'none', background: 'rgba(180,35,24,0.9)', color: '#fff', fontSize: `${body * 0.7}cqw`, lineHeight: 1, cursor: 'pointer' }}>×</button></td>}
                </tr>
              );
            })}
          </tbody>
          {total && (
            <tfoot>
              <tr>
                {Array.from({ length: cols }).map((_, ci) => (
                  <td key={ci} style={{ padding: `${fit.pad}cqh ${fit.numeric[ci] ? '0.8cqw' : '1cqw'} ${fit.pad}cqh 0.8cqw`, lineHeight: TABLE_FIT.LINE, ...figure(ci), background: resolved.isInverse ? 'rgba(255,255,255,0.1)' : theme.primarySoft, color: resolved.isInverse ? resolved.text : theme.primary, fontWeight: 700, verticalAlign: 'top', borderRadius: ci === 0 ? '0.6cqw 0 0 0.6cqw' : ci === cols - 1 ? '0 0.6cqw 0.6cqw 0' : 0 }}>
                    <EText path={`table.total.${ci}`} value={total[ci]} placeholder={ci === 0 ? 'Total' : '—'} tag="span" style={{ display: 'inline-block', minWidth: '3cqw' }} />
                  </td>
                ))}
                {edit.editable && <td />}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {edit.editable && (
        // Editor chrome in the footer band (never exported): add a row, and — when the rows do not fit even at the
        // smallest type — how many do and the split into continuation pages the PDF / PPTX export would make anyway.
        <div style={{ position: 'absolute', left: GUTTER, bottom: '7cqh', display: 'flex', alignItems: 'center', gap: '1.2cqw', ...TYPE.bodySmall }}>
          <span data-eadd="" onClick={() => mut((t) => { t.rows.push(Array.from({ length: Math.max(cols, 1) }, () => '')); })} style={{ cursor: 'pointer', color: '#7C3AED', fontWeight: 600 }}>+ Add row</span>
          {!fit.fits && (
            <span data-table-overflow="" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.8cqw', padding: '0.5cqh 0.5cqw 0.5cqh 1cqw', borderRadius: 999, background: '#FEF0C7', color: '#93370D', fontSize: '1.05cqw' }}>
              {fit.maxRows} rows fit at most, split into {fit.pages} pages
              {edit.replaceWith && <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => edit.replaceWith!(splitTableSlide(slide, edit.language))} style={{ border: 'none', borderRadius: 999, padding: '0.4cqh 0.9cqw', background: '#93370D', color: '#fff', fontSize: '1cqw', fontWeight: 600, cursor: 'pointer' }}>Split into pages</button>}
            </span>
          )}
        </div>
      )}
      <Footnote slide={slide} resolved={resolved} />
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

// ─── 19. Embed (YouTube / web page) ─────────────────────────────────────────

function embedSrc(url: string): { src: string; kind: 'youtube' | 'web' } {
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  if (yt) return { src: `https://www.youtube.com/embed/${yt[1]}`, kind: 'youtube' };
  return { src: url, kind: 'web' };
}

function EmbedSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const resolved = resolveTheme(theme, slide.theme ?? 'light');
  const edit = useEdit();
  const eyebrowColor = resolved.isInverse ? 'rgba(255,255,255,0.85)' : theme.primary;
  const url: string = slide.url || '';
  const hasHeader = !!(slide.title || slide.eyebrow) || edit.editable;
  const e = url ? embedSrc(url) : null;
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <SlideHeader slide={slide} eyebrowColor={eyebrowColor} resolved={resolved} />
      <div style={{ position: 'absolute', left: GUTTER, right: GUTTER, top: hasHeader ? '34cqh' : '14cqh', bottom: edit.editable ? '17cqh' : '11cqh', borderRadius: '1cqw', overflow: 'hidden', background: resolved.isInverse ? 'rgba(255,255,255,0.08)' : theme.surface }}>
        {e ? (
          <iframe src={e.src} title={slide.title || 'Embed'} style={{ width: '100%', height: '100%', border: 0 }} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', color: resolved.textMuted, ...TYPE.body }}>Paste a YouTube or web page link below</div>
        )}
      </div>
      {edit.editable && (
        <div style={{ position: 'absolute', left: GUTTER, right: GUTTER, bottom: '10cqh', display: 'flex', gap: '0.6cqw', alignItems: 'center', ...TYPE.bodySmall, color: resolved.textMuted }}>
          <span style={{ ...TYPE.caption, color: theme.primary }}>URL</span>
          <EText path="url" value={slide.url} placeholder="https://www.youtube.com/watch?v=… or https://…" style={{ flex: 1, color: resolved.text }} />
        </div>
      )}
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

// ─── Campaign Results layouts ────────────────────────────────────────────────
// The client app's Results-page export compositions, drawn with the deck theme and editable in
// place. Mirrors backend/src/engine/pdfRenderer.ts (coverReportSlide … closingAboutSlide).

const U = (px: number) => `${Math.round((px / 12.8) * 1000) / 1000}cqw`;
function luminance(hex: string): number {
  const h = hex.replace('#', ''); if (h.length < 6) return 0.5;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
type RsTone = 'brand' | 'accent' | 'soft' | 'light';
function rsTone(t: Theme, tone: RsTone | undefined) {
  switch (tone) {
    case 'brand': return { bg: t.primary, border: 'transparent', label: 'rgba(255,255,255,0.65)', value: '#fff', sub: 'rgba(255,255,255,0.55)' };
    case 'accent': { const dark = luminance(t.accent) > 0.6; return { bg: t.accent, border: 'transparent', label: dark ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.7)', value: dark ? t.text : '#fff', sub: dark ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.6)' }; }
    case 'soft': return { bg: t.primarySoft, border: t.primaryTint, label: t.primary, value: t.primary, sub: t.textMuted };
    default: return { bg: t.bgSoft, border: t.border, label: t.textMuted, value: t.text, sub: t.textSubtle };
  }
}
const rsLabel = (px: number, color: string, tracking = '0.16em'): CSSProperties => ({ fontSize: U(px), fontWeight: 600, letterSpacing: tracking, textTransform: 'uppercase', lineHeight: 1.3, color });
const rsPalette = (t: Theme) => [t.primary, t.accent, mixWithWhite(t.primary, 0.55), '#F59E0B', '#10B981', '#EC4899'];

function RsTile({ theme, m, path, size = 'card', tone, pad, listPath, index }: { theme: Theme; m: any; path: string; size?: 'hero' | 'card' | 'small'; tone?: RsTone; pad?: number; listPath?: string; index?: number }) {
  const s = rsTone(theme, m?.tone || tone);
  const vpx = size === 'hero' ? 32 : size === 'small' ? 16 : 22;
  const inner = (
    <>
      <EText path={`${path}.label`} value={m?.label} placeholder="Label" style={rsLabel(size === 'small' ? 9 : 10, s.label, '0.14em')} />
      <EText path={`${path}.value`} value={m?.value} fallback="—" placeholder="0" style={{ marginTop: U(4), fontSize: U(vpx), fontWeight: size === 'hero' ? 700 : 600, lineHeight: 1.1, color: s.value, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }} />
      <EText path={`${path}.caption`} value={m?.caption} placeholder="Add caption" style={{ marginTop: U(3), fontSize: U(10), lineHeight: 1.35, color: s.sub }} />
    </>
  );
  const style: CSSProperties = { borderRadius: U(size === 'hero' ? 16 : 12), padding: U(pad ?? (size === 'hero' ? 20 : 12)), display: 'flex', flexDirection: 'column', background: s.bg, border: `1px solid ${s.border}`, minWidth: 0 };
  return listPath != null && index != null ? <ECard listPath={listPath} index={index} style={style}>{inner}</ECard> : <div style={style}>{inner}</div>;
}
function RsCard({ theme, children, style }: { theme: Theme; children: ReactNode; style?: CSSProperties }) {
  return <div style={{ borderRadius: U(16), background: theme.bg, border: `1px solid ${theme.border}`, ...style }}>{children}</div>;
}
function RsBar({ pct, color, track, h = 6 }: { pct: number; color: string; track: string; h?: number }) {
  return <div style={{ flex: 1, height: U(h), borderRadius: 999, overflow: 'hidden', background: track, minWidth: 0 }}><div style={{ width: `${Math.max(0, Math.min(100, pct))}%`, height: '100%', borderRadius: 999, background: color }} /></div>;
}
/** Results-page shell: logo top-left, brand mark bottom-right, eyebrow + title header, body 64px in from the edges. */
function RsShell({ slide, theme, children, bodyTop, hideHeader }: { slide: any; theme: Theme; children: ReactNode; bodyTop?: number; hideHeader?: boolean }) {
  const resolved = resolveTheme(theme, 'light');
  const edit = useEdit();
  const hasHeader = !hideHeader && (!!(slide.title || slide.eyebrow) || edit.editable);
  const top = bodyTop ?? (hasHeader ? 152 : 56);
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={false} />
      {hasHeader && (
        <div style={{ position: 'absolute', left: U(64), right: U(64), top: U(96), height: U(40), display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: U(4) }}>
          <EText path="eyebrow" value={slide.eyebrow} placeholder="EYEBROW" style={rsLabel(11, theme.primary, '0.18em')} />
          <EText path="title" value={slide.title} placeholder="Title" tag="h2" style={{ margin: 0, fontSize: U(28), fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.01em', color: resolved.text }} />
        </div>
      )}
      <div style={{ position: 'absolute', left: U(64), right: U(64), top: U(top), bottom: U(112), display: 'flex', flexDirection: 'column', minHeight: 0 }}>{children}</div>
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

function CoverReportSlide({ slide, theme, title }: { slide: any; theme: Theme; title: string }) {
  const resolved = resolveTheme(theme, 'light'); const dark = resolveTheme(theme, 'dark');
  const p = slide.panel || {}; const stats: any[] = (slide.stats || []).slice(0, 4);
  const media = slide.media ? { ...slide.media, frame: slide.media.frame ?? 'phone' } : slide.media;
  const edit = useEdit();
  const long = String(slide.title || '').length > 40;
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: U(400), background: dark.bg, padding: `${U(48)} ${U(44)}`, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', right: U(-20), bottom: U(-30), fontSize: U(260), fontWeight: 900, lineHeight: 1, letterSpacing: '-0.06em', color: 'rgba(255,255,255,0.04)' }}>{String(p.brand || 'B').charAt(0).toUpperCase()}</div>
        <div style={{ position: 'relative', height: U(28) }}>{dark.logoUrl && <LogoImg inverse src={dark.logoUrl} style={{ maxHeight: U(28), maxWidth: U(200), objectFit: 'contain', objectPosition: 'left center' }} />}</div>
        <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: U(12) }}>
          <EText path="eyebrow" value={slide.eyebrow} placeholder="CAMPAIGN REPORT" style={rsLabel(10, 'rgba(255,255,255,0.7)', '0.28em')} />
          <EText path="panel.brand" value={p.brand} placeholder="Brand" style={{ fontSize: U(14), fontWeight: 600, color: 'rgba(255,255,255,0.9)' }} />
          <EText path="panel.period" value={p.period} placeholder="Period" style={{ fontSize: U(12), color: 'rgba(255,255,255,0.5)' }} />
          <EText path="panel.market" value={p.market} placeholder="Market" style={{ fontSize: U(12), color: 'rgba(255,255,255,0.5)' }} />
        </div>
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: U(4), background: `linear-gradient(90deg, ${theme.primary} 0%, ${theme.accent} 100%)` }} />
      </div>
      <div style={{ position: 'absolute', left: U(400), right: 0, top: 0, bottom: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', padding: `${U(40)} ${U(56)}`, gap: U(32), minHeight: 0 }}>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: U(16) }}>
            <EText path="title" value={slide.title} fallback={title} placeholder="Campaign name" tag="h1" style={{ margin: 0, fontSize: U(long ? 42 : 54), fontWeight: 700, lineHeight: 1, letterSpacing: '-0.025em', color: resolved.text, maxWidth: U(media?.url || edit.editable ? 520 : 720) }} />
            <EText path="subtitle" value={slide.subtitle} placeholder="Subtitle" style={{ fontSize: U(15), color: resolved.textMuted }} />
          </div>
          {(media?.url || media?.posterUrl || edit.editable) && <div style={{ height: U(420), width: U(240), flexShrink: 0 }}><EMedia path="media" media={media} style={{ width: '100%', height: '100%' }} frameRadius="2cqw" placeholder="Add the hero post" /></div>}
        </div>
        {(stats.length > 0 || edit.editable) && (
          <div style={{ display: 'flex', flexShrink: 0, borderTop: `1px solid ${theme.border}` }}>
            {stats.map((st, i) => (
              <ECard key={i} listPath="stats" index={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: `${U(20)} ${U(32)}`, borderRight: i < stats.length - 1 ? `1px solid ${theme.border}` : 'none' }}>
                <EText path={`stats.${i}.label`} value={st.label} placeholder="LABEL" style={rsLabel(10, theme.textSubtle, '0.18em')} />
                <EText path={`stats.${i}.value`} value={st.value} placeholder="0" style={{ marginTop: U(2), fontSize: U(22), fontWeight: 700, color: resolved.text, fontVariantNumeric: 'tabular-nums' }} />
              </ECard>
            ))}
            <EAddCard listPath="stats" count={stats.length} max={4} label="Add stat" blank={() => ({ label: 'Stat', value: '0' })} style={{ flex: 1, minHeight: U(70) }} />
          </div>
        )}
      </div>
    </div>
  );
}

function MetricsGlanceSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const resolved = resolveTheme(theme, 'light');
  const hero = slide.hero || {}; const cards: any[] = (slide.cards || []).slice(0, 2); const tiles: any[] = (slide.tiles || []).slice(0, 3);
  const ctx: any[] = slide.context || []; const platforms: string[] = slide.platforms || [];
  const h = rsTone(theme, 'brand');
  return (
    <RsShell slide={slide} theme={theme}>
      <div style={{ display: 'flex', flex: 1, gap: U(24), minHeight: 0 }}>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: U(16) }}>
          <div style={{ flex: 1, borderRadius: U(16), background: h.bg, padding: U(20), display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <EText path="hero.label" value={hero.label} placeholder="LABEL" style={rsLabel(11, h.label, '0.18em')} />
            <EText path="hero.value" value={hero.value} fallback="—" placeholder="0" style={{ fontSize: U(52), fontWeight: 700, lineHeight: 1, color: h.value, fontVariantNumeric: 'tabular-nums' }} />
            <EText path="hero.caption" value={hero.caption} placeholder="Caption" style={{ fontSize: U(12), color: h.sub }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: U(16) }}>
            {cards.map((c, i) => <RsTile key={i} theme={theme} m={c} path={`cards.${i}`} size="hero" tone="light" listPath="cards" index={i} />)}
          </div>
        </div>
        <div style={{ width: U(370), flexShrink: 0, display: 'flex', flexDirection: 'column', gap: U(12) }}>
          <RsCard theme={theme} style={{ padding: U(16) }}>
            {ctx.map((c, i) => (
              <div key={i} style={{ fontSize: U(12), marginBottom: U(6), color: resolved.text, display: 'flex', gap: U(8) }}>
                <EText path={`context.${i}.label`} value={c.label} placeholder="Label" tag="span" style={{ fontWeight: 600 }} />
                <EText path={`context.${i}.value`} value={c.value} placeholder="Value" tag="span" style={{ color: resolved.textMuted }} />
              </div>
            ))}
            {platforms.length > 0 && <div style={{ display: 'flex', flexWrap: 'wrap', gap: U(6), marginTop: U(8) }}>{platforms.map((p, i) => <span key={i} style={{ display: 'inline-flex', alignItems: 'center', borderRadius: 999, padding: `${U(4)} ${U(10)}`, fontSize: U(11), fontWeight: 500, background: theme.primarySoft, color: theme.primary, border: `1px solid ${theme.primaryTint}` }}>{p}</span>)}</div>}
          </RsCard>
          {tiles.length > 0 && <div style={{ display: 'grid', gridTemplateColumns: `repeat(${tiles.length}, 1fr)`, gap: U(8) }}>{tiles.map((m, i) => <RsTile key={i} theme={theme} m={m} path={`tiles.${i}`} tone={i === 0 ? 'soft' : 'light'} listPath="tiles" index={i} />)}</div>}
          {slide.mix && (
            <RsCard theme={theme} style={{ padding: U(16) }}>
              <EText path="mix.label" value={slide.mix.label} placeholder="Channel mix" style={{ fontSize: U(11), fontWeight: 600, marginBottom: U(8), color: resolved.text }} />
              <div style={{ position: 'relative', height: U(10), borderRadius: 999, overflow: 'hidden', background: theme.surface }}><div style={{ position: 'absolute', left: 0, top: 0, height: '100%', borderRadius: 999, width: `${Number(slide.mix.pct) || 0}%`, background: theme.primary }} /></div>
              <div style={{ marginTop: U(8), display: 'flex', justifyContent: 'space-between', fontSize: U(11), color: resolved.textMuted }}>
                <EText path="mix.left" value={slide.mix.left} placeholder="Organic 0%" tag="span" />
                <EText path="mix.right" value={slide.mix.right} placeholder="Paid 0%" tag="span" />
              </div>
            </RsCard>
          )}
          {slide.extra && <RsTile theme={theme} m={slide.extra} path="extra" tone="light" />}
        </div>
      </div>
    </RsShell>
  );
}

function ChartOverviewSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const resolved = resolveTheme(theme, 'light');
  const panels: any[] = (slide.panels || []).slice(0, 5);
  const pal = rsPalette(theme);
  const n = panels.length;
  const cols = n >= 5 ? '1.1fr 1fr 0.8fr' : n === 4 ? '1fr 1fr' : `repeat(${Math.max(n, 1)}, 1fr)`;
  const cell = (p: any, i: number, span?: string) => (
    <div key={i} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, borderRadius: U(16), background: theme.bg, border: `1px solid ${theme.border}`, padding: `${U(14)} ${U(16)}`, gridColumn: span }}>
      <EText path={`panels.${i}.title`} value={p.title} placeholder="Chart title" style={{ fontSize: U(13), fontWeight: 600, color: resolved.text }} />
      <EText path={`panels.${i}.subtitle`} value={p.subtitle} placeholder="Subtitle" style={{ fontSize: U(10), color: resolved.textMuted, marginBottom: U(8) }} />
      {p.waterfall ? (() => {
        const w = p.waterfall; const steps: any[] = w.steps || []; const max = Math.max(...steps.map((x: any) => Number(x.bottom) + Number(x.height)), 1);
        return (
          <>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: U(8) }}><span style={rsLabel(9, resolved.textMuted, '0.14em')}>{w.label}</span><EText path={`panels.${i}.waterfall.value`} value={w.value} tag="span" style={{ fontSize: U(20), fontWeight: 700, color: resolved.text }} /></div>
            <EText path={`panels.${i}.waterfall.sub`} value={w.sub} style={{ fontSize: U(10), color: resolved.textMuted, marginBottom: U(6) }} />
            <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: U(10), minHeight: U(70), borderBottom: `1px solid ${theme.border}`, paddingBottom: U(2) }}>
              {steps.map((x: any, j: number) => <div key={j} style={{ flex: 1, position: 'relative', height: '100%' }}><div style={{ position: 'absolute', left: '12%', right: '12%', bottom: `${(Number(x.bottom) / max) * 100}%`, height: `${Math.max((Number(x.height) / max) * 100, 1.5)}%`, background: x.total ? (j === 0 ? theme.primary : theme.accent) : mixWithWhite(theme.primary, 0.5), borderRadius: `${U(3)} ${U(3)} 0 0` }} /></div>)}
            </div>
            <div style={{ display: 'flex', gap: U(10), marginTop: U(4) }}>{steps.map((x: any, j: number) => <div key={j} style={{ flex: 1, textAlign: 'center', fontSize: U(9), color: resolved.textMuted }}>{x.label}</div>)}</div>
          </>
        );
      })() : (
        <div style={{ flex: 1, minHeight: 0 }} dangerouslySetInnerHTML={{ __html: chartSvg(p.chart || {}, { primary: theme.primary, accent: theme.accent, text: resolved.text, textMuted: resolved.textMuted, grid: theme.border, palette: pal }).replace('<svg ', '<svg style="width:100%;height:100%" ') }} />
      )}
    </div>
  );
  return (
    <RsShell slide={slide} theme={theme}>
      <div style={{ display: 'grid', gridTemplateColumns: cols, gridTemplateRows: n > 3 ? '1fr 1fr' : '1fr', gap: U(12), flex: 1, minHeight: 0 }}>
        {n >= 5 ? [cell(panels[0], 0), cell(panels[1], 1), cell(panels[2], 2), cell(panels[3], 3), cell(panels[4], 4, '2 / span 2')] : panels.map((p, i) => cell(p, i))}
      </div>
      {slide.footer && <div style={{ fontSize: U(10), color: resolved.textMuted, marginTop: U(8), display: 'flex', justifyContent: 'space-between' }}><EText path="footer.left" value={slide.footer.left} tag="span" /><EText path="footer.right" value={slide.footer.right} tag="span" /></div>}
    </RsShell>
  );
}

function BoxesAudienceSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const resolved = resolveTheme(theme, 'light');
  const pal = rsPalette(theme);
  const stats: any[] = (slide.stats || []).slice(0, 3);
  const g = slide.gender || {}; const female = Number(g.female) || 0, male = Number(g.male) || 0; const tot = female + male || 1; const fPct = Math.round((female / tot) * 100);
  const ages: any[] = (slide.ages || []).slice(0, 6); const countries: any[] = (slide.countries || []).slice(0, 6); const maxC = Math.max(...countries.map((c) => Number(c.pct) || 0), 0.001);
  const interests: string[] = (slide.interests || []).slice(0, 14); const langs: any[] = (slide.languages || []).slice(0, 5);
  const h4 = (text: string) => <div style={rsLabel(11, resolved.textMuted)}>{text}</div>;
  const cardStyle: CSSProperties = { padding: U(16), display: 'flex', flexDirection: 'column', gap: U(12), minHeight: 0 };
  return (
    <RsShell slide={slide} theme={theme}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: U(16), flex: 1, minHeight: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: U(8) }}>
          <span style={{ display: 'inline-flex', borderRadius: 999, padding: `${U(4)} ${U(10)}`, fontSize: U(11), fontWeight: 600, background: theme.primarySoft, color: theme.primary, border: `1px solid ${theme.primaryTint}` }}><EText path="badge" value={slide.badge} fallback="Organic creators only" tag="span" /></span>
          <EText path="note" value={slide.note} placeholder="Note" tag="span" style={{ fontSize: U(11), color: resolved.textMuted }} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: U(12) }}>{stats.map((m, i) => <RsTile key={i} theme={theme} m={m} path={`stats.${i}`} size="hero" pad={16} tone={i === 0 ? 'brand' : i === 2 ? 'soft' : 'light'} listPath="stats" index={i} />)}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: U(16), flex: 1, minHeight: 0 }}>
          <RsCard theme={theme} style={cardStyle}>
            {h4('Gender & age')}
            {female + male > 0 && <div><div style={{ display: 'flex', height: U(10), borderRadius: 999, overflow: 'hidden' }}><div style={{ width: `${fPct}%`, background: theme.primary }} /><div style={{ width: `${100 - fPct}%`, background: theme.accent }} /></div><div style={{ marginTop: U(6), display: 'flex', justifyContent: 'space-between', fontSize: U(11), color: resolved.textMuted }}><span>♀ Female {fPct}%</span><span>♂ Male {100 - fPct}%</span></div></div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: U(6) }}>{ages.map((b, i) => <div key={i} style={{ display: 'flex', alignItems: 'center', gap: U(8) }}><span style={{ width: U(40), flexShrink: 0, fontSize: U(11), fontWeight: 500, color: resolved.text }}>{b.label}</span><RsBar pct={Math.min(100, (Number(b.pct) || 0) * 100)} color={pal[i % pal.length]} track={theme.surface} /><span style={{ width: U(32), textAlign: 'right', fontSize: U(10), color: resolved.textMuted }}>{((Number(b.pct) || 0) * 100).toFixed(0)}%</span></div>)}</div>
          </RsCard>
          <RsCard theme={theme} style={cardStyle}>
            {h4('Top countries')}
            <div style={{ display: 'flex', flexDirection: 'column', gap: U(8) }}>{countries.map((c, i) => <div key={i} style={{ display: 'flex', alignItems: 'center', gap: U(8) }}><span style={{ width: U(80), flexShrink: 0, fontSize: U(12), fontWeight: 500, color: resolved.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.label}</span><RsBar pct={((Number(c.pct) || 0) / maxC) * 100} color={theme.primary} track={theme.surface} /><span style={{ width: U(40), textAlign: 'right', fontSize: U(11), fontWeight: 600, color: resolved.text }}>{((Number(c.pct) || 0) * 100).toFixed(1)}%</span></div>)}</div>
          </RsCard>
          <RsCard theme={theme} style={cardStyle}>
            {h4('Audience interests')}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: U(6) }}>{interests.map((it, i) => <span key={i} style={{ display: 'inline-block', borderRadius: 999, padding: `${U(4)} ${U(10)}`, fontSize: U(11), fontWeight: 500, background: theme.primarySoft, color: resolved.text, border: `1px solid ${theme.primaryTint}` }}>{it}</span>)}</div>
            {langs.length > 0 && <div style={{ marginTop: 'auto', paddingTop: U(8), borderTop: `1px solid ${theme.border}` }}><div style={{ ...rsLabel(10, resolved.textMuted), marginBottom: U(6) }}>Languages</div><div style={{ display: 'flex', flexWrap: 'wrap', gap: `${U(4)} ${U(12)}` }}>{langs.map((l, i) => <span key={i} style={{ fontSize: U(11), color: resolved.text }}><span style={{ fontWeight: 500 }}>{l.label}</span> <span style={{ color: resolved.textMuted }}>{((Number(l.pct) || 0) * 100).toFixed(0)}%</span></span>)}</div></div>}
          </RsCard>
        </div>
      </div>
    </RsShell>
  );
}

function MetricsChannelSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const resolved = resolveTheme(theme, 'light');
  const pal = rsPalette(theme);
  const primary: any[] = (slide.primary || []).slice(0, 4); const secondary: any[] = (slide.secondary || []).slice(0, 4);
  const side = slide.side || {}; const creators: any[] = side.creators || []; const rows: any[] = side.rows || [];
  const maxV = Math.max(...creators.map((c) => Number(c.pct) || 0), 1);
  const f = slide.funnel; const steps: any[] = f && Array.isArray(f.steps) ? f.steps : []; const top = Math.max(...steps.map((x) => Number(x.raw) || 0), 1);
  const tones: RsTone[] = ['brand', 'light', 'accent', 'soft'];
  return (
    <RsShell slide={slide} theme={theme}>
      <div style={{ display: 'flex', flex: 1, gap: U(24), minHeight: 0 }}>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: U(16) }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: U(12) }}>{primary.map((m, i) => <RsTile key={i} theme={theme} m={m} path={`primary.${i}`} tone={tones[i]} listPath="primary" index={i} />)}</div>
          {secondary.length > 0 && <div style={{ display: 'grid', gridTemplateColumns: `repeat(${secondary.length}, 1fr)`, gap: U(8) }}>{secondary.map((m, i) => <RsTile key={i} theme={theme} m={m} path={`secondary.${i}`} size="small" tone="light" listPath="secondary" index={i} />)}</div>}
          {steps.length > 0 && (
            <RsCard theme={theme} style={{ padding: U(16), display: 'flex', flexDirection: 'column', gap: U(8) }}>
              <EText path="funnel.title" value={f.title} fallback="Video completion" style={rsLabel(11, resolved.textMuted)} />
              {steps.map((x, i) => <div key={i} style={{ display: 'flex', alignItems: 'center', gap: U(8) }}><span style={{ width: U(40), flexShrink: 0, fontSize: U(11), fontWeight: 600, color: resolved.textMuted }}>{x.label}</span><RsBar pct={Math.max(((Number(x.raw) || 0) / top) * 100, Number(x.raw) > 0 ? 2 : 0)} color={pal[i % pal.length]} track={theme.surface} h={10} /><span style={{ width: U(56), flexShrink: 0, textAlign: 'right', fontSize: U(11), fontWeight: 600, color: resolved.text }}>{x.value}</span><span style={{ width: U(44), flexShrink: 0, textAlign: 'right', fontSize: U(10), color: theme.textSubtle }}>{x.note}</span></div>)}
              <EText path="funnel.note" value={f.note} placeholder="Note" style={{ fontSize: U(10), color: theme.textSubtle }} />
            </RsCard>
          )}
        </div>
        <RsCard theme={theme} style={{ width: U(400), flexShrink: 0, padding: U(20), display: 'flex', flexDirection: 'column' }}>
          {creators.length ? (
            <>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: U(16) }}><EText path="side.title" value={side.title} fallback="Top creators" style={rsLabel(11, resolved.textMuted)} /><span style={{ fontSize: U(10), color: theme.textSubtle }}>{side.hint || 'by impressions'}</span></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: U(12) }}>
                {creators.slice(0, 6).map((c, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: U(12) }}>
                    <span style={{ width: U(16), flexShrink: 0, fontSize: U(10), fontWeight: 700, color: theme.textSubtle }}>{i + 1}</span>
                    <div style={{ width: U(112), flexShrink: 0, minWidth: 0 }}><EText path={`side.creators.${i}.name`} value={c.name} style={{ fontSize: U(12), fontWeight: 600, color: resolved.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} /><div style={{ fontSize: U(10), color: resolved.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.handle ? `@${String(c.handle).replace(/^@/, '')}` : ''}</div></div>
                    <RsBar pct={((Number(c.pct) || 0) / maxV) * 100} color={pal[i % pal.length]} track={theme.surface} />
                    <span style={{ width: U(56), textAlign: 'right', fontSize: U(12), fontWeight: 600, color: resolved.text, flexShrink: 0 }}>{c.value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <EText path="side.title" value={side.title} fallback="Channel summary" style={rsLabel(11, resolved.textMuted)} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: U(12), fontSize: U(13), marginTop: U(16) }}>{rows.map((x, i) => <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: U(8) }}><EText path={`side.rows.${i}.label`} value={x.label} tag="span" style={{ color: resolved.textMuted }} /><EText path={`side.rows.${i}.value`} value={x.value} tag="span" style={{ fontWeight: 600, color: resolved.text }} /></div>)}</div>
              <EText path="side.note" value={side.note} placeholder="Note" multiline style={{ marginTop: 'auto', paddingTop: U(12), borderTop: `1px solid ${theme.border}`, fontSize: U(11), lineHeight: 1.6, color: theme.textSubtle }} />
            </>
          )}
        </RsCard>
      </div>
    </RsShell>
  );
}

const RS_SEGMENTS: Record<string, { label: string; desc: string }> = { Stars: { label: 'Stars', desc: 'High engagement & spread' }, Amplifiers: { label: 'Amplifiers', desc: 'Wide reach, lower engagement' }, Loyalists: { label: 'Loyalists', desc: 'Deep engagement, niche community' }, Developing: { label: 'Developing', desc: 'Growth opportunity' } };
const rsSegmentColor = (t: Theme, seg: string) => (seg === 'Stars' ? t.primary : seg === 'Amplifiers' ? t.accent : seg === 'Loyalists' ? mixWithWhite(t.primary, 0.5) : t.textSubtle);

function TableMatrixSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const resolved = resolveTheme(theme, 'light');
  const items: any[] = (slide.items || []).slice(0, 12).map((c: any) => ({ ...c, eri: Number(c.eri) || 0, virality: c.virality == null ? null : Number(c.virality) }));
  const ERI_T = 0.03, VIR_T = 1;
  const maxEri = Math.max(...items.map((c) => c.eri), ERI_T * 2); const maxVir = Math.max(...items.map((c) => c.virality ?? 0), VIR_T * 2);
  const eriLine = (ERI_T / maxEri) * 100, virLine = (VIR_T / maxVir) * 100;
  const ranked = [...items].sort((a, b) => b.eri - a.eri); const maxEriVal = ranked[0]?.eri || 1;
  const quads = [{ seg: 'Stars', x: true, y: true }, { seg: 'Amplifiers', x: false, y: true }, { seg: 'Loyalists', x: true, y: false }, { seg: 'Developing', x: false, y: false }];
  const cols = `1fr ${U(96)} ${U(56)} ${U(56)}`;
  return (
    <RsShell slide={slide} theme={theme}>
      <div style={{ display: 'flex', flex: 1, gap: U(20), minHeight: 0 }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', borderRadius: U(16), border: `1px solid ${theme.border}`, overflow: 'hidden', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: `${U(10)} ${U(16)}`, borderBottom: `1px solid ${theme.border}`, flexShrink: 0 }}><div style={rsLabel(11, resolved.textMuted, '0.14em')}>ERI vs virality</div><div style={{ display: 'flex', gap: U(16), fontSize: U(10), color: theme.textSubtle }}><span>→ ERI = engagement depth</span><span>↑ Virality = content spread</span></div></div>
          <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
            <div style={{ position: 'absolute', left: U(4), top: '50%', transform: 'translateY(-50%) rotate(180deg)', writingMode: 'vertical-rl', ...rsLabel(9, theme.textSubtle, '0.14em') }}>Virality</div>
            <div style={{ position: 'absolute', bottom: U(4), left: '50%', transform: 'translateX(-50%)', ...rsLabel(9, theme.textSubtle, '0.14em') }}>ERI %</div>
            <div style={{ position: 'absolute', left: U(20), right: U(12), top: U(12), bottom: U(20) }}>
              {quads.map((q) => <div key={q.seg} style={{ position: 'absolute', left: q.x ? `${eriLine}%` : 0, right: q.x ? 0 : `${100 - eriLine}%`, top: q.y ? 0 : `${100 - virLine}%`, bottom: q.y ? `${100 - virLine}%` : 0, background: mixWithWhite(rsSegmentColor(theme, q.seg), 0.1) }}><div style={{ position: 'absolute', ...(q.y ? { top: U(8) } : { bottom: U(8) }), ...(q.x ? { left: U(10) } : { right: U(10) }), textAlign: q.x ? 'left' : 'right' }}><div style={{ fontSize: U(11), fontWeight: 700, color: rsSegmentColor(theme, q.seg) }}>{RS_SEGMENTS[q.seg].label}</div><div style={{ fontSize: U(9), color: resolved.textMuted }}>{RS_SEGMENTS[q.seg].desc}</div></div></div>)}
              <div style={{ position: 'absolute', left: `${eriLine}%`, top: 0, bottom: 0, borderLeft: '1.5px dashed rgba(0,0,0,0.18)' }}><span style={{ position: 'absolute', bottom: U(2), left: U(3), fontSize: U(9), fontWeight: 600, color: theme.textSubtle, whiteSpace: 'nowrap' }}>3% ERI</span></div>
              <div style={{ position: 'absolute', left: 0, right: 0, bottom: `${virLine}%`, borderTop: '1.5px dashed rgba(0,0,0,0.18)' }}><span style={{ position: 'absolute', top: U(2), left: U(3), fontSize: U(9), fontWeight: 600, color: theme.textSubtle, whiteSpace: 'nowrap' }}>1× viral.</span></div>
              {items.map((c, i) => { const col = rsSegmentColor(theme, c.segment); return <div key={i} title={c.name} style={{ position: 'absolute', width: U(30), height: U(30), left: `calc(${(c.eri / maxEri) * 100}% - ${U(15)})`, bottom: `calc(${((c.virality ?? 0) / maxVir) * 100}% - ${U(15)})`, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: col, boxShadow: '0 0 0 2px #fff', zIndex: 2 }}><span style={{ fontSize: U(8), fontWeight: 700, color: '#fff' }}>{String(c.name || '?').substring(0, 3)}</span></div>; })}
            </div>
          </div>
        </div>
        <RsCard theme={theme} style={{ width: U(440), flexShrink: 0, padding: U(16), display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'grid', gridTemplateColumns: cols, marginBottom: U(12) }}>{['Creator', 'ERI', 'Viral.', 'Clicks'].map((h, i) => <span key={h} style={{ ...rsLabel(10, resolved.textMuted, '0.14em'), textAlign: i === 0 ? 'left' : i === 1 ? 'center' : 'right' }}>{h}</span>)}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: U(10), overflow: 'hidden' }}>
            {ranked.map((c, i) => { const col = rsSegmentColor(theme, c.segment); return (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: cols, alignItems: 'center', gap: U(6) }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: U(6), minWidth: 0 }}><span style={{ width: U(8), height: U(8), borderRadius: '50%', background: col, flexShrink: 0 }} /><span style={{ fontSize: U(11), fontWeight: 500, color: resolved.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</span><span style={{ flexShrink: 0, borderRadius: 999, padding: `${U(2)} ${U(6)}`, fontSize: U(8), fontWeight: 600, background: mixWithWhite(col, 0.15), color: col }}>{c.segment}</span></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: U(4) }}><RsBar pct={(c.eri / maxEriVal) * 100} color={col} track={theme.surface} /><span style={{ fontSize: U(10), fontWeight: 600, color: resolved.text, minWidth: U(28), textAlign: 'right' }}>{(c.eri * 100).toFixed(1)}%</span></div>
                <span style={{ fontSize: U(10), textAlign: 'right', fontWeight: 500, color: col }}>{c.virality != null ? `${c.virality.toFixed(1)}×` : '—'}</span>
                <span style={{ fontSize: U(10), textAlign: 'right', color: resolved.textMuted }}>{c.clicks ?? '—'}</span>
              </div>
            ); })}
          </div>
          <div style={{ marginTop: 'auto', paddingTop: U(12), borderTop: `1px solid ${theme.border}`, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: `${U(6)} ${U(16)}` }}>{Object.entries(RS_SEGMENTS).map(([k, v]) => <div key={k} style={{ display: 'flex', alignItems: 'center', gap: U(6), minWidth: 0 }}><span style={{ width: U(8), height: U(8), borderRadius: '50%', background: rsSegmentColor(theme, k), flexShrink: 0 }} /><span style={{ fontSize: U(10), fontWeight: 600, color: resolved.text }}>{v.label}</span><span style={{ fontSize: U(9), color: theme.textSubtle, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.desc}</span></div>)}</div>
        </RsCard>
      </div>
    </RsShell>
  );
}

function CreatorResultsSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const resolved = resolveTheme(theme, 'light');
  const edit = useEdit();
  const it: any = (slide.items || [])[0] || {};
  const stats: any[] = (it.stats || []).slice(0, 6); const chips: any[] = it.chips || [];
  const media = it.media ? { ...it.media, frame: it.media.frame ?? 'phone' } : it.media;
  const tones: RsTone[] = ['light', 'brand', 'light', 'accent', 'soft', 'light'];
  return (
    <RsShell slide={slide} theme={theme} hideHeader bodyTop={96}>
      <div style={{ display: 'flex', flex: 1, gap: U(24), minHeight: 0 }}>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: U(16) }}>
          <RsCard theme={theme} style={{ padding: U(16) }}>
            <EText path="eyebrow" value={slide.eyebrow} placeholder="CREATOR 1 / N" style={{ ...rsLabel(10, theme.primary, '0.2em'), marginBottom: U(8) }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: U(12) }}>
              {(it.avatarUrl || edit.editable) && <Avatar url={it.avatarUrl} name={it.title} size={U(52)} color={theme.primary} />}
              <div style={{ minWidth: 0, flex: 1 }}>
                <EText path="items.0.title" value={it.title} placeholder="Creator name" style={{ fontSize: U(22), fontWeight: 700, lineHeight: 1.2, color: resolved.text }} />
                <EText path="items.0.subtitle" value={it.subtitle} placeholder="@handle · platform" style={{ fontSize: U(12), color: resolved.textMuted, marginTop: U(2) }} />
              </div>
            </div>
          </RsCard>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: U(10) }}>{stats.map((m, i) => <RsTile key={i} theme={theme} m={m} path={`items.0.stats.${i}`} tone={tones[i]} listPath="items.0.stats" index={i} />)}</div>
          <EText path="items.0.body" value={it.body} placeholder="One line of analysis" multiline style={{ fontSize: U(12), lineHeight: 1.6, color: resolved.textMuted }} />
          {chips.length > 0 && <div style={{ borderRadius: U(12), padding: `${U(10)} ${U(12)}`, display: 'flex', flexWrap: 'wrap', gap: `${U(4)} ${U(16)}`, fontSize: U(11), background: theme.bgSoft, border: `1px solid ${theme.border}` }}>{chips.map((c, i) => <span key={i}><span style={{ color: theme.textSubtle }}>{c.label} </span><EText path={`items.0.chips.${i}.value`} value={c.value} tag="span" style={{ fontWeight: 600, color: resolved.text }} /></span>)}</div>}
        </div>
        {(media?.url || media?.posterUrl || edit.editable) && (
          <div style={{ width: U(260), flexShrink: 0, display: 'flex', flexDirection: 'column', gap: U(8), alignItems: 'center' }}>
            <div style={{ alignSelf: 'flex-start', ...rsLabel(10, theme.primary, '0.18em') }}>{it.mediaLabel || 'Top post'}</div>
            <div style={{ height: U(430), width: '100%' }}><EMedia path="items.0.media" media={media} style={{ width: '100%', height: '100%' }} frameRadius="2cqw" placeholder="Add their post" /></div>
            <EText path="items.0.mediaCaption" value={it.mediaCaption} placeholder="" style={{ fontSize: U(10), fontWeight: 500, color: resolved.textMuted }} />
          </div>
        )}
      </div>
    </RsShell>
  );
}

function BoxesStackedSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const items: any[] = (slide.items || []).slice(0, 3);
  return (
    <RsShell slide={slide} theme={theme}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: U(20), width: U(576) }}>
        {items.map((it, i) => { const tone = rsTone(theme, it.tone || (i === 0 ? 'brand' : 'soft')); const bullets: string[] = Array.isArray(it.bullets) ? it.bullets : []; return (
          <div key={i} style={{ borderRadius: U(16), background: tone.bg, border: `1px solid ${tone.border}`, padding: `${U(20)} ${U(24)}`, minHeight: U(120) }}>
            <EText path={`items.${i}.title`} value={it.title} placeholder="Title" style={{ fontSize: U(12), fontWeight: 700, color: tone.value }} />
            <ul style={{ margin: `${U(12)} 0 0`, paddingLeft: U(18), display: 'flex', flexDirection: 'column', gap: U(4), fontSize: U(12), lineHeight: 1.5, color: tone.value }}>
              {bullets.map((b, j) => <li key={j}><EText path={`items.${i}.bullets.${j}`} value={b} placeholder="Point" tag="span" /></li>)}
            </ul>
            {!bullets.length && it.body && <EText path={`items.${i}.body`} value={it.body} multiline style={{ marginTop: U(10), fontSize: U(12), lineHeight: 1.5, color: tone.value }} />}
          </div>
        ); })}
      </div>
    </RsShell>
  );
}

function TableGlossarySlide({ slide, theme }: { slide: any; theme: Theme }) {
  const resolved = resolveTheme(theme, 'light');
  const terms: any[] = (slide.terms || []).slice(0, 14);
  return (
    <RsShell slide={slide} theme={theme}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: `${U(12)} ${U(40)}`, fontSize: U(12), lineHeight: 1.6, color: resolved.textMuted }}>
        {terms.map((x, i) => <div key={i}><EText path={`terms.${i}.term`} value={x.term} tag="span" style={{ fontWeight: 600, color: resolved.text }} /><span> — </span><EText path={`terms.${i}.body`} value={x.body} tag="span" /></div>)}
      </div>
      <EText path="footer" value={slide.footer} placeholder="" style={{ marginTop: 'auto', paddingTop: U(12), fontSize: U(11), color: theme.textSubtle }} />
    </RsShell>
  );
}

function ClosingAboutSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const resolved = resolveTheme(theme, 'dark');
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <div style={{ position: 'absolute', inset: `0 0 ${U(6)} 0`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: U(24), padding: `0 ${U(64)}`, textAlign: 'center' }}>
        {resolved.logoUrl && <LogoImg inverse src={resolved.logoUrl} style={{ maxHeight: U(48), maxWidth: U(260), objectFit: 'contain' }} />}
        <EText path="title" value={slide.title} fallback="Thank you." tag="h1" style={{ margin: 0, fontSize: U(34), fontWeight: 700, letterSpacing: '-0.02em', color: resolved.text, maxWidth: U(700) }} />
        <EText path="subtitle" value={slide.subtitle} placeholder="Tagline" style={{ fontSize: U(18), fontWeight: 500, lineHeight: 1.6, color: 'rgba(255,255,255,0.7)', maxWidth: U(560) }} />
        <EText path="body" value={slide.body} placeholder="Description" multiline style={{ fontSize: U(14), lineHeight: 1.6, color: 'rgba(255,255,255,0.45)', maxWidth: U(540) }} />
        {slide.cta && <div style={{ borderRadius: 999, padding: `${U(8)} ${U(24)}`, fontSize: U(14), fontWeight: 600, background: theme.accent, color: luminance(theme.accent) > 0.6 ? theme.text : '#fff' }}><EText path="cta" value={slide.cta} tag="span" /></div>}
      </div>
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: U(6), background: `linear-gradient(90deg, ${theme.primary} 0%, ${theme.accent} 100%)` }} />
    </div>
  );
}

// ─── 20. Proposal layouts ───────────────────────────────────────────────────
// Gamma-style pages used by the "Proposal 1 — Large" and "Proposal 2 — Short"
// templates: agenda grid, goals trefoil, creator tiers, channel shares, arrow
// points, checklist, side-photo section and a contacts closing. Each one is
// mirrored in backend/src/engine/pdfRenderer.ts (same names, same geometry).

/** Small italic footnote under the body area — only when the page carries a `footnote` field (templates set '' to invite one). */
function Footnote({ slide, resolved }: { slide: any; resolved: ResolvedTheme }) {
  if (slide.footnote == null) return null;
  return (
    <div style={{ position: 'absolute', left: GUTTER, right: '22cqw', bottom: '4.5cqh' }}>
      <EText path="footnote" value={slide.footnote} placeholder="Add a footnote" multiline style={{ ...TYPE.bodySmall, fontSize: '1.05cqw', fontStyle: 'italic', color: resolved.textMuted }} />
    </div>
  );
}

/** Agenda / overview: numbered chapters in two columns, each number over a rule. `items: [{ label?, title, body? }]` (≤ 6). */
function BoxesAgendaSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const resolved = resolveTheme(theme, slide.theme ?? 'light');
  const edit = useEdit();
  const items: any[] = (slide.items ?? []).slice(0, 6);
  const eyebrowColor = resolved.isInverse ? 'rgba(255,255,255,0.85)' : theme.primary;
  const rule = resolved.isInverse ? 'rgba(255,255,255,0.35)' : theme.primaryTint;
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <SlideHeader slide={slide} eyebrowColor={eyebrowColor} resolved={resolved} />
      <div style={{ position: 'absolute', left: GUTTER, right: GUTTER, bottom: '11cqh', top: '38cqh', display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: '3cqw', rowGap: '3.5cqh', alignContent: 'start' }}>
        {items.map((it, i) => { const hl = Number.isInteger(slide.highlight) ? Number(slide.highlight) : -1; const lit = hl === i; const dim = hl >= 0 && !lit; return (
          <ECard key={i} listPath="items" index={i} style={{ minWidth: 0, opacity: dim ? 0.45 : 1 }}>
            <EText path={`items.${i}.label`} value={it.label} fallback={String(i + 1).padStart(2, '0')} placeholder="01" style={{ ...TYPE.body, fontSize: '1.5cqw', fontWeight: lit ? 700 : 500, color: lit ? theme.primary : resolved.textMuted, paddingBottom: '1cqh', borderBottom: `0.15cqw solid ${rule}`, marginBottom: '1.4cqh' }} />
            <EText path={`items.${i}.title`} value={it.title} placeholder="Chapter" style={{ ...TYPE.title, fontSize: '2.1cqw', color: resolved.text }} />
            <EText path={`items.${i}.body`} value={it.body} placeholder={edit.editable ? 'Add a line' : undefined} style={{ ...TYPE.bodySmall, color: resolved.textMuted, marginTop: '0.6cqh' }} />
          </ECard>
        ); })}
        <EAddCard listPath="items" count={items.length} max={6} label="Add chapter" blank={() => ({ label: String(items.length + 1).padStart(2, '0'), title: 'Chapter', body: '' })} style={{ minHeight: '10cqh' }} />
      </div>
      <Footnote slide={slide} resolved={resolved} />
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

/** The three-petal figure behind the goals page: left, top-right, bottom-right petal with one icon each. */
export const PETAL_PATH = 'M100 97 C 74 90, 50 64, 56 32 C 62 8, 138 8, 144 32 C 150 64, 126 90, 100 97 Z';
export const PETAL_SLOTS = [{ rot: -90, cx: 52, cy: 100 }, { rot: 30, cx: 124, cy: 58 }, { rot: 150, cx: 124, cy: 142 }];
function TrefoilSvg({ color, icons, style }: { color: string; icons: string[]; style?: CSSProperties }) {
  return (
    <svg viewBox="0 0 200 200" style={style} aria-hidden="true">
      {PETAL_SLOTS.map((p, i) => (
        <g key={i}>
          <path d={PETAL_PATH} fill={color} transform={`rotate(${p.rot} 100 100)`} />
          <text x={p.cx} y={p.cy} textAnchor="middle" dominantBaseline="central" fontSize="20" fill="#fff">{icons[i] || ''}</text>
        </g>
      ))}
    </svg>
  );
}

/** Goals trefoil: exactly three goals around the petal figure — first on the left, two on the right. `items: [{ icon?, title, body? }]`. */
function BoxesPetalsSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const resolved = resolveTheme(theme, slide.theme ?? 'light');
  const edit = useEdit();
  const items: any[] = (slide.items ?? []).slice(0, 3);
  const eyebrowColor = resolved.isInverse ? 'rgba(255,255,255,0.85)' : theme.primary;
  const hasHeader = !!(slide.title || slide.eyebrow) || edit.editable;
  const cell = (i: number, align: 'left' | 'right') => {
    const it = items[i];
    if (!it) return <div key={i} />;
    return (
      <ECard key={i} listPath="items" index={i} style={{ textAlign: align, display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 }}>
        <EText path={`items.${i}.title`} value={it.title} placeholder="Goal" style={{ ...TYPE.title, fontSize: '2cqw', color: resolved.text, marginBottom: '1cqh' }} />
        <EText path={`items.${i}.body`} value={it.body} placeholder="What it means" multiline style={{ ...TYPE.body, color: resolved.textMuted }} />
      </ECard>
    );
  };
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <SlideHeader slide={slide} eyebrowColor={eyebrowColor} resolved={resolved} />
      <div style={{ position: 'absolute', left: GUTTER, right: GUTTER, bottom: '11cqh', top: hasHeader ? '36cqh' : '14cqh', display: 'grid', gridTemplateColumns: '1fr 27cqw 1fr', gap: '2.4cqw', alignItems: 'center' }}>
        {cell(0, 'right')}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1cqh' }}>
          <TrefoilSvg color={theme.primary} icons={items.map((it) => it.icon || '')} style={{ width: '100%', height: 'auto' }} />
          {edit.editable && (
            <div style={{ display: 'flex', gap: '0.8cqw', ...TYPE.bodySmall, color: resolved.textMuted }}>
              {items.map((it, i) => <EText key={i} path={`items.${i}.icon`} value={it.icon} placeholder="icon" style={{ minWidth: '2.4cqw', textAlign: 'center', border: `1px dashed ${theme.border}`, borderRadius: '0.5cqw', padding: '0.2cqh 0.4cqw' }} />)}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '4cqh' }}>
          {cell(1, 'left')}
          {cell(2, 'left')}
          <EAddCard listPath="items" count={items.length} max={3} label="Add goal" blank={() => ({ icon: '★', title: 'Goal', body: '' })} style={{ minHeight: '8cqh' }} />
        </div>
      </div>
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

/** Arrow points: two columns of "→ title + body" entries. `items: [{ title, body? }]` (≤ 6). */
function BoxesPointsSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const resolved = resolveTheme(theme, slide.theme ?? 'light');
  const edit = useEdit();
  const items: any[] = (slide.items ?? []).slice(0, 8);
  const eyebrowColor = resolved.isInverse ? 'rgba(255,255,255,0.85)' : theme.primary;
  const hasHeader = !!(slide.title || slide.eyebrow) || edit.editable;
  const dense = items.length > 4;
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <SlideHeader slide={slide} eyebrowColor={eyebrowColor} resolved={resolved} />
      <div style={{ position: 'absolute', left: GUTTER, right: GUTTER, bottom: '11cqh', top: hasHeader ? '36cqh' : '14cqh', display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: `${dense ? 2 : 3}cqh 3cqw`, alignContent: 'start' }}>
        {items.map((it, i) => (
          <ECard key={i} listPath="items" index={i} style={{ display: 'flex', gap: '1.2cqw', alignItems: 'flex-start', minWidth: 0 }}>
            <div style={{ flexShrink: 0, width: '2.6cqw', fontSize: '2.2cqw', lineHeight: 1, color: theme.primary, marginTop: '0.2cqh' }}>→</div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <EText path={`items.${i}.title`} value={it.title} placeholder="Point" style={{ ...TYPE.title, fontSize: '1.9cqw', color: resolved.text, marginBottom: '0.8cqh' }} />
              <EText path={`items.${i}.body`} value={it.body} placeholder="Add text" multiline style={{ ...(dense ? TYPE.bodySmall : TYPE.body), color: resolved.textMuted }} />
            </div>
          </ECard>
        ))}
        <EAddCard listPath="items" count={items.length} max={8} label="Add point" blank={() => ({ title: 'Point', body: '' })} style={{ minHeight: '8cqh' }} />
      </div>
      <Footnote slide={slide} resolved={resolved} />
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

/** Checklist: "always included" style grid of ticked items. `items: [{ title, body? }]` (≤ 12; 2 columns up to 6, then 3). */
function BoxesChecklistSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const resolved = resolveTheme(theme, slide.theme ?? 'light');
  const edit = useEdit();
  const items: any[] = (slide.items ?? []).slice(0, 12);
  const eyebrowColor = resolved.isInverse ? 'rgba(255,255,255,0.85)' : theme.primary;
  const hasHeader = !!(slide.title || slide.eyebrow) || edit.editable;
  const cols = items.length <= 6 ? 2 : 3;
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <SlideHeader slide={slide} eyebrowColor={eyebrowColor} resolved={resolved} />
      <div style={{ position: 'absolute', left: GUTTER, right: GUTTER, bottom: '11cqh', top: hasHeader ? '36cqh' : '14cqh', display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: '2.2cqh 2.4cqw', alignContent: 'start' }}>
        {items.map((it, i) => (
          <ECard key={i} listPath="items" index={i} style={{ display: 'flex', gap: '1cqw', alignItems: 'flex-start', minWidth: 0 }}>
            <div style={{ flexShrink: 0, width: '2.1cqw', height: '2.1cqw', borderRadius: '50%', background: theme.primary, color: '#fff', display: 'grid', placeItems: 'center', fontSize: '1.15cqw', fontWeight: 700, marginTop: '0.1cqh' }}>✓</div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <EText path={`items.${i}.title`} value={it.title} placeholder="Included item" style={{ ...TYPE.body, fontSize: cols === 3 ? '1.3cqw' : '1.45cqw', fontWeight: 600, color: resolved.text, lineHeight: 1.35 }} />
              <EText path={`items.${i}.body`} value={it.body} placeholder={edit.editable ? 'Add detail' : undefined} style={{ ...TYPE.bodySmall, color: resolved.textMuted, marginTop: '0.3cqh' }} />
            </div>
          </ECard>
        ))}
        <EAddCard listPath="items" count={items.length} max={12} label="Add item" blank={() => ({ title: 'Included item', body: '' })} style={{ minHeight: '5cqh' }} />
      </div>
      <Footnote slide={slide} resolved={resolved} />
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

/** Creator tiers: up to 4 columns, each with an icon disc on a stepped bar, title, a range chip (subtitle), body and bullets. */
function ColumnsTiersSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const resolved = resolveTheme(theme, slide.theme ?? 'light');
  const edit = useEdit();
  const columns: any[] = (slide.columns ?? []).slice(0, 4);
  const cols = Math.max(columns.length + 0, 1);
  const eyebrowColor = resolved.isInverse ? 'rgba(255,255,255,0.85)' : theme.primary;
  const hasHeader = !!(slide.title || slide.eyebrow) || edit.editable;
  const chipBg = resolved.isInverse ? 'rgba(255,255,255,0.12)' : theme.primarySoft;
  const dense = columns.some((c) => Array.isArray(c.bullets) && c.bullets.length > 3);
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <SlideHeader slide={slide} eyebrowColor={eyebrowColor} resolved={resolved} />
      <div style={{ position: 'absolute', left: GUTTER, right: GUTTER, bottom: '11cqh', top: hasHeader ? (slide.kicker ? '40cqh' : '36cqh') : '14cqh', display: 'grid', gap: '2.4cqw', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, alignItems: 'start' }}>
        {columns.map((c, i) => (
          <ECard key={i} listPath="columns" index={i} style={{ display: 'flex', flexDirection: 'column', minWidth: 0, paddingTop: `${(columns.length - 1 - i) * 2.5}cqh` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.8cqw', marginBottom: '2cqh' }}>
              <div style={{ width: '3.4cqw', height: '3.4cqw', borderRadius: '50%', background: theme.primary, color: '#fff', display: 'grid', placeItems: 'center', flexShrink: 0, fontSize: '1.6cqw', lineHeight: 1 }}>
                <EText path={`columns.${i}.icon`} value={c.icon} fallback="★" placeholder="★" tag="span" style={{ textAlign: 'center' }} />
              </div>
              <div style={{ flex: 1, height: '0.9cqw', borderRadius: 999, background: theme.primary, opacity: 0.75 }} />
            </div>
            <EText path={`columns.${i}.title`} value={c.title} placeholder="Tier" style={{ ...TYPE.title, fontSize: '1.9cqw', color: resolved.text, marginBottom: '0.9cqh' }} />
            <EText path={`columns.${i}.subtitle`} value={c.subtitle} placeholder="[1k–10k followers]" style={{ ...TYPE.caption, textTransform: 'none', letterSpacing: '0.02em', color: theme.primary, background: chipBg, display: 'inline-block', alignSelf: 'flex-start', padding: '0.5cqh 0.9cqw', borderRadius: 999, marginBottom: '1.4cqh' }} />
            <EText path={`columns.${i}.body`} value={c.body} placeholder="Add text" multiline style={{ ...(dense ? TYPE.bodySmall : TYPE.body), color: resolved.textMuted }} />
            <SubBullets path={`columns.${i}.bullets`} bullets={c.bullets} resolved={resolved} small />
          </ECard>
        ))}
        <EAddCard listPath="columns" count={columns.length} max={4} label="Add tier" blank={() => ({ icon: '★', title: 'Tier', subtitle: '', body: '' })} style={{ minHeight: '20cqh' }} />
      </div>
      <Footnote slide={slide} resolved={resolved} />
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

/** Share columns: a big figure (eyebrow, e.g. "70%") over each column's title and text — channel mix, budget split. */
function ColumnsStatSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const resolved = resolveTheme(theme, slide.theme ?? 'light');
  const edit = useEdit();
  const columns: any[] = (slide.columns ?? []).slice(0, 4);
  const cols = Math.max(columns.length + 0, 1);
  const eyebrowColor = resolved.isInverse ? 'rgba(255,255,255,0.85)' : theme.primary;
  const hasHeader = !!(slide.title || slide.eyebrow) || edit.editable;
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <SlideHeader slide={slide} eyebrowColor={eyebrowColor} resolved={resolved} />
      <div style={{ position: 'absolute', left: GUTTER, right: GUTTER, bottom: '11cqh', top: hasHeader ? (slide.kicker ? '42cqh' : '38cqh') : '14cqh', display: 'grid', gap: '2.4cqw', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, alignItems: 'start' }}>
        {columns.map((c, i) => (
          <ECard key={i} listPath="columns" index={i} style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <EText path={`columns.${i}.eyebrow`} value={c.eyebrow} placeholder="70%" style={{ ...TYPE.displaySm, fontSize: '4.4cqw', lineHeight: 1, color: resolved.isInverse ? '#fff' : theme.primary, marginBottom: '1.4cqh' }} />
            <EText path={`columns.${i}.title`} value={c.title} placeholder="Channel" style={{ ...TYPE.title, fontSize: '1.9cqw', color: resolved.text, marginBottom: '1.2cqh' }} />
            <EText path={`columns.${i}.body`} value={c.body} placeholder="Add text" multiline style={{ ...(cols >= 3 ? TYPE.bodySmall : TYPE.body), color: resolved.textMuted }} />
            <SubBullets path={`columns.${i}.bullets`} bullets={c.bullets} resolved={resolved} small />
          </ECard>
        ))}
        <EAddCard listPath="columns" count={columns.length} max={4} label="Add column" blank={() => ({ eyebrow: '—', title: 'Channel', body: '' })} style={{ minHeight: '20cqh' }} />
      </div>
      <Footnote slide={slide} resolved={resolved} />
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

/** Section · light page, copy on the left, a photo on the right with an angled edge. */
function SectionSideSlide({ slide, theme, index }: { slide: any; theme: Theme; index: number }) {
  const resolved = resolveTheme(theme, slide.theme ?? 'light');
  const num = String(index + 1).padStart(2, '0');
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <div style={{ position: 'absolute', top: 0, bottom: 0, right: 0, width: '44cqw', clipPath: 'polygon(9% 0, 100% 0, 100% 100%, 0 100%)' }}>
        <EMedia path="media" media={slide.media && (slide.media.url || slide.media.posterUrl) ? { ...slide.media, aspect: 'fill', frame: undefined } : slide.media} style={{ width: '100%', height: '100%' }} frameRadius="0" placeholder="Add a photo" />
      </div>
      <div style={{ position: 'absolute', left: GUTTER, width: '44cqw', top: '50%', transform: 'translateY(-50%)' }}>
        <EText path="eyebrow" value={slide.eyebrow} placeholder={`Section ${num}`} style={{ ...TYPE.caption, color: theme.primary, marginBottom: '1.6cqh' }} />
        <EText path="title" value={slide.title} tag="h1" placeholder="Section name" style={{ ...TYPE.display, fontSize: '4.6cqw', color: resolved.text, margin: 0 }} />
        <EText path="subtitle" value={slide.subtitle} placeholder="Add a subtitle" multiline style={{ ...TYPE.lead, fontSize: '1.8cqw', color: resolved.textMuted, marginTop: '2.5cqh' }} />
      </div>
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}

/** A contact card from the Beatly people directory (or a blank one). */
export function contactFromTeamMember(m: any): any {
  return { name: m?.name || 'Name', role: m?.title || 'Role', org: '', phone: m?.phone || '', email: m?.email || '', avatarUrl: m?.pictureUrl || undefined };
}

/** Closing · big sign-off line and up to three contact cards (name, role, company/address, phone, e-mail). */
function ClosingContactsSlide({ slide, theme }: { slide: any; theme: Theme }) {
  const resolved = resolveTheme(theme, slide.theme ?? 'light');
  const edit = useEdit();
  const contacts: any[] = (slide.contacts ?? []).slice(0, 3);
  const cols = Math.max(contacts.length + 0, 1);
  const cardBg = resolved.isInverse ? 'rgba(255,255,255,0.08)' : theme.surface;
  const add = edit.pickTeamMember
    ? () => edit.pickTeamMember!((m) => edit.update((s) => ({ ...s, contacts: [...(s.contacts || []), contactFromTeamMember(m)] })))
    : undefined;
  return (
    <div style={baseStyle(resolved, theme.fontDisplay)}>
      <CornerLogo url={resolved.logoUrl} isInverse={resolved.isInverse} />
      <div style={{ position: 'absolute', left: GUTTER, right: GUTTER, top: '20cqh' }}>
        <Eyebrow path="eyebrow" text={slide.eyebrow} color={resolved.isInverse ? 'rgba(255,255,255,0.85)' : theme.primary} />
        <EText path="title" value={slide.title} fallback="Creator-Led Marketing." tag="h1" placeholder="Creator-Led Marketing." style={{ ...TYPE.display, fontSize: '4.8cqw', color: resolved.text, margin: 0, maxWidth: '80cqw' }} />
        <EText path="subtitle" value={slide.subtitle} placeholder="Add a closing line" style={{ ...TYPE.bodyLarge, color: resolved.textMuted, marginTop: '2cqh', maxWidth: '60cqw' }} />
      </div>
      <div style={{ position: 'absolute', left: GUTTER, right: GUTTER, bottom: '11cqh', display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: '2.4cqw', alignItems: 'stretch' }}>
        {contacts.map((c, i) => (
          <ECard key={i} listPath="contacts" index={i} style={{ display: 'flex', gap: '1.4cqw', alignItems: 'flex-start', padding: '2.2cqh 1.8cqw', borderRadius: '1.2cqw', background: cardBg, minWidth: 0 }}>
            <Avatar url={c.avatarUrl} name={c.name} size="4.2cqw" color={theme.primary} />
            <div style={{ minWidth: 0, flex: 1, overflowWrap: 'anywhere' }}>
              <EText path={`contacts.${i}.name`} value={c.name} placeholder="Name" style={{ ...TYPE.title, fontSize: '1.7cqw', color: resolved.text }} />
              <EText path={`contacts.${i}.role`} value={c.role} placeholder="Role" style={{ ...TYPE.bodySmall, color: theme.primary, fontWeight: 600, marginBottom: '1cqh' }} />
              <EText path={`contacts.${i}.org`} value={c.org} placeholder="Company · address" style={{ ...TYPE.bodySmall, color: resolved.textMuted }} />
              <EText path={`contacts.${i}.phone`} value={c.phone} placeholder="Phone" style={{ ...TYPE.bodySmall, color: resolved.textMuted }} />
              <EText path={`contacts.${i}.email`} value={c.email} placeholder="E-mail" style={{ ...TYPE.bodySmall, color: resolved.text, fontWeight: 500 }} />
            </div>
          </ECard>
        ))}
        <EAddCard listPath="contacts" count={contacts.length} max={3} label="Add contact" onAdd={add} blank={() => contactFromTeamMember(null)} style={{ minHeight: '14cqh' }} />
      </div>
      <BrandMark theme={theme} resolved={resolved} />
    </div>
  );
}
